import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { LoadOptions } from 'devextreme/data';
import { ResolvedData } from 'devextreme/data/custom_store';
import { Observable, filter, lastValueFrom, map, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { CoreBaseApiCommandParams } from '@app/core/models/core-base-api.model';
import { CoreModuleName, EidosIconType, LocalStorageKeys } from '@app/core/models/core-constant.model';
import { EidosConfigService } from '@common/config/eidos-config.service';
import { IEidosIcon, IEidosModuleConfiguration } from '@common/config/environment.interface';
import { DynamicApiRequestPaginatedParams, DynamicApiResponse, DynamicApiResponsePaginatedModel, IDynamicApiResponse } from '@common/models/dynamic-api-response.model';
import { CoreFormatService } from '@common/services/core-format.service';
import { EidosBaseApiService } from '@common/services/eidos-base-api.service';
import { EidosLogService } from '@common/services/eidos-log.service';
import { EidosSecurityService } from '@common/services/eidos-security.service';

import { IReservationApiService, IReservationApiServiceOption, ReservationServiceData, ReservationServiceOption, ReservationServiceType } from '@app/res-common/models/rescom-service.model';
import { IResVillaAvailabilityParams, IReservationModuleAPIConfiguration, ResVillaAvailability, ReservationUploadBlobResponse } from '@app/res-common/models/rescom.model';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { ReservationApiBoolean } from '../../res-common/models/res-constant.model';
import { IReservationApiPermission, ReservationActionBooking } from '../../res-common/models/reservation-permission.model';
import { IResLinkedBooking, IReservationApiLinkedBookingManageParams, IReservationLinkedBooking } from '../components/res-booking/res-booking-link/res-booking-link.component';
import { GeoTreeSearch, IDynamicMapApiParams, IResApiGetGeoTreeTopicItinerary, ResBkgVisaHealth, ResDynamicMapResponse, ResTopicItinerary, ResVoyageDescription } from '../models/geo-tree.model';
import { IResApiPackagePeopleManageParameters, IResApiPeopleManageParameters, IResApiPeopleParameters, ResPeople, ResPeopleType } from '../models/people.model';
import { BookingGuestAirData, FutureFlightsDetail, FutureFlightsGuest, IResApiFutureFlights, IReservationApiAirLine, IReservationApiAirPnr, IReservationApiAirPnrHistory, IReservationApiAirResult, IReservationApiBkgAirDetail, IReservationApiBookAirResultParams, IReservationApiBookingGuestAirData, IReservationApiChangePnrAirClass, IReservationApiDeleteAirPnrParams, IReservationApiGetAirPnrHistoryParams, IReservationApiGetAirResultsParams, IReservationApiImportPNRParams, IReservationApiRefreshAirResultPnrsParams, IReservationApiUpdateBookingGuestAirData, ResAirData, ResBkgAirDetail, ResFutureFlights, ResRightRezOfficeIdConfig, ReservationAirLine, ReservationAirPnr, ReservationAirPnrHistory, ReservationAirResult, ReservationApiRightRezParams, ReservationApiRightRezResponse } from '../models/res-air.model';
import { IResApiBookingCertificate, IResApiBookingCertificateAvailableAmount, IResApiBookingCertificateDetail, IResApiCreateBookingCertificate, IResApiGetBookingCertificateDetail, IResApiImportCertificate, IResApiSearchCertificate, IResApiUpdateBookingCertificate, ResBookingCertificate, ResBookingCertificateAvailableAmount, ResBookingCertificateDetail, ResCertificateType } from '../models/res-bkg-certificate.model';
import { IReservationApiCreateBkgDocumentParams } from '../models/res-bkg-document.model';
import { IResApiBkgItemsManage, IResBkgItemApiParams, ResBkgGroupedItems, ResBkgItem, ResBkgItemFlightInfo, ResBkgItemTotal } from '../models/res-bkg-item.model';
import { IReservationApiBookingNote, IReservationApiGetBkgNotes, IReservationApiManageBkgNotes, ResBkgNote, ResBkgNoteTouch } from '../models/res-bkg-notes.model';
import {
  BookingGuest,
  BookingToRequest,
  IMarket,
  IResApiAddVillaOptionParams,
  IResApiGetBkgJourneyItineraryHistoryDetailsParams,
  IResApiGetBkgJourneyItineraryHistoryParams,
  IResApiGetBkgJourneyItineraryParams,
  IResApiGetBkgTopicHistoryDetailsParams,
  IResApiGetExportInvoiceDataParams,
  IResApiManageJetSeats,
  IResApiManageOwnArrangementParams,
  IResApiParametersQuoteCreate,
  IResBookingCreateApiResponse,
  IResBookingGroup,
  IResBookingManageConfirmSuiteStatusParams,
  IResBookingManageSuiteRemarkParams,
  IResBookingSupplierListForAdvisingReportParams,
  IResTailorMadeItems,
  IResValueExistCheckParams,
  IReservationApiBkgWishlist,
  IReservationApiBookingActionCheckParameters,
  IReservationApiBookingActionParameters,
  IReservationApiBookingAddPackageParameters,
  IReservationApiBookingDelete,
  IReservationApiBookingExternalGifUrlParameters,
  IReservationApiBookingFinancialRecap,
  IReservationApiBookingGetMyActivitiesUrlParameters,
  IReservationApiBookingGuest,
  IReservationApiBookingRequestParameters,
  IReservationApiBookingVersionRequestParameters,
  IReservationApiEditBookingParameters,
  IReservationApiManageBkgWishlistParams,
  IReservationApiManageReferralDiscounts,
  IReservationApiParametersGuest,
  IReservationBookingMarginality,
  Market,
  ResAccountingExportData,
  ResApiBookingActionResponse,
  ResBkgDmc,
  ResBkgDuration,
  ResBkgIntercompanyList,
  ResBkgJourneyItinerary,
  ResBkgJourneyItineraryHistory,
  ResBkgJourneyItineraryHistoryDates,
  ResBkgList,
  ResBkgMainDestination,
  ResBkgProductTypes,
  ResBkgSalesAssistant,
  ResBkgSecondarySalesAgent,
  ResBkgSecondaryTravelAgency,
  ResBkgSegment,
  ResBkgSubSegment,
  ResBkgTailorMadeParams,
  ResBkgTailorMadeServiceTypes,
  ResBkgTopicHistoryDates,
  ResBkgTopicHistoryDetail,
  ResBkgVillaOption,
  ResBkgVillaService,
  ResBkgWishlist,
  ResBooking,
  ResBookingCancelReason,
  ResBookingFinancialRecap,
  ResBookingSupplierListForAdvisingReport,
  ResBookingV2VParams,
  ResFccCalculation,
  ResFinancialSplit,
  ResFlightOwnArrangement,
  ResTailorMadeParameters,
  ReservationBookingMarginality,
  ReservationBookingReferralGuests
} from '../models/res-booking.model';
import { IReservationCertificateType, IReservationCommissionPayoutType, IReservationCompany, IReservationLoyaltyDiscount, IReservationLoyaltyRedemptionTier, IReservationNoteType, IReservationPriceType, IReservationServiceType, IReservationSuiteAmenity } from '../models/res-cached-data.models';
import { IReservationApiCreateCashReceipt, IReservationCashReceiptCashAccount, IReservationCashReceiptCheckNO, IReservationCashReceiptPaymentReason, ResCashReceipt, ResCashReceiptCashAccount, ReservationCashReceiptPayerGroup } from '../models/res-cash-receipt.model';
import { IResApiCheckCommissionPayoutBatchParams, IResApiCommissionPayout, IResApiCommissionPayoutBatch, IResApiCreateCommissionPayoutBatchParams, IResApiDeleteCommissionPayoutParams, IResApiGenerateCommissionPayoutBatchSelfInvoiceParams, IResApiGetCommissionPayoutAgenciesParams, IResApiGetCommissionPayoutBatchesParams, IResApiGetCommissionPayoutBkgListParams, IResApiGetCommissionPayoutParams, IResApiManageCommissionPayoutBatchParams, IResApiOpenCommissionPayoutBatchParams, IResApiSendCommissionPayoutBatchToBcParams, IResApiSendCommissionPayoutBatchToFinanceParams, IResApiSendCommissionPayoutEmailSpecs, IResApiUnlockCommissionPayoutBkgsParams, IResApiUpdateCommissionAmount, IResApiUploadCommissionPayoutBatchPaymentParams, ResBonusCommissionDetail, ResCommissionCompanies, ResCommissionPayout, ResCommissionPayoutAK, ResCommissionPayoutBatch, ReservationCommission } from '../models/res-commission.models';
import { IReservationApiGetCounty, IReservationApiGetState } from '../models/res-country-state-county.models';
import {
  IResGtyUpdateParams,
  IResInventoryUpdateParams,
  IResProductsPaymentSchedulerParams,
  IReservationApiCruiseAvailability,
  IReservationApiCruiseInventoryParams,
  IReservationApiCruiseSuiteListAvailability,
  IReservationApiDeck,
  IReservationGroupVoyageList,
  ResDeck,
  ResInventoryVoyageGTY,
  ResProductsPaymentScheduler,
  ReservationCruiseAvailabilityModel,
  ReservationCruiseInventoryModel,
  ReservationCruiseSuite,
  ReservationCruiseSuiteListAvailabilityModel,
  ReservationGroupVoyageList,
  ReservationPackagePricesAndCostsHistory,
} from '../models/res-cruise-availability.models';
import {
  IResApiVoyagesSearchParameters,
  IResGetCabinOccupancyParams,
  IReservationApiCruise,
  IReservationApiCruiseItinerary,
  IReservationApiCruiseLight,
  IReservationApiCruiseSuiteProfitability,
  IReservationApiDeleteCruiseParams,
  IReservationApiUpdateSuiteData,
  IReservationSuitePriceCategory,
  ResSuitePriceCategory,
  ReservationCabinOccupancy,
  ReservationCruise,
  ReservationCruiseItinerary,
  ReservationCruiseLight,
  ReservationCruiseSuiteProfitability,
} from '../models/res-cruise.models';
import { IReservationApiSaveCustomRequest, ReservationCustomRequest } from '../models/res-custom-request.models';
import { ResDepositToBookingConversionParams } from '../models/res-deposit.model';
import { ResGoodwillCompany, ResGoodwillDefaultPriceType, ResGoodwillDefaultPrices, ResGoodwillRMTypes } from '../models/res-goodwill-deposit.model';
import { IResGetGuestBookedActivitiesParams, ReservationBookingActivitiesBooked } from '../models/res-guest-my-activity';
import {
  IReservationHomeActionData,
  IReservationHomeCategory,
  IReservationHomeItem,
  IReservationHomeItemActions,
} from '../models/res-home.model';
import { IReservationApiDeclineInsurance, IReservationApiInsurance, ResInsurance } from '../models/res-insurance.model ';
import { IResApiCompareManifestParams, IResApiCompareTwoManifestsParams, IResApiDeleteFullManifestParams, IResApiDeleteManifestParams, IResApiGetManifestParams, IResApiGetSavedFullManifestsParams, IResApiGetSavedManifestsParams, IResApiManifest, IResApiManifestVoyage, IResApiOpenSavedFullManifestParams, IResApiOpenSavedManifestParams, IResApiSaveFullManifestParams, IResApiSaveManifestParams, IResApiSavedManifestHeader, IResApiUpdateFullManifestHeaderParams, IResApiUpdateFullManifestParams, IResApiUpdateManifestHeaderParams, IResApiUpdateManifestParams, ResManifest, ResSavedManifestHeader } from '../models/res-manifest.model';
import {
  IResApiGetPriceSetupBookingListParams,
  IResApiGetReplaceOptionsHistoryParams,
  IResApiReplaceAvailability,
  IResApiReplaceHistory,
  IResApiReplaceOptionsParams,
  IResApiReplaceStatus,
  IReservationAddServiceOptionParams,
  IReservationApiCloneServices,
  IReservationApiGetAllocationByOption,
  IReservationApiGetCostPerPax,
  IReservationApiGetOptionAllocation,
  IReservationApiGetOptionAllocationDetails,
  IReservationApiGetOptionAvailability,
  IReservationApiGetOptionCost,
  IReservationApiGetOptionPrice,
  IReservationApiGetOptions,
  IReservationApiGetPriceDayByDay,
  IReservationApiGetSanctuaryCampAllocations,
  IReservationApiManageBandCost,
  IReservationApiManageOptionAllocation,
  IReservationApiManageOptionAvailability,
  IReservationApiManageV2OptionCost,
  IReservationApiManageV2OptionPrice,
  IReservationApiOptionPrice,
  IReservationCheckComboAvailabilityParams,
  IReservationDeleteOptionPackageParams,
  IReservationEditServiceOptionParams,
  IReservationGetAllocations,
  IReservationGetBkgOptionsParams,
  IReservationGetBkgServicesParams,
  IReservationOptionPriceAllocation,
  IReservationSuiteType,
  IReservationUpdateMessageParams,
  ResPackageClearingCruise,
  ResPackageClearingNoCruise,
  ResPackageClearingVoyage,
  ResPriceSetupBookingList,
  ResReplaceHistory,
  ResSanctuaryCampAgency,
  ResSanctuaryCampAllocation,
  ResSanctuaryCampSuiteCategory,
  ReservationGetAllocations,
  ReservationOptionCost,
  ReservationOptionCostPerPax,
  ReservationOptionPrice,
  ReservationOptionPriceAllocation,
  ReservationOptionPriceAllocationDetail,
  ReservationOptionPriceDayByDay,
} from '../models/res-option.models';
import {
  IResApiAgenciesSearchParameters,
  IResApiGuestCheckBookingExistsParams,
  IResApiGuestCheckBookingExistsResult,
  IResPastGuestReferralParameters,
  IResReferralParameters,
  IReservationApiAgency,
  IReservationApiAgent,
  IReservationApiAgentParameters,
  IReservationApiDeleteGuestParameters,
  IReservationApiIndividual,
  IReservationApiIndividualsParameters,
  IReservationApiInsertGuestParameters,
  IReservationApiReplaceGuestParameters,
  IReservationApiUpdateGuestParameters,
  ResApiAgenciesSearchType,
  ResOwner,
  ResPastGuestReferral,
  ReservationAgency,
  ReservationAgent,
  ReservationIndividual,
} from '../models/res-owner.model';
import { IReservationApiPackageEcmData, IReservationApiPackageInfo, IReservationApiPackageInfoParameters, ResServiceEMCDetails } from "../models/res-package-detail.model";
import {
  BookingCreatedBy,
  HlTypes,
  IPackageDayLocationsByProject,
  IPackageDayProjects,
  IPackageHighlight,
  IPackageItineraryDay2AKP,
  IResApiChangeBookingOwnerParams,
  IResApiDayByDayImportFromWebsite,
  IResApiGetDayByDayPackagesParams,
  IResApiGetDayByDayTopicItineraryParams,
  IResApiGetPackageCostSupplierListParams,
  IResApiGetPackageTravelDateGuide,
  IResApiGetPurcahseOrderParams,
  IResApiGetTourBookParams,
  IResApiGuestsManage,
  IResApiManageDayByDayItinerary,
  IResApiManageDayByDayPackagesParams,
  IResApiManageDayByDayTopicItinerary,
  IResApiManagePackagePricesSetup,
  IResApiManagePackageSuiteCategoryStatus,
  IResApiManagePackageTravelDateRelease,
  IResApiManageSupplierItemRequestListItem,
  IResApiManageTopicPackageParams,
  IResApiPackageClone,
  IResApiPackageCommunicationEmail,
  IResApiPackageConnectedPackages,
  IResApiPackageOptionToSuiteCategory,
  IResApiPackageSupplierRequest,
  IResApiPackageSwapParams,
  IResApiSupplierContacts,
  IResApiSupplierEditRequest,
  IResApiSupplierRequest,
  IResManagePackageTravelDateGuide,
  IResPackageCostBandGetParams,
  IResPackageCostBandManageParams,
  IResPackageDisplay,
  IResPackageOptionToSuiteCategory,
  IResPackageSetupSingleSuppManage,
  IResParamsPackageAllocations,
  IResPriceNote,
  IResSearchSmartFilter,
  IReservationApiBookPackageParams,
  IReservationApiDeletePackageParams,
  IReservationApiGetPackageParams,
  IReservationApiLikedPackageUpdateParams,
  IReservationApiManagePackageAvailabilityParams,
  IReservationApiManagePackageDepSchedulingParams,
  IReservationApiManagePackageItineraryDaysParams,
  IReservationApiManagePackageLinkedPortsParams,
  IReservationApiManagePackageLinkedToursParams,
  IReservationApiManagePackageLinkedVoyagesParams,
  IReservationApiManagePackageOptionsParams,
  IReservationApiManagePackageParams,
  IReservationApiManagePackageTravelDatesParams,
  IReservationApiManagePackageUpgradeParams,
  IReservationApiManangePackageCanPolicyParams,
  IReservationApiPackageBookingList,
  IReservationApiPackageConfiguratedPrice,
  IReservationApiPackageDepScheduling,
  IReservationApiPackageOptionCost,
  IReservationApiPackageOptionPriceParams,
  IReservationApiPackageOptionPriceTypeAvailibility,
  IReservationApiPackageOptionPriceTypeAvailibilityParams,
  IReservationApiPackagePrice,
  IReservationApiPackageSetup,
  IReservationApiPackageUpgrade,
  IReservationApiPackageUpgradePrice,
  IReservationApiPackageUpgradePriceParams,
  IReservationApiPricesCloneDatesParams,
  IReservationCruisePriceType,
  IReservationPackageOptionSpecialItem,
  MarketingStatus,
  PackageDayLocation,
  PackageDayProject,
  PackageHighlight,
  PackageHighlightType,
  PackageItineraryDay2AKP,
  PackageOptionCostBand,
  ResBookingPackageDayByDay,
  ResBookingTopicDayByDay,
  ResCruisePriceType,
  ResInventoryPackage,
  ResInventoryPriceType,
  ResPackage,
  ResPackageAllocation,
  ResPackageAvailabilityStatus,
  ResPackageBookingList,
  ResPackageCancPolicy,
  ResPackageCategory,
  ResPackageCommunicationEmail,
  ResPackageConnectedPackages,
  ResPackageCostSupplierList,
  ResPackageDayByDay,
  ResPackageDepScheduling,
  ResPackageDepartureFootnotes,
  ResPackageDepartureFreeShorex,
  ResPackageExportListByBooking,
  ResPackageFootnotes,
  ResPackageItinerary,
  ResPackageJets,
  ResPackageOptionCost,
  ResPackageOptionToSuiteCategory,
  ResPackagePrice,
  ResPackagePriceHeader,
  ResPackagePriceSetup,
  ResPackagePriceValidityPeriod,
  ResPackageProperty,
  ResPackagePurchaseOrder,
  ResPackageSetupTravelDate,
  ResPackageSingleSupp,
  ResPackageSuiteConfig,
  ResPackageSupplierEmailRequestURL,
  ResPackageSupplierRequestData,
  ResPackageTopic,
  ResPackageTravelDateGuide,
  ResPackageTravelDateRelease,
  ResPackageType,
  ResPackageUpgrade,
  ResPackageUpgradePrice,
  ResPriceSetupExchangeRate,
  ResSearchFilterPreferences,
  ResSearchFilterSmartItem,
  ResSupplierContact,
  ResSupplierEditRequestDetails,
  ReservationBasePackage,
  ReservationPackageHighlightByType,
  ReservationPackageHighlightsType,
  ReservationPackageTravelDateDetail,
  ReservationProductItineraryBaseData,
  TourBook
} from '../models/res-package.model';
import { IResTransferCashParams } from '../models/res-payment.model';
import { ResProductService, ReservationCamp } from '../models/res-product.models';
import { IPromoCombinability, IPromoParams, IResApiManagePromoPackages, IResApiManagePromoProperties, IResApiSearchablePromoProperties, IResPromoCompany, IResPromoCompatibility, IResPromoHeader, IResPromoPriceType, IResPromoShip, IResPromoSuiteCategory, IReservationApiManagePromoActionParams, IReservationApiManagePromoAgenciesParams, IReservationApiManagePromoCompatibilityParams, IReservationApiManagePromoParams, IReservationApiManagePromoVoyagesParams, IReservationApiPromoApply, IReservationApiPromoDetail, IReservationBkgPromoItemManagePramaters, IReservationPromoApplyPramaters, ResBonusCommissionType, ResGetPromoBooking, ResPromo, ResPromoActionItems, ResPromoApply, ResPromoCompany, ResPromoCompatibility, ResPromoHeader, ResPromoPackage, ResPromoPriceType, ResPromoProperty, ResPromoShip, ResPromoSuiteCategory } from '../models/res-promo.model';
import { IResCampSelectParams, IResDeckplanInfoParams, IResPackagePricesHistoryParams, IResPackageSearchParams, IResPackageSelectParams, IResPackageSnglSuppParams as IResPackageSingleSuppParams, IResProductCod, IResProductServiceSelectParams, IResSuiteDeckplanParams, IResVoyageSearchRequiredParams, IReservationApiCruiseSuiteDeckplanModel, ResBargeVoyageParams, ResProductCod, ReservationCruiseSuiteDeckplanConfiguration, ReservationCruiseSuiteDeckplanModel } from '../models/res-search.models';
import { IReservationApiCreateNewServiceOrOption, IReservationApiGeotree, IReservationApiSetupOption, IReservationApiSetupSequenceDiscount, IReservationApiSetupServiceOptionInclusiveDetail, ResGroups, ResGuestGroup, ResPayerDepartment, ResPayerGroup, ReservationSetupOption, ReservationSetupSequenceDiscount, ReservationSetupServiceOptionInclusiveDetail, ResOandaCurrency, IReservationBookVilla, ReservationServiceBookingReport, ReservationPackageServiceAllocation, ReservationServiceAllocation, ReservationServicePackageListElement } from '../models/res-service.models';
import {
  IReservationApiShorexOption,
  IReservationApiShorexPrice,
  IReservationApiShorexPriceParameters,
  ReservationShorexOption,
  ReservationShorexPrice,
} from '../models/res-shorex.model';
import { IReservationApiUserInfo } from '../models/res-user.model';
import { IResVillaAvailabilityGetParams, ResVillaSellingAvailability } from '@app/res-common/models/villa.model';
import { IResApiGetHeritageDepositParams, ResHeritageCurrency, ResHeritagePrice, ResHeritageTier } from '../models/res-heritage-deposit.model';
import { IResExpAvailabilityGetParams, ResExpAvailability, ResBkgExperiencesApiParams, ResExperience, ResExperienceOptional, ResBkgExperienceManageChoicesApiParams } from '@app/res-common/models/res-experience.model';
import { ResSupplierCancPolicy, ResSupplierDepScheduling } from '@app/res-setup/models/resadm-service.models';


export interface IReservationErrorApi {
  errorCod: string;
  errorDescription: string;
}
export interface IReservationApiUploadBlobParameters {
  Source: string;
  BlobCode: string;
  BlobFile: File;
  UseNewApi: boolean;
}
export interface IReservationApiTsUpserGuest {
  id: number;
  DB: string;
}
export interface IResExtendBookingParams extends IResExtendBookingApiParams{
  productCode: string
}
export interface IResExtendBookingApiParams {
  BkgID: number
  Guests: IReservationApiParametersGuest[]
  BookVilla: IReservationBookVilla[]
}
export interface IResVillaEditPrice {
  BkgID: number
  PropertyID: number
	SellingPrice: number
	Cost: number
	CostCurrency:string
	ROE: number
}
@Injectable({
  providedIn: 'root',
})
export class ReservationApiService extends EidosBaseApiService {

  readonly self = this;

  constructor(
    public configService: EidosConfigService,
    public eidosSecurityService: EidosSecurityService,
    public http: HttpClient,
    public eidosLogService: EidosLogService,
    public dialog: MatDialog,
    public domSanitizer: DomSanitizer,
    public coreFormatService: CoreFormatService,
  ) {
    super(coreFormatService, configService, eidosLogService, dialog, eidosSecurityService, http);

    this.configService.currentModulesConfig
      .pipe(
        filter(
          (modules: Array<IEidosModuleConfiguration>) => !!modules.find((module) => module.moduleName === CoreModuleName.Reservation)
        )
      )
      .subscribe((modules) => {
        const module = modules.find((module) => module.moduleName === CoreModuleName.Reservation);

        if (module) {
          const reservationModuleAPI = module.API as IReservationModuleAPIConfiguration;
          this.urlAPI = reservationModuleAPI.url;
          this.staticUrlAPI = reservationModuleAPI.staticUrl;
          this.pluginUrlAPI = reservationModuleAPI.pluginUrl;
          this.apiVersion = reservationModuleAPI.version;
          if (reservationModuleAPI.headers) {
            this.headers = {
              ...reservationModuleAPI.headers,
              ...this.headers
            };
          }
        }
      });
  }
  /**
   * Get current user info
   *
   * @return {*}  {Observable<IReservationApiUserInfo>}
   * @memberof ReservationApiService
   */
  public getUserInfo(): Observable<IReservationApiUserInfo | undefined> {
    return this.callDynamicAPI('account/resuserinfo', {}, 'GET').pipe(
      map<DynamicApiResponse, IReservationApiUserInfo | undefined>((response) =>
        Array.isArray(response.body) && response.body.length > 0 ? (response.body[0] as IReservationApiUserInfo) : undefined)
    );
  }
  public getJwtToken() {
    return this.getExternalAPI('oem/dd/getJwtToken', 'GET', {}).pipe(
      map<any, any>((response) => {
        if(response.StatusCode != 200) throw new Error(response.ErrorMessage)

        return response.Token ?? ''
      })
    )
  }

  clearApiCache() {
    return this.getExternalAPI('cache/redis/clear', 'DELETE', {}).pipe(
      map<any, any>((response) => {
        if(response?.data?.cleared === 0) return false

        return true
      })
    )
  }
  /**
   * upload file on blob Azure
   *
   * @param {IReservationApiUploadBlobParameters} data*
   * @return {*}  {Observable<Array<ReservationShorexPrice>>}
   * @memberof ReservationApiService
   */
  public uploadBlobFile(data: IReservationApiUploadBlobParameters): Observable<ReservationUploadBlobResponse> {
    const formData = new FormData();
    formData.append("fileData", data.BlobFile);

    if (data.UseNewApi) {
      return this.sendFileRequestNew({
        type: "POST",
        url: `documents/document-blob-nocs?Source=${data.Source}&BlobCode=${data.BlobCode}`,
        data: formData,
        reportProgress: false
      })
        .pipe(
          filter((response: any) => response.type !== 0),
          map<any, ReservationUploadBlobResponse>(response => new ReservationUploadBlobResponse(response)),
          catchError(
            (
              error: HttpErrorResponse) => {
              return this.handleGenericError(error);
            }
          )
        );
    } else {
      return this.sendFileRequest({
        type: "POST",
        url: `documents/document-blob?Source=${data.Source}&BlobCode=${data.BlobCode}`,
        data: formData,
        reportProgress: false
      })
        .pipe(
          filter((response: any) => response.type !== 0),
          map<any, ReservationUploadBlobResponse>(response => new ReservationUploadBlobResponse(response)),
          catchError(
            (
              error: HttpErrorResponse) => {
              return this.handleGenericError(error);
            }
          )
        );
    }
  }
  /**
   * upload file on blob Azure
   *
   * @param {IReservationApiUploadBlobParameters} data*
   * @return {*}  {Observable<Array<ReservationShorexPrice>>}
   * @memberof ReservationApiService
   */
  public downBlobFile(guid: string) {
    return `${this.config?.urlAPI}/documents/document-blob?FileGuid=${guid}`;
  }

  public downloadBlobFileInline(guid: string) {
    let headers = this.getHeaders(null);
    let token = localStorage.getItem(LocalStorageKeys.TOKEN);
    if (token) {
      headers = headers.set(LocalStorageKeys.TOKEN, token);
    }

    let options = {
      withCredentials: true,
      headers,
      observe: 'response' as 'body',
      responseType: 'blob' as 'blob',
    };

    let apiUrl = `${this.config?.urlAPI}/documents/document-blob?FileGuid=${guid}&FileInline=false`;
    return this.http.get(apiUrl, options as any);
  }

  /**
   * Retrieves shorex price matching with filters specified
   *
   * @param {IReservationApiShorexPriceParameters} filter*
   * @return {*}  {Observable<Array<ReservationShorexPrice>>}
   * @memberof ReservationApiService
   */
  public getShorexPrices(filter?: IReservationApiShorexPriceParameters): Observable<Array<ReservationShorexPrice>> {
    return this.callDynamicAPI('shorex/selectPrices', filter, 'POST').pipe(
      map<DynamicApiResponse, Array<ReservationShorexPrice>>((response) =>
        response.body.map((prices: IReservationApiShorexPrice) => new ReservationShorexPrice(prices))
      )
    );
  }
  /**
   * Retrieves a package product
   *
   * @param {IResPackageSelectParams} params
   * @return {*}  {Observable<ReservationBasePackage>}
   * @memberof ReservationApiService
   */
  public getPackage(params: IResPackageSelectParams): Observable<ReservationBasePackage> {
    return this.callDynamicAPI('product/selectPKG', params, 'POST')
      .pipe(
        map<DynamicApiResponse, ReservationBasePackage>((response) => new ReservationBasePackage(response.body))
      );
  }
  public getPackageAllocationStatus(params: IResParamsPackageAllocations): Observable<ResPackageAllocation> {
    return this.callDynamicAPI('product/allocationStatus', params, 'POST')
      .pipe(
        map<DynamicApiResponse, ResPackageAllocation>((response) => new ResPackageAllocation(response.body?.[0]))
      );
  }

  /**
   * Retrieves a package product Optimized
   *
   * @param {IResPackageSelectParams} params
   * @return {*}  {Observable<ReservationBasePackage>}
   * @memberof ReservationApiService
   */
  public getPackageOpt(params: IResPackageSelectParams): Observable<ReservationBasePackage> {
    return this.callDynamicAPI('product/selectPkgOpt', params, 'POST')
      .pipe(
        map<DynamicApiResponse, ReservationBasePackage>((response) => new ReservationBasePackage(response))
      );
  }

  public getPackageVoyagePrices(voyageID?: number): Observable<ResCruisePriceType[]> {
    return this.callDynamicAPI('package/selectPackageVoyagePrices', { VoyageID: voyageID }).pipe(
      map<DynamicApiResponse, ResCruisePriceType[]>((response) => {
        return response.body.map((c: IReservationCruisePriceType) => new ResCruisePriceType(c));
      })
    )
  }

  public getPackageOptionToSuiteCategoryList(packageID?: number | string): Observable<ResPackageOptionToSuiteCategory[]> {
    return this.callDynamicAPI('package/getOptionSuiteCategory', { PackageID: packageID }).pipe(
      map<DynamicApiResponse, ResPackageOptionToSuiteCategory[]>((response) => {
        return response.body.map((c: IResPackageOptionToSuiteCategory) => new ResPackageOptionToSuiteCategory(c));
      })
    )
  }

  public managePackageOptionToSuiteCategoryList(params: IResApiPackageOptionToSuiteCategory[]) {
    const packageID = params?.[0].PackageID;
    return this.callDynamicAPI('package/manageOptionSuiteCategory', { PackageOption2SuiteCategory: params, PackageID: packageID }, 'POST').pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }


  /**
   * Retrieves a camp product
   *
   * @param {IResCampSelectParams} params
   * @return {*}  {Observable<ReservationCamp>}
   * @memberof ReservationApiService
   */
  public getCamp(params: IResCampSelectParams): Observable<ReservationCamp> {
    return this.callDynamicAPI('product/selectCMP', params, 'POST')
      .pipe(
        map<DynamicApiResponse, ReservationCamp>((response) => new ReservationCamp(response.body))
      );
  }
  /**
   * Retrieves a service product
   *
   * @param {IResProductServiceSelectParams} params
   * @return {*}  {Observable<ResProductService>}
   * @memberof ReservationApiService
   */
  public getProductService(params: IResProductServiceSelectParams): Observable<ResProductService> {
    return this.callDynamicAPI('product/selectSRV', params, 'POST')
      .pipe(
        map<DynamicApiResponse, ResProductService>((response) => new ResProductService(response.body))
      );
  }
  /**
   * Retrieves a service product
   *
   * @param {IResProductServiceSelectParams} params
   * @return {*}  {Observable<ReservationServiceType>}
   * @memberof ReservationApiService
   */
  getSearchableProductService(): Observable<ReservationServiceType[]> {
    return this.callDynamicAPI('search/allProductService', {}, 'GET').pipe(
        map<DynamicApiResponse, ReservationServiceType[]>((response) =>
          response.body.map((s: IReservationServiceType) => new ReservationServiceType(s))
        )
      )
  }

  getSearchDurationFilter(companyID?: number): Observable<ResBkgDuration[]> {
    return this.getDynamicAPI('booking/getSearchDurationFilter', {CompanyID: companyID}).pipe(
      map<DynamicApiResponse, ResBkgDuration[]>((response) => {
        return response.body.map((data: any) => new ResBkgDuration(data))
      })
    );
  }
  getDmcEDocUrl(bkgID: number): Observable<string> {
    return this.getDynamicAPI('booking/getDmcEDocUrl', {BkgID: bkgID}).pipe(
      map<DynamicApiResponse, string>((response) => {
        return response.body[0]?.Url ?? ''
      })
    );
  }
  /**
   *
   * Retrieves a package product
   *
   * @param {IResPackageSearchParams} params
   * @return {*}  {Observable<BookingPackage>}
   * @memberof ReservationApiService
   */
  public getPaginatedPackage(params: IResPackageSearchParams): Observable<DynamicApiResponsePaginatedModel<ResPackage>> {
    (params.IsRetail = params?.IsRetail ? 'Y' : 'N');

    if (params?.MonthList) {
      if (params?.MonthList?.length === 0) {
        params.MonthList = '';
      } else if (params?.MonthList?.length === 1) {
        params.MonthList = params.MonthList[0];
      } else {
        params.MonthList = params.MonthList.toString();
      }
    }

    return this.callDynamicAPI(
      'product/searchLightPKG',
      params,
      'POST'
    ).pipe(
      map<DynamicApiResponse, DynamicApiResponsePaginatedModel<ResPackage>>(
        (response) => {
          response.body = response.body.map(
            (pkg: IReservationApiPackageSetup) => new ResPackage(pkg)
          );
          return new DynamicApiResponsePaginatedModel<ResPackage>(response);
        }
      )
    );
  }

  public getPackageList(params:IResPackageSearchParams): Observable<ResPackage[]> {

    return this.callDynamicAPI(
      'product/searchLightPKG',
      params,
      'POST'
    ).pipe(
      map<DynamicApiResponse, ResPackage[]>(
        (response) => response.body = response.body.map((pkg: IReservationApiPackageSetup) => new ResPackage(pkg))
      )
    );
  }


  /**
   *
   * Retrieves a package single supplement cost
   *
   * @param {IResPackageSearchParams} params
   * @return {*}  {Observable<BookingPackage>}
   * @memberof ReservationApiService
   */
  public getPackageSingleSupp(params: IResPackageSingleSuppParams): Observable<ResPackageSingleSupp[]> {

    return this.callDynamicAPI(
      'package/selectSingleSupplement',
      params,
      'GET'
    ).pipe(
      map<DynamicApiResponse, ResPackageSingleSupp[]>(
        (response) => response.body.map((pkg: any) => new ResPackageSingleSupp(pkg))
      )
    );
  }
  getRightRezOfficeIdConfig(): Observable<ResRightRezOfficeIdConfig[]> {
    return this.callDynamicAPI('rightrez/officeIdConfig',
      { },
      'GET',
    ).pipe(
      map<DynamicApiResponse, Array<ResRightRezOfficeIdConfig>>((response) => {
        return response.body.map((cfg: any) => new ResRightRezOfficeIdConfig(cfg));
      })
    );
  }
  /*

    getPackagesPaged(
      filters?: IResPackageSearchParams
    ): Observable<DynamicApiResponsePaginatedModel<ResPackage>> {
      if (!isUndefined(filters)) {
        const owner = this.reservationService.currentOwner.getValue();
        if (owner.isAgent()) {
          filters.AgencyID = owner.agencyID;
        }

        // Remap isRetail boolean filter to ReservationApiBoolean
        !isUndefined(filters.IsRetail) &&
          (filters.IsRetail = filters!.IsRetail ? 'Y' : 'N');
      }

      return this.callDynamicAPI(
        'product/searchPKG',
        this.remapCruiseSelectFilters(filters),
        'POST'
      ).pipe(
        map<DynamicApiResponse, DynamicApiResponsePaginatedModel<ResPackage>>(
          (response) => {
            response.body = response.body.map(
              (pkg: IReservationApiPackageSetup) => new ResPackage(pkg)
            );
            return new DynamicApiResponsePaginatedModel<ResPackage>(response);
          }
        )
      );
    }
  */

  /**
   * Retrieves shorex price matching with filters specified
   *
   * @param {*} filter*
   * @return {*}  {Observable<Array<ReservationShorexPrice>>}
   * @memberof ReservationApiService
   */
  public getShorexOptions(filter?: any): Observable<Array<ReservationShorexOption>> {
    return this.callDynamicAPI('shorex/selectOptions', filter, 'POST').pipe(
      map<DynamicApiResponse, Array<ReservationShorexOption>>((response) =>
        response.body.map((option: IReservationApiShorexOption) => new ReservationShorexOption(option))
      )
    );
  }

  // public getSuggestedItems(suggestedItemsReq: IResSuggestedItemsRequest, currentCategory: string) {
  //   return this.callDynamicAPI('booking/suggestedItems' + currentCategory, suggestedItemsReq, 'POST').pipe(
  //     map<DynamicApiResponse, Array<ResBookingSuggestedItem>>((response) =>
  //       response.body.map((product: any) => {
  //         switch (currentCategory) {
  //           case 'CRU':
  //             return new ReservationCruise(product);
  //           case 'SHX':
  //             return new ReservationShorex(product);
  //           default:
  //             return product;
  //         }
  //       })
  //     )
  //   );
  // }
  /**
   * Retrieves user's permissions
   *
   * @return {*}  {Observable<IReservationApiPermission>}
   * @memberof ReservationApiService
   */
  public getPermission(): Observable<IReservationApiPermission> {
    return this.callDynamicAPI('reservation/permission', {}, 'POST').pipe(
      map<DynamicApiResponse, IReservationApiPermission>((response) => response.body)
    );
  }

  public getReservationDashboard() {
    const self = this;
    function createMenuCategory(item: any): IReservationHomeCategory {
      const menuCategory = {
        icon: {
          iconCode: item.ImageId,
          iconType: item.IconType || EidosIconType.SVG,
          iconSize: item.IconSize || 2,
        } as IEidosIcon,
        title: item.Label,
        description: item.Description,
        children: [],
        isFavourites: false,
      } as IReservationHomeCategory;
      return menuCategory;
    }

    function addToken(path:string){
      const token = self.eidosSecurityService.getToken();
      if(!token) return path;
      const sep = path.includes('?') ? '&' : '?';
      return `${path}${sep}token=${token}`
    }

    function createMenuItem(item: any, items: Array<any>): IReservationHomeItem {
      const url = item.Module==='__integrated__' ? addToken(item.Path) : item.Path
      const menuItem: IReservationHomeItem = {
        id: item.MenuId,
        icon: {
          iconCode: item.IconId,
          iconType: item.IconType || EidosIconType.SVG,
          iconSize: item.IconSize || 1,
        } as IEidosIcon,
        title: item.Label,
        description: item.Description,
        action: !!item.Path ? IReservationHomeItemActions.URL : undefined,
        actionData: {
          target: !!item.NewWindow ? '_blank' : '_self',
          url: url,
          safeUrl: self.domSanitizer.bypassSecurityTrustUrl(url),
          urlRelative: item.PathType ? item.PathType === 'R' : false,
          module: item.Module
        } as IReservationHomeActionData,
        children: [],
      };

      //add children
      items
        .filter((child) => child.ParentId === item.MenuId)
        .sort((a, b) => a.Pos - b.Pos)
        .forEach((child) => menuItem.children!.push(createMenuItem(child, items)));

      if (items.length > 0 && item.ImageId) {
        menuItem.icon.iconCode = item.ImageId;
        menuItem.icon.iconSize = item.IconSize || 2;
      }
      return menuItem;
    }
    function _prepareDashboard(items: Array<any>): Array<IReservationHomeCategory> {
      const homeMenu: Array<IReservationHomeCategory> = [];

      items
        .filter((item) => item.ParentId === 0)
        .sort((a, b) => a.Pos - b.Pos)
        .forEach((item) => {
          const menuItem = createMenuCategory(item);

          //add children
          items
            .filter((child) => child.ParentId === item.MenuId)
            .sort((a, b) => a.Pos - b.Pos)
            .forEach((child) => menuItem.children.push(createMenuItem(child, items)));

          homeMenu.push(menuItem);
        });

      return homeMenu;
    }
    return this.callDynamicAPI('oneis/home', {}, 'GET').pipe(
      map<DynamicApiResponse, Array<IReservationHomeCategory>>((response) =>
        response.body.HomeList ? _prepareDashboard(response.body.HomeList) : _prepareDashboard([])
      )
    );
  }


  /**
   * Search voyages with filters
   *
   * @param {IResApiVoyagesSearchParameters} [filters]
   * @return {DynamicApiResponsePaginatedModel<ReservationCruise>}
   * @memberof ReservationApiService
   */
  public getVoyagesSearch(filters: IResApiVoyagesSearchParameters) {
    return this.callDynamicAPI(
      'voyages/search',
      filters,
      'POST'
    ).pipe(
      map<DynamicApiResponse, DynamicApiResponsePaginatedModel<ReservationCruise>>(
        (response) => {
          const paginatedModel = new DynamicApiResponsePaginatedModel<ReservationCruise>(response);
          paginatedModel.content = response.body.map(
            (cruise: IReservationApiCruise) => new ReservationCruise(cruise)
          );
          return paginatedModel;
        })
    );
  }
  public getPeopleSearch(filters: IResApiPeopleParameters) {
    return this.callDynamicAPI(
      'people/select',
      filters,
      'POST'
    ).pipe(
      map<DynamicApiResponse, DynamicApiResponsePaginatedModel<ResPeople>>(
        (response) => {
          const paginatedModel = new DynamicApiResponsePaginatedModel<ResPeople>(response);
          paginatedModel.content = response.body.map(
            (people: IReservationApiCruise) => new ResPeople(people)
          );
          return paginatedModel;
        })
    );
  }

  peoplePackageManage(filters: IResApiPackagePeopleManageParameters) {
    return this.callDynamicAPI(
      'people/packageManage',
      filters,
      'POST'
    ).pipe(
      map<DynamicApiResponse, any>(response => response.body)
    );
  }
  peopleManage(filters: IResApiPeopleManageParameters) {
    return this.callDynamicAPI(
      'people/manage',
      filters,
      'POST'
    ).pipe(
      map<DynamicApiResponse, ResPeople>(response => new ResPeople(response.body[0]))
    );
  }

  getPeopleTypeList(): Observable<ResPeopleType[]> {
    return this.getDynamicAPI('people/getPeopleType').pipe(
      map<DynamicApiResponse, ResPeopleType[]>((response) => {
        return response.body.map((data: any) => new ResPeopleType(data))
      })
    );
  }

  getPromotionShips(promoID?: number) {
    return this.callDynamicAPI('promoVoyages/getShips', { PromoID: promoID }).pipe(
      map<DynamicApiResponse, Array<ResPromoShip>>((response) =>
        response.body.map((p: IResPromoShip) => new ResPromoShip(p))
      )
    );
  }

  getPromoPackages(promoID?: number): Observable<ResPromoPackage[]> {
    return this.getDynamicAPI('promo/getPromoPackages', {PromoID: promoID}).pipe(
      map<DynamicApiResponse, ResPromoPackage[]>((response) => {
        return response.body.map((data: any) => new ResPromoPackage(data))
      })
    );
  }

  managePromoPackages(params?: IResApiManagePromoPackages) {
    return this.postDynamicAPI('promo/managePromoPackages', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  getPromotionPriceTypes(promoID?: number, companyPromoID?: number) {
    return this.callDynamicAPI('promoVoyages/getPriceTypes', { PromoID: promoID, CompanyPromoID: companyPromoID }).pipe(
      map<DynamicApiResponse, Array<ResPromoPriceType>>((response) =>
        response.body.map((p: IResPromoPriceType) => new ResPromoPriceType(p))
      )
    );
  }

  packageRefreshPdf(packageID: number) {
    return this.callDynamicAPI(
      'package/PackagePdfRefresh',
      { PackageID: packageID },
      'GET'
    ).pipe(
      map<DynamicApiResponse, boolean>(
        (response) => response.errors.length == 0
      )
    );
  }
  /**
   * Search voyages with filters
   *
   * @param {IResApiVoyagesSearchParameters} [filters]
   * @return {DynamicApiResponsePaginatedModel<ReservationCruise>}
   * @memberof ReservationApiService
   */
  public getAgenciesOrDirectsSearch(filters: IResApiAgenciesSearchParameters) {

    switch (filters.SearchType) {

      case ResApiAgenciesSearchType.Agencies:
        return this.callDynamicAPI(
          'agency/select',
          Object.assign({ Typology: 'AGN' }, filters),
          'GET'
        ).pipe(
          map<DynamicApiResponse, DynamicApiResponsePaginatedModel<ReservationAgency | ReservationIndividual>>(
            (response) => {
              const paginatedModel = new DynamicApiResponsePaginatedModel<ReservationAgency>(response);
              paginatedModel.content = response.body.map(
                (agency: IReservationApiAgency) => new ReservationAgency(agency)
              );
              return paginatedModel;
            })
        );

      case ResApiAgenciesSearchType.Direct:
        return this.callDynamicAPI(
          'individual/select',
          filters,
          'GET'
        ).pipe(
          map<DynamicApiResponse, DynamicApiResponsePaginatedModel<ReservationIndividual>>(
            (response) => {
              const paginatedModel = new DynamicApiResponsePaginatedModel<ReservationIndividual>(response);
              paginatedModel.content = response.body.map(
                (individual: IReservationApiIndividual) => new ReservationIndividual(individual)
              );
              return paginatedModel;
            })
        );

      case ResApiAgenciesSearchType.Consortium:
        return this.callDynamicAPI(
          'agency/select',
          Object.assign({ Typology: 'CNS' }, filters),
          'GET'
        ).pipe(
          map<DynamicApiResponse, DynamicApiResponsePaginatedModel<ReservationAgency>>(
            (response) => {
              const paginatedModel = new DynamicApiResponsePaginatedModel<ReservationAgency>(response);
              paginatedModel.content = response.body.map(
                (agency: IReservationApiAgency) => new ReservationAgency(agency)
              );
              return paginatedModel;
            })
        );

      case ResApiAgenciesSearchType.HeartQuarter:
        return this.callDynamicAPI(
          'agency/select',
          Object.assign({ Typology: 'AHQ' }, filters),
          'GET'
        ).pipe(
          map<DynamicApiResponse, DynamicApiResponsePaginatedModel<ReservationAgency>>(
            (response) => {
              const paginatedModel = new DynamicApiResponsePaginatedModel<ReservationAgency>(response);
              paginatedModel.content = response.body.map(
                (agency: IReservationApiAgency) => new ReservationAgency(agency)
              );
              return paginatedModel;
            })
        );
    }
  }

  getIndividualGuests(filters: IReservationApiIndividualsParameters) {
    return this.callDynamicAPI(
      'individual/select',
      filters,
      'GET'
    ).pipe(
      map<DynamicApiResponse, DynamicApiResponsePaginatedModel<ReservationIndividual>>(
        (response) => {
          const paginatedModel = new DynamicApiResponsePaginatedModel<ReservationIndividual>(response);
          paginatedModel.content = response.body.map(
            (individual: IReservationApiIndividual) => new ReservationIndividual(individual)
          );
          return paginatedModel;
        })
    );
  }

  checkReferralPastGuest(bkgID: number, params: { IndividualID: number }[]) {
    return this.callDynamicAPI('referral/CheckPastGuest', { BkgID: bkgID, Guests: params }, 'POST').pipe(
      map<DynamicApiResponse, ResPastGuestReferral[]>((response) => {
        return response.body.map((r: IResPastGuestReferralParameters) => new ResPastGuestReferral(r));
      })
    );
  }

  getReferralIndividualID(params: IResReferralParameters) {
    return this.callDynamicAPI('booking/GetGuestReferral', params, 'GET').pipe(
      map<DynamicApiResponse, { GuestReferralID: number, OldDirectID: number }>((response) => response.body[0])
    );
  }

  manageReferral(params: IResReferralParameters) {
    if(this.callMillisec>0 && DateTime.now().toMillis()-this.callMillisec<1000) return of(0)
    return this.callDynamicAPI('booking/ManageGuestReferral', params, 'POST').pipe(
      map<DynamicApiResponse, number>((response) => response.body)
    );
  }

  mailGuestChecker(individualID: number) {
    return this.callDynamicAPI('booking/checkMailGuestReferral', { DirectID: individualID }, 'GET').pipe(
      map<DynamicApiResponse, { DirectID: number, Email: string }>((response) => response.body[0])
    );
  }

  getPortfolioGuests(individualID: number[], bkgID: number) {
    return this.callDynamicAPI('booking/getPortfolioGuests', { DirectID: individualID, BkgID: bkgID }, 'GET').pipe(
      map<DynamicApiResponse, ReservationBookingReferralGuests[]>((response) => response.body.map((g: any) => new ReservationBookingReferralGuests(g))
      )
    );
  }

  managePortfolioDiscounts(bkgID: number, tblPortfolio: IReservationApiManageReferralDiscounts[]) {
    return this.callDynamicAPI('booking/managePortfolioDiscountReferral', { BkgID: bkgID, TblPortfolio: tblPortfolio }, 'POST').pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  checkReferralVerification(bkgID: number) {
    return this.callDynamicAPI('booking/getReferralIndividualVerification', { BkgID: bkgID }, 'GET').pipe(
      map<DynamicApiResponse, { IsVerified: string, Verified: string }>((response) => response.body[0])
    );
  }

  /**
   * Select inventory capacity of a cruise
   *
   * @return {*}
   * @memberof ReservationApiService
   */
  getInventorySelectCapacity(payload: IResVoyageSearchRequiredParams, owner: ResOwner) {
    if(owner.isValid) {
      if (owner.isAgent()) {
        payload.OwnerType='AGENCY'
        payload.AgencyCod = owner.agencyID;
      } else if (owner.isDirect()) {
        payload.OwnerType = "DIRECT";
      }
    }
    return this.callDynamicAPI('inventory/selectcapacity', payload, 'GET').pipe(
      map<DynamicApiResponse, ReservationCruiseInventoryModel>((response) => new ReservationCruiseInventoryModel(response.body))
    );
  }
  /**
   * Select inventory capacity of a cruise with multisuite
   *
   * @return {*}
   * @memberof ReservationApiService
   */
  retriveInventorySelectCapacity(payload: IResVoyageSearchRequiredParams, owner: ResOwner) {
    if(owner.isValid) {
      if (owner.isAgent()) {
        payload.OwnerType='AGENCY'
        payload.AgencyCod = owner.agencyID;
      } else if (owner.isDirect()) {
        payload.OwnerType = "DIRECT";
      }
    }
    return this.callDynamicAPI('inventory/selectcapacity', payload, 'POST').pipe(
      map<DynamicApiResponse, ReservationCruiseInventoryModel>((response) => new ReservationCruiseInventoryModel(response.body))
    );
  }
  /**
   * Select inventory suites of a cruise
   *
   * @param {IResVoyageSearchRequiredParams} payload
   * @param {IReservationSuiteAmenity[]} [amenities=[]]
   * @return {*}
   * @memberof ReservationApiService
   */
  getInventorySuiteList(payload: IResVoyageSearchRequiredParams, amenities: IReservationSuiteAmenity[] = []) {
    return this.callDynamicAPI('inventory/suiteList', payload, 'GET').pipe(
      map<DynamicApiResponse, Array<ReservationCruiseSuiteListAvailabilityModel>>((response) =>
        response.body.map(
          (reservationCruiseAvail: IReservationApiCruiseSuiteListAvailability) =>
            new ReservationCruiseSuiteListAvailabilityModel(reservationCruiseAvail, amenities)
        )
      )
    );
  }


  getPackagePricesHistory(payload: IResPackagePricesHistoryParams) {
    return this.callDynamicAPI('package/pricesHistory', payload, 'GET').pipe(
      map<DynamicApiResponse, ReservationPackagePricesAndCostsHistory>((response) =>
        new ReservationPackagePricesAndCostsHistory(response.body)
      )
    );
  }


  /**
   * update inventory suites of a cruise
   *
   * @param {IResInventoryUpdateParams} payload
   * @return {*}
   * @memberof ReservationApiService
   */
  inventoryUpdate(payload: IResInventoryUpdateParams) {
    return this.callDynamicAPI('inventory/update', payload, 'POST').pipe(
      map<DynamicApiResponse, object>((response) => response.body)
    );
  }

  gtyUpdate(payload: IResGtyUpdateParams) {
    return this.callDynamicAPI('gty/update', payload, 'POST').pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }
  /**
   * Retrieves cruise availability
   *
   * @return {*}
   * @memberof ReservationApiService
   */
  getCruiseAvailabilityNew(owner: ResOwner, payload: IReservationApiCruiseInventoryParams, showAllSuites: boolean = false) {
    if(owner.isValid) {
      if (owner.isAgent()) {
        payload.OwnerType='AGENCY'
        payload.AgencyCod = owner.agencyID;
      } else if (owner.isDirect()) {
        payload.OwnerType = "DIRECT";
      }
    }
    const url = showAllSuites ? 'inventory/selectAll' : 'inventory/select';
    return this.callDynamicAPI(url, payload, 'GET').pipe(
      map<DynamicApiResponse, Array<ReservationCruiseAvailabilityModel>>((response) =>
        response.body.map((aval: IReservationApiCruiseAvailability) => new ReservationCruiseAvailabilityModel(aval))
      )
    );
  }
  getVoyageInventoryGTY(voyageID: number) {
    return this.callDynamicAPI('inventory/selectGTY', {VoyageID: voyageID}, 'GET').pipe(
      map<DynamicApiResponse, ResInventoryVoyageGTY[]>((response) =>
        response.body.map((item: any) => new ResInventoryVoyageGTY(item))
      )
    );
  }
  /**
   * Get current selected products
   *
   * @param {ResProductType} productCode
   * @param {number[]} [productIDs=[]]
   * @param {*} [filters]
   * @return {*}  {Promise<any[]>}
   * @memberof ResPreBookingUtility
   */
  getProductsPaymentScheduler(params: IResProductsPaymentSchedulerParams) {
    return this.callDynamicAPI('inventory/pyamentScheduler', params, 'GET').pipe(
      map<DynamicApiResponse, Array<ResProductsPaymentScheduler>>((response) =>
        response.body.map((data: any) => new ResProductsPaymentScheduler(data))
      )
    );
  }
  /**
   * Get suites deckplan of a cruise
   *
   * @param {IResSuiteDeckplanParams} payload
   * @return {*}
   * @memberof ReservationApiService
   */
  getSuiteDeckplan(payload: IResSuiteDeckplanParams) {
    //TODO cache data
    return this.callDynamicAPI('suite/deckplan', payload, 'GET').pipe(
      map<DynamicApiResponse, ReservationCruiseSuiteDeckplanModel>(
        (response) => new ReservationCruiseSuiteDeckplanModel(response.body as IReservationApiCruiseSuiteDeckplanModel)
      )
    );
  }
  getDeckplanInfo(payload: IResDeckplanInfoParams) {
    //TODO cache data
    return this.callDynamicAPI('suite/deckplanInfo', payload, 'GET').pipe(
      map<DynamicApiResponse, ReservationCruiseSuiteDeckplanConfiguration>(
        (response) => new ReservationCruiseSuiteDeckplanConfiguration(response.body[0])
      )
    );
  }
  //#region Booking

  /**
   * Create booking with specified params
   *
   * @param {IResApiParametersQuoteCreate} params
   * @return {*}  {Observable<IResBookingCreateApiResponse>}
   * @memberof ReservationApiService
   */
  createBooking(params: IResApiParametersQuoteCreate): Observable<IResBookingCreateApiResponse> {
    return this.postDynamicAPI('quote/create', params).pipe(
      map<DynamicApiResponse, IResBookingCreateApiResponse>((apiResponse) => {
        let response: IResBookingCreateApiResponse = {
          BkgID: ResBooking.INVALID_BOOKING
        };

        //TODO: check function Artiom
        //Exception only used in Heritage deposit page
        // if(apiResponse.body.DT0[0].BkgID) {
        //   response.BkgID = apiResponse.body.DT0[0].BkgID ?? ResBooking.INVALID_BOOKING;
        //   return response;
        // }

        if (!!apiResponse && Array.isArray(apiResponse.body) && apiResponse.body.length > 0) {
          response.BkgID = apiResponse.body[0].BkgID ?? ResBooking.INVALID_BOOKING;
          response.ErrorMessage = apiResponse.body[0].ErrorMessage;
        }

        return response;
      })
    );
  }

  getTailorMadeParameters() {
    return this.callDynamicAPI('tailorMade/parameters', {}, 'GET').pipe(
      map<DynamicApiResponse, ResTailorMadeParameters>((response) => new ResTailorMadeParameters(response.body))
    );
  }
  /**
   * Retrieves booking items list
   *
   * @return {*}  {Observable<ResBkgItem[]>}
   * @memberof ReservationApiService
   */
  getBookingItems(bkgID:number, filters?: IResBkgItemApiParams): Observable<ResBkgGroupedItems> {
    return this.callDynamicAPI('booking/items', {BkgID:bkgID, ...filters}, 'POST').pipe(
      map<DynamicApiResponse, ResBkgGroupedItems>((response) => {

        const content = new ResBkgGroupedItems()

        const items = new DynamicApiResponsePaginatedModel<ResBkgItem>(response);

        items.content = response.body.items.map((item: any) => new ResBkgItem(item))
        const flitghtInfos:ResBkgItemFlightInfo[] = (response.body.flightInfo?.length ?? 0)>0 ? response.body.flightInfo.map((fi: any)=>new ResBkgItemFlightInfo(fi)) : []
        items.content
          .forEach(itm=>
            itm.supplements.forEach(s=>
              s.flitghtInfos = flitghtInfos.filter(f=>f.itemID===s.itemID)
            )
          )
        content.itemsPaginated = items
        content.total = new ResBkgItemTotal(response.body.total)



        let aggregationField = ''

        content.groups = response.body.groups.map((item: any, index:number) => {
          const g = new ResGroups()
          if(item.GuestCod) {
            aggregationField='guestCod'
            g.code = `${item.GuestCod}`
            g.description = item.GuestName
            g.id = item.GuestCod
          } else if(item.RoomConfHdrID) {
            aggregationField='roomConfHdrID'
            g.code = `${item.RoomConfHdrID}`
            g.description = `G${index+1}`
            g.id = item.RoomConfHdrID
          } else if(item.ProductCod) {
            aggregationField='productCod'
            g.code = item.ProductCod
            g.description = item.ProductName
            g.id = index+1
            g.attributes = [
              {productName:item.ProductName},
              {itemName:item.ItemName},
              {suiteCategoryID:item.SuiteCategoryID},
              {suiteCategory:item.SuiteCategory},
              {priceCategoryID:item.PriceCategoryID},
              {priceCategory:item.PriceCategory},
              {suiteNo:item.SuiteNo},
            ]
          } else if(item.SupplierID) {
            aggregationField='supplierID'
            g.code = `${item.SupplierID}`
            g.description = item.SupplierName
            g.id = item.SupplierID
          }

          return g
        })

        let row=0, lastCodes=''
        items.content.forEach(item => {
          item.aggregationDescription = ''
          item.aggregationField = aggregationField
          const codes = `${(item as any)[aggregationField]}`;

          if(lastCodes!==codes) {
            row = 0
            lastCodes=codes
          }
          item.aggregationGroupRow = ++row

          codes.split(',').forEach(code=>{
              const group = content.groups.find(g=>g.code==code)
              if(group && !item.aggregations.find(ag=>ag.code===code)) {
                item.aggregations.push(group)
                item.aggregationDescription += ', ' + group.description
              }
          })

          item.aggregationDescription = item.aggregationDescription.replace(/^\, /,'')
        })

        return content
      }),
      catchError((_response: IDynamicApiResponse) => {
        return [];
      })
    );
  }

  manageMultiItems(bkgID: number, items: IResApiBkgItemsManage[], source?:string) {
    return this.callDynamicAPI('booking/manageMultiItems', {BkgID: bkgID, EditItem: items, Source:source}, 'POST');
  }


  /**
   * Retrieves booking cancel reasons list
   *
   * @return {*}  {Observable<ResBookingCancelReason>}
   * @memberof ReservationApiService
   */
  getBookingCancelReason(bkgID?:number): Observable<Array<ResBookingCancelReason>> {
    return this.callDynamicAPI('booking/cancelReason', {BkgID: bkgID}, 'GET').pipe(
      map<DynamicApiResponse, Array<ResBookingCancelReason>>((response) => response.body.map((r: any) => new ResBookingCancelReason(r))),
      catchError((_response: IDynamicApiResponse) => {
        return [];
      })
    );
  }
  getFccCertificate(bkgID?:number): Observable<ResFccCalculation[]> {
    return this.callDynamicAPI('fcc/calculationGet', {BkgID: bkgID}, 'GET').pipe(
      map<DynamicApiResponse, ResFccCalculation[]>((response) =>
        response.body.map((r: any) => new ResFccCalculation(r))
      ),
      catchError((_response: IDynamicApiResponse) => {
        return [];
      })
    );
  }
  normalizeBookingGet(data: any) {

    const bkg = Object.assign({}, data?.DT0[0] ?? {})
    bkg.Owner = data?.DT1[0] ?? {}
    bkg.FinacialRecap = data.DT2 ?? []
    bkg.Voyages = data?.DT3 ?? []
    bkg.Voyages.forEach((v: any) => {
      v.Itineraries = data?.DT9.filter((i: any) => i.VoyageID == v.VoyageID)
      v.Features = data?.DT10.filter((f: any) => f.VoyageID == v.VoyageID)
    })

    bkg.Supplements = data.DT4
    bkg.Supplements.forEach((s: any) => {
      s.Promo = data?.DT8.find((p: any) => p.PromoID == s.PromoID) ?? {}
    })

    bkg.Pax = data?.DT5
    bkg.Pax.forEach((s: any) => {
      s.GuestOwnArrangments = data?.DT16.filter((d: any) => d.DetailID == s.DetailID)
    })

    bkg.Deposit = data?.DT6
    bkg.Penalties = data?.DT7

    bkg.CustomPackages = data?.DT11
    bkg.CustomPackages.forEach((s: any) => {
      s.CustomPackagesDetail = data?.DT12.filter((d: any) => d.DetailID == s.DetailID)
    })

    bkg.Packages = data?.DT13
    bkg.PriceJourney = data?.DT14
    bkg.JourneySupplements = data?.DT15

    bkg.Links = data?.DT17
    bkg.EmbarkDebarkInfo = data?.DT18[0] ?? {}
    bkg.EmbarkDebarkDetails = data?.DT19
    bkg.RoomsConfig = data?.DT20
    bkg.SummaryNotes = data?.DT21

    return bkg
  }




  /**
   * Retrieves booking matching with filters specified
   *
   * @param {IReservationApiBookingRequestParameters} [filter]
   * @param {boolean} [lightMode=false] Specify to retrieve booking in a minimal structure
   * @return {*}  {Observable<ResBooking>}
   * @memberof ReservationApiService
   */
  getBooking(
    filter?: IReservationApiBookingRequestParameters,
    lightMode: boolean = false
  ): Observable<ResBooking> {
    let url = lightMode ? 'lightBooking/select' : 'booking/selectOptimized';

    return this.callDynamicAPI(url, filter, 'GET').pipe(
      map<DynamicApiResponse, ResBooking>((response) => {
        const data = lightMode ? response.body : this.normalizeBookingGet(response.body)
        return new ResBooking(data)
      }),
      catchError((response: IDynamicApiResponse) => {
        return of(ResBooking.ErrorResponse(filter?.BkgID ?? 0, response.Errors ?? [], response));
      })
    );
  }
  /**
   * Post booking edit params to edit a booking
   *
   * @param {IReservationApiEditBookingParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  editBooking(params: IReservationApiEditBookingParameters) {
    return this.callDynamicAPI('booking/edit', params, 'POST');
  }
  /**
   * Post booking version request params to recalculate a booking
   *
   * @param {IReservationApiBookingVersionRequestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  recalculateBooking(params: IReservationApiBookingRequestParameters) {
    return this.callDynamicAPI('booking/recalculate', Object.assign({ Action: 'RePrice' }, params), 'POST');
  }
  /**
   * Performs booking version confirmation
   *
   * @param {IReservationApiBookingVersionRequestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  confirmBooking(params: IReservationApiBookingVersionRequestParameters) {
    return this.callDynamicAPI('booking/confirm', params, 'POST');
  }
  /**
   * Perform booking version deletion
   *
   * @param {IReservationApiBookingVersionRequestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  deleteBooking(params: IReservationApiBookingDelete) {
    return this.callDynamicAPI('booking/delete', params, 'POST');
  }
  /**
   * Perform booking version cloning
   *
   * @param {IReservationApiBookingVersionRequestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  cloneBooking(params: IReservationApiBookingVersionRequestParameters) {
    return this.callDynamicAPI('booking/addversion', params, 'POST');
  }
  /**
   * Request autheticated url to open myactivities on web site
   *
   * @param {IReservationApiBookingVersionRequestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  getMyActivitiesUrl(params: IReservationApiBookingGetMyActivitiesUrlParameters) {
    return this.getExternalAPI('oem/crystalws/Agent/OnBehalfOf', 'POST', params).pipe(
      map<any, string>(res => {
        const url = res.Body.url ?? '';
        return url;
      })
    );
  }
  getExternalGifUrl(params: IReservationApiBookingExternalGifUrlParameters) {
    return this.getExternalEmAPI('mb/s/BackDoor/AK', 'POST', params).pipe(
      map<any, string>(res => {
        const url = res.Body.url ?? '';
        return url;
      })
    );
  }
  getSpResult(spCode: string) {
    return this.getExternalAPI('oem/dd/listdata', 'POST', {name:spCode}).pipe(
      map<any, any[]>(res => {
        if(res.HasError) return of([])
        return res.listdata;
      })
    );
  }


  getDynamicMap(prms:IDynamicMapApiParams){
    return this.getExternalAPI('oem/keytechmaps/itinerary', 'POST', prms).pipe(
      map<any, ResDynamicMapResponse>(res => new ResDynamicMapResponse(res))
    );
  }

  /**
   * Request upsert guest on Travel studio
   *
   * @param {IReservationApiBookingVersionRequestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  upsertGuestOnTS(params: IReservationApiTsUpserGuest) {
    const payload = {
      id: (params.id ?? '').toString(),
      DB: params.DB
    }
    return this.callDynamicAPI('oem/travelstudio/sendGuest2TS', payload, 'POST').pipe(
      map<any, any>(res => {
        return res;
      })
    );
  }
  /**
   * Request autheticated url to open myactivities on web site
   *
   * @param {IReservationApiBookingVersionRequestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  bookingAddPackage(params: IReservationApiBookingAddPackageParameters) {
    return this.callDynamicAPI('booking/addpackage', params, 'POST').pipe(
      map<IDynamicApiResponse, IDynamicApiResponse>(res => {
        return res;
      })
    );
  }
  /**
   * Check if can perform booking action
   *
   * @param {IReservationApiBookingActionCheckParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  checkBookingAction(params: IReservationApiBookingActionCheckParameters) {

    const { Action, ...apiParams } = params;
    let apiAction = '';

    switch (Action) {
      case ReservationActionBooking.ChangeVoyageBookingVersion:
        apiAction = 'ChangeVoyage';
        break;
      default:
        throw {
          name: 'Unhandled booking action',
          message: 'Not implemented a check handling for this booking action.'
        };
    }

    return this.postDynamicAPI('booking/checkAction', {
      Action: apiAction,
      ...apiParams
    });
  }
  /**
   * Performs a booking action
   *
   * @param {IReservationApiBookingActionParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  executeBookingAction(params: IReservationApiBookingActionParameters, options?: any) {
    let route: string = '';
    switch (params.Action) {
      case ReservationActionBooking.ChangeSuiteBookingVersion:
        route = 'booking/changesuite';
        break;
      case ReservationActionBooking.PromoteBooking:
        route = 'booking/promote';
        break;
    }

    const { Action, ...routeParams } = params;

    if (options?.keepPrice) routeParams.KeepPrice = 'Y'

    return this.callDynamicAPI(route, routeParams, 'POST').pipe(
      map<DynamicApiResponse, ResApiBookingActionResponse>((response) => {
        const res = new ResApiBookingActionResponse()
        if(response.body.length>0) {
          if(response.body[0].ErrorCod) {
            res.error = true
            res.ok = false
            res.errorMessage = response.body.map((e:any)=>`${e.ErrorMessage}`)
          }
        }
        return res;
      })
    );
  }
  /**
   * Retrieves booking guests
   *
   * @param {number} bkgID
   * @param {number} sectionID
   * @return {*}
   * @memberof ReservationApiService
   */
  public getBookingGuests(bkgID: number, sectionID: number) {
    return this.postDynamicAPI('booking/guests', { BkgID: bkgID, SectionID: sectionID }).pipe(
      map<DynamicApiResponse, Array<BookingGuest>>((response) => {
        return response.body.map((guest: IReservationApiBookingGuest) => new BookingGuest(guest));
      })
    );
  }
  /**
   * Retrieves booking itinerary
   *
   * @param {number} bkgID
   * @return {*}
   * @memberof ReservationApiService
   */
  public getBookingItinerary(bkgID: number) {
    return this.postDynamicAPI('booking/itinerary', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, Array<ReservationCruiseItinerary>>((response) => {
        return response.body.map((i: IReservationApiCruiseItinerary) => new ReservationCruiseItinerary(i));
      })
    );
  }
  /**
   * Retrieves booking financials
   *
   * @param {number} bkgID
   * @return {*}
   * @memberof ReservationApiService
   */
  public getBookingFinancials(bkgID: number) {
    return this.getDynamicAPI('booking/financial', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, Array<ResBookingFinancialRecap>>((response) => {
        return response.body.Financials.map((i: IReservationApiBookingFinancialRecap) => new ResBookingFinancialRecap(i));
      })
    );
  }
  /**
   * Convert an OD to a booking
   *
   * @param {ResDepositToBookingConversionParams} params
   * @return {*}
   * @memberof ReservationApiService
   */
  convertODToBkg(params: ResDepositToBookingConversionParams) {
    return this.postDynamicAPI('booking/voyage2voyage', params)
  }
  /**
   * Perform booking V2V
   *
   * @param {ResBookingV2VParams} params
   * @return {*}
   * @memberof ReservationApiService
   */
  bookingV2V(params: ResBookingV2VParams) {
    return this.postDynamicAPI('booking/v2v', params);
  }
  /**
   * Get a booking guest booked activities
   *
   * @param {IResGetGuestBookedActivitiesParams} params
   * @return {Observable<ReservationBookingActivitiesBooked>}
   * @memberof ReservationApiService
   */
  getGuestBookedActivities(params: IResGetGuestBookedActivitiesParams): Observable<ReservationBookingActivitiesBooked> {
    return this.getDynamicAPI('booking/guestBookedActivities', params).pipe(
      map<DynamicApiResponse, ReservationBookingActivitiesBooked>((response) => {
        return new ReservationBookingActivitiesBooked(response.body);
      })
    );
  }

  getGuestTypesForBkg(params: { BkgID: number }): Observable<IReservationLoyaltyDiscount[]> {
    return this.callDynamicAPI('booking/guestTypes', params, 'GET').pipe(
      map<DynamicApiResponse, IReservationLoyaltyDiscount[]>((response) => response.body)
    );
  }

  getAllLinkedBkgs(params: { BkgID: number }): Observable<{ prev: IResLinkedBooking[], next: IResLinkedBooking[], linked: IResLinkedBooking[] }> {
    return this.getDynamicAPI('booking/checkBookingLink', params).pipe(
      map<DynamicApiResponse, { prev: IResLinkedBooking[], next: IResLinkedBooking[], linked: IResLinkedBooking[] }>((response) => {
        return { prev: response.body.linkedPrev, next: response.body.linkedNext, linked: response.body.linkedBooking };
      }
      ))
  }

  /**
   * Get links to other bkgs of current bkg
   *
   * @param {({ BkgID: number } | { Sequence: number })} params
   * @return {*}  {Observable<IReservationLinkedBooking[]>}
   * @memberof ReservationApiService
   */
  getLinkedBkgsOfBkg(params: { BkgID: number } | { Sequence: number }): Observable<IReservationLinkedBooking[]> {
    return this.callDynamicAPI('booking/getLinks', params, 'GET').pipe(
      map<DynamicApiResponse, IReservationLinkedBooking[]>((response) => response.body.map((i: any) => ({ ...i, Status: 'A' })))
    );
  }
  /**
   * Checks is if can be linked to bkgidprv as IsSameVyg
   *
   * @param {{ BkgID: number, BkgIDPrv: number, IsSameVyg: ReservationApiBoolean }} params
   * @return {*}  {(Observable<(any[] | {})>)}
   * @memberof ReservationApiService
   */
  checkBkgLink(params: { BkgID: number, BkgIDPrv: number, IsSameVyg: ReservationApiBoolean }): Observable<(any[] | {})> {
    return this.callDynamicAPI('booking/checkLink', params, 'GET').pipe(
      map<DynamicApiResponse, any[] | {}>((response) => response.body)
    );
  }
  /**
   * Upsert booking links
   *
   * @param {{ LinkedBkg: IReservationApiLinkedBookingManageParams[], IsSameVyg: ReservationApiBoolean }} params
   * @return {*}  {(Observable<(any[] | {})>)}
   * @memberof ReservationApiService
   */
  manageBkgLinks(params: { LinkedBkg: IReservationApiLinkedBookingManageParams[], IsSameVyg: ReservationApiBoolean }): Observable<(any[] | {})> {
    return this.callDynamicAPI('booking/upsertLinks', params, 'POST').pipe(
      map<DynamicApiResponse, any[] | {}>((response) => response.body)
    );
  }

  /**
   * Save OD notes
   *
   * @param {number} bkgID
   * @param {string} newNote
   * @return {*}
   * @return {*}
   * @memberof ReservationApiService
   */
  saveBookingNotes(bkgID: number, note: string) {
    return this.postDynamicAPI('booking/upsertHeaderNote', { bkgID, note });
  }

  //#endregion Booking

  /**
   * Retrieves cruise availability
   *
   * @return {*}  {Observable<ReservationAvailabilityModel>}
   * @memberof ReservationApiService
   */
  public getCruiseAvailabilityWithDetails(voyageId: number, route: string = 'cruise/availability') {
    return this.callDynamicAPI(route, { voyageId }, 'GET').pipe(
      map<DynamicApiResponse, ReservationCruiseAvailabilityModel>((response) => {
        const reservationCruiseAvailability = new ReservationCruiseAvailabilityModel(response.body);
        return reservationCruiseAvailability;
      })
    );
  }

  /**
   *  Get Individuals, used in bookin guests search
   *
   * @param {IReservationApiIndividualsParameters} [filters]
   * @return {*}
   * @memberof ReservationApiService
   */
  public getIndividuals(filters?: IReservationApiIndividualsParameters) {
    return this.callDynamicAPI('individual/select', filters, 'GET').pipe(
      map<DynamicApiResponse, ReservationIndividual[]>((response) => {
        return response.body.map((r: IReservationApiIndividual) => new ReservationIndividual(r));
      })
    );
  }

  public getPaginatedIndividuals(filters?: IReservationApiIndividualsParameters, loadOptions?: any) {
    filters = this.fillPaginatedParams(filters, loadOptions);

    return this.callDynamicAPI('individual/select', filters, 'GET').pipe(
      map<DynamicApiResponse, { data: any[]; totalCount: number }>((response) => {
        return {
          data: response.body.map((r: IReservationApiIndividual) => new ReservationIndividual(r)),
          totalCount: response.recordsCount,
        };
      })
    );
  }



  /**
   * Get Owners, used in owners search
   *
   * @param {IReservationApiIndividualsParameters} [filters]
   * @param {*} [loadOptions]
   * @return {*}
   * @memberof ReservationApiService
   */
  public getOwners(filters?: IReservationApiIndividualsParameters) {
    return this.callDynamicAPI('individualOwners/select', filters, 'GET').pipe(
      map<DynamicApiResponse, ReservationIndividual[]>((response) => {
        return response.body.map((r: IReservationApiIndividual) => new ReservationIndividual(r));
      })
    );
  }

  public getPaginatedOwners(filters?: IReservationApiIndividualsParameters, loadOptions?: any) {
    filters = this.fillPaginatedParams(filters, loadOptions);

    return this.callDynamicAPI('individualOwners/select', filters, 'GET').pipe(
      map<DynamicApiResponse, { data: any[]; totalCount: number }>((response) => {
        return {
          data: response.body.map((r: IReservationApiIndividual) => new ReservationIndividual(r)),
          totalCount: response.recordsCount,
        };
      })
    );
  }

  /**
   * Get individual household
   *
   * @param {IReservationApiIndividualsParameters} [filters]
   * @return {*}
   * @memberof ReservationApiService
   */
  public getHousehold(household_id: number) {
    return this.callDynamicAPI('individual/select', { household_id }, 'GET').pipe(
      map<DynamicApiResponse, ReservationIndividual[]>((response) => {
        return response.body.map((r: IReservationApiIndividual) => new ReservationIndividual(r));
      })
    );
  }


  public guestCheckBookingExists(params: IResApiGuestCheckBookingExistsParams): Observable<{ current: IResApiGuestCheckBookingExistsResult[], next: IResApiGuestCheckBookingExistsResult[], previous: IResApiGuestCheckBookingExistsResult[] }> {
    return this.callDynamicAPI('individual/checkBookingExists', params, 'POST').pipe(
      map<DynamicApiResponse, { current: IResApiGuestCheckBookingExistsResult[], next: IResApiGuestCheckBookingExistsResult[], previous: IResApiGuestCheckBookingExistsResult[] }>((response) => {
        return { current: response.body.Current, next: response.body.Next, previous: response.body.Previous };
      }
      ))
  }

  /**
   * Manage Individuals
   *
   * @param {IReservationApiIndividual} params
   * @return {*}
   * @memberof ReservationApiService
   */
  public manageIndividuals(params: IReservationApiIndividual) {
    return this.callDynamicAPI('individual/manage', params, 'POST');
  }

  /**
   * Get Agencies
   *
   * @param {IReservationApiAgency} [filters]
   * @return {*}
   * @memberof ReservationApiService
   */
  public getAgencies(filters?: IReservationApiAgency) {
    return this.callDynamicAPI('agency/select', filters, 'GET').pipe(
      map<DynamicApiResponse, ReservationAgency[]>((response) => {
        return response.body.map((r: IReservationApiAgency) => new ReservationAgency(r));
      })
    );
  }

  public getPaginatedAgencies(filters?: IReservationApiAgency, loadOptions?: any) {
    filters = this.fillPaginatedParams(filters, loadOptions);

    return this.callDynamicAPI('agency/select', filters, 'GET').pipe(
      map<DynamicApiResponse, { data: any[]; totalCount: number }>((response) => {
        return {
          data: response.body.map((r: IReservationApiAgency) => new ReservationAgency(r)),
          totalCount: response.recordsCount,
        };
      })
    );
  }

  /**
   * Get Agents by Agency
   *
   * @param {IReservationApiAgentParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  public getAgents(params: IReservationApiAgentParameters) {
    return this.callDynamicAPI('agent/agency', params, 'GET').pipe(
      map<DynamicApiResponse, ReservationAgent[]>((response) => {
        return response.body.map((r: IReservationApiAgent) => new ReservationAgent(r));
      })
    );
  }

  public getPaginatedAgents(filters: IReservationApiAgentParameters, loadOptions?: any) {
    filters = this.fillPaginatedParams(filters, loadOptions);

    return this.callDynamicAPI('agent/agency', filters, 'GET').pipe(
      map<DynamicApiResponse, { data: any[]; totalCount: number }>((response) => {
        return {
          data: response.body.map((r: IReservationApiAgent) => new ReservationAgent(r)),
          totalCount: response.recordsCount,
        };
      })
    );
  }

  /**
   * Manage Agencies
   *
   * @param {IReservationApiAgency} params
   * @return {*}
   * @memberof ReservationApiService
   */
  public manageAgencies(params: IReservationApiAgency) {
    return this.callDynamicAPI('agency/manage', params, 'POST');
  }

  /**
   * Manage Agents
   *
   * @param {IReservationApiAgent} params
   * @return {*}
   * @memberof ReservationApiService
   */
  public manageAgents(params: IReservationApiAgent) {
    return this.callDynamicAPI('agent/manage', params, 'POST');
  }

  /**
   * Create Guest
   *
   * @param {IReservationApiInsertGuestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  public insertGuest(params: IReservationApiInsertGuestParameters) {
    return this.callDynamicAPI('guest/insert', params, 'POST');
  }

  /**
   * Update Guest
   *
   * @param {IReservationApiUpdateGuestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  public updateGuest(params: IReservationApiUpdateGuestParameters) {
    return this.callDynamicAPI('guest/update', params, 'POST')
  }

  /**
   * Delete Guest
   *
   * @param {IReservationApiDeleteGuestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  public deleteGuest(params: IReservationApiDeleteGuestParameters) {
    return this.callDynamicAPI('guest/delete', params, 'POST').pipe(
      map<DynamicApiResponse, DynamicApiResponse>((response) => {
        if(response.body && response.body?.length > 0 && response.body[0].ErrorCod){
          response.errors = response.body.map((item: any) => {
            return {
              ErrorCod: item.ErrorCod,
              ErrorDesc: item.ErrorMessage
            }
          })
        }
        return response.body;
      })
    );
  }

  /**
   * Associate Directs to Guest
   *
   * @param {IReservationApiReplaceGuestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  public replaceGuest(params: IReservationApiReplaceGuestParameters) {
    return this.postDynamicAPI('guest/replace', params);
  }

  public getCountries(search?: string, cod?: number): Observable<Array<any>> {
    let params: any = {};
    if (!!search) {
      params.Country = search;
    }
    if (!!cod) {
      params.CountryCod = cod;
    }

    return this.callDynamicAPI('country/select', params).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body;
      })
    );
  }

  public getStates(params: IReservationApiGetState): Observable<Array<any>> {
    return this.callDynamicAPI('state/select', params).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body;
      })
    );
  }

  public getCounties(params: IReservationApiGetCounty): Observable<Array<any>> {
    return this.callDynamicAPI('county/select', params).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body;
      })
    );
  }

  public getOccupations(Occupation?: string): Observable<Array<any>> {
    return this.callDynamicAPI('occupation/select', { Occupation }).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body;
      })
    );
  }

  public getCashAccounts(params: { CurrencyCod: string, ReceiptTypeID: number, DebitCredit?: string }): Observable<Array<ResCashReceiptCashAccount>> {
    return this.callDynamicAPI('cashAccounts/select', params).pipe(
      map<DynamicApiResponse, Array<ResCashReceiptCashAccount>>((response) => {
        return response.body.map((r: IReservationCashReceiptCashAccount) => new ResCashReceiptCashAccount(r));
      })
    );
  }

  public getCheckNo(): Observable<Array<IReservationCashReceiptCheckNO>> {
    return this.callDynamicAPI('checkNO/select').pipe(
      map<DynamicApiResponse, Array<IReservationCashReceiptCheckNO>>((response) => {
        return response.body;
      })
    );
  }

  public getPaymentReasons(): Observable<Array<IReservationCashReceiptPaymentReason>> {
    return this.callDynamicAPI('paymentReasons/select').pipe(
      map<DynamicApiResponse, Array<IReservationCashReceiptPaymentReason>>((response) => {
        return response.body;
      })
    );
  }
  public getPayerGroup(bkgId:number): Observable<ReservationCashReceiptPayerGroup[]> {
    return this.callDynamicAPI('payerGroup/select',{BkgID:bkgId}).pipe(
      map<DynamicApiResponse, ReservationCashReceiptPayerGroup[]>((response) => {
        return response.body.map((pg:any)=> new ReservationCashReceiptPayerGroup(pg));
      })
    );
  }
  /**
   * Retrieves all possible booking groups
   *
   * @param {{ BkgID: number, GrpID?: number }} params
   * @return {*}  {Observable<Array<ResGuestGroup>>}
   * @memberof ReservationApiService
   */
  public getBookingGuestGroups(bkgID: number): Observable<ResGuestGroup[]> {
    return this.callDynamicAPI('booking/guestgroups', {BkgID: bkgID}).pipe(
      map<DynamicApiResponse,ResGuestGroup[]>((response) => {
        return response.body.map((data:any)=>new ResGuestGroup(data))
      })
    );
  }
  /**
   * Retrieves all possible booking payers
   *
   * @param {{ BkgID: number, GrpID?: number }} params
   * @return {*}  {Observable<Array<ResPayerGroup>>}
   * @memberof ReservationApiService
   */
  public getBookingPayers(bkgID: number): Observable<ResPayerGroup[]> {
    return this.callDynamicAPI('booking/payers', {BkgID: bkgID}).pipe(
      map<DynamicApiResponse, ResPayerGroup[]>((response) => {
        return response.body.map((data:any)=>new ResPayerGroup(data))
      })
    );
  }
  /**
   * Retrieves all possible booking payers
   *
   * @param {{ BkgID: number, GrpID?: number }} params
   * @return {*}  {Observable<Array<ResPayerGroup>>}
   * @memberof ReservationApiService
   */
  public getPayerDepartments(bkgID: number): Observable<ResPayerDepartment[]> {
    return this.callDynamicAPI('payer/departments', {BkgID: bkgID}).pipe(
      map<DynamicApiResponse, ResPayerDepartment[]>((response) => {
        return response.body.map((data:any)=>new ResPayerDepartment(data))
      })
    );
  }
  manageBookingGuestGroups(bkgID:number,guestGroups:ResGuestGroup[]) {
    return this.postDynamicAPI('booking/managegroups', {BkgID: bkgID, GroupGuest:guestGroups}).pipe(
      map<DynamicApiResponse, boolean>((response) => {
        return response.errors.length===0
      })
    );
  }
  /**
   * Retrieves all booking possible groups
   *
   * @param {{ BkgID: number, GrpID?: number }} params
   * @return {*}  {Observable<Array<IResBookingGroup>>}
   * @memberof ReservationApiService
   */
  public getBookingGroups(params: { BkgID: number, GrpID?: number }): Observable<Array<IResBookingGroup>> {
    return this.callDynamicAPI('booking/groups', params).pipe(
      map<DynamicApiResponse, Array<IResBookingGroup>>((response) => {
        return response.body as IResBookingGroup[];
      })
    );
  }
  /**
   * Retrieves all booking agent owners
   *
   * @return {*}  {Observable<Array<any>>}
   * @memberof ReservationApiService
   */
  public getBookingAgentOwners(bkgID: number, search?: string, id?: string): Observable<Array<any>> {
    let params: any = { BkgID: bkgID };
    if (!!search) {
      params.SearchText = search;
    }
    if (!!id) {
      params.Bkg_Agent_Owner = id;
    }
    return this.callDynamicAPI('booking/agentowners', params).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body;
      })
    );
  }
  /**
   * Retrieves booking custom request
   *
   * @param {number} bkgID
   * @param {number} sectionID
   * @return {*}
   * @memberof ReservationApiService
   */
  public getBookingCustomRequest(bkgID: number, sectionID: number) {
    return this.postDynamicAPI('customRequest/get', { BkgID: bkgID, SectionID: sectionID }).pipe(
      map<DynamicApiResponse, ReservationCustomRequest | null>((response) => {
        return !!response.body && !_.isEmpty(response.body) ? new ReservationCustomRequest(response.body) : null;
      })
    );
  }

  public saveBookingCustomRequest(req: IReservationApiSaveCustomRequest) {
    return this.postDynamicAPI('customRequest/save', req).pipe(
      map<DynamicApiResponse, number>((response) =>
        !!response.body && !_.isEmpty(response.body) ? response.body.RequestID : null
      )
    );
  }

  public getDeposit(params: any): Observable<any> {
    return this.callDynamicAPI('OpenDeposit/get', params).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body;
      })
    );
  }

  public getBkgServices(params: IReservationGetBkgServicesParams, loadOptions?: any): Observable<any> {
    if (params.DateTo && !params.DateTo.isValid) params.DateTo = undefined;
    if (params.DateFrom && !params.DateFrom.isValid) params.DateFrom = undefined;
    params = this.fillPaginatedParams(params, loadOptions);

    return this.callDynamicAPI('bkgService/select', params).pipe(
      map<DynamicApiResponse, { data: any[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: IReservationApiService) => new ReservationServiceData(c, this.coreFormatService)),
          totalCount: response.recordsCount,
        };
      })
    );
  }

  public getBkgOptions(params: IReservationGetBkgOptionsParams, loadOptions?: any): Observable<any> {
    if (params.DateTo && !params.DateTo.isValid) params.DateTo = undefined;
    if (params.DateFrom && !params.DateFrom.isValid) params.DateFrom = undefined;
    params = this.fillPaginatedParams(params, loadOptions);

    return this.callDynamicAPI('bkgOption/select', params).pipe(
      map<DynamicApiResponse, { data: any[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: IReservationApiServiceOption) => new ReservationServiceOption(c, this.coreFormatService)),
          totalCount: response.recordsCount,
        };
      })
    );
  }

  public addServiceOptions(params: IReservationAddServiceOptionParams) {
    return this.postDynamicAPI('serviceOption/add', params);
  }

  public replaceServiceOptions(params: IResApiReplaceOptionsParams): Observable<IResApiReplaceStatus[]> {
    return this.postDynamicAPI('option/replace', params).pipe(
      map<DynamicApiResponse, Array<IResApiReplaceStatus>>((response) => {
        const { body } = response;
        return !body || Array.isArray(body) ? body : body[Object.keys(body).at(-1) ?? -1] ?? [];
      })
    );
  }

  public checkReplaceServiceOptionsAvl(params: IResApiReplaceOptionsParams): Observable<IResApiReplaceAvailability[]> {
    return this.postDynamicAPI('option/replaceCheckAvl', params).pipe(
      map<DynamicApiResponse, Array<IResApiReplaceAvailability>>((response) => {
        return response.body;
      })
    );
  }

  public getReplaceServiceOptionsHistory(params: IResApiGetReplaceOptionsHistoryParams, loadOptions: any): Observable<{ data: ResReplaceHistory[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    return this.callDynamicAPI('option/replaceHistory', params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResReplaceHistory[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: IResApiReplaceHistory) => new ResReplaceHistory(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  public editServiceOption(params: IReservationEditServiceOptionParams) {
    return this.postDynamicAPI('serviceOption/update', params);
  }

  public deleteOptionPackage(params: IReservationDeleteOptionPackageParams) {
    return this.postDynamicAPI('serviceOption/delete', params);
  }

  public execMarginCalculator(bkgID:number) {
    return this.callDynamicAPI('booking/marginCalculator', {BkgID: bkgID}, 'GET').pipe(
      map<DynamicApiResponse, boolean>(response => !!response.body)
    );
  }

  public getCommission(bkgID: number): Observable<any> {
    return this.callDynamicAPI('commission/get', { BkgID: bkgID, Detail: 'Y', Fe: 'Y' }).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body.map((c: any) => new ReservationCommission(c));
      })
    );
  }

  public updateCommissionAmount(params: IResApiUpdateCommissionAmount) {
    return this.postDynamicAPI('commissionAmount/update', params);
  }
  /**
   * Does a transfer cash between bookings or groups
   *
   * @param {IResTransferCashParams} params
   * @return {Observable<{result: boolean, message: string}>}
   * @memberof ReservationApiService
   */
  public transferCash(params: IResTransferCashParams[]): Observable<{ result: boolean; message: string }> {
    return this.postDynamicAPI('booking/transferCash', { Transfer: params }).pipe(
      map<DynamicApiResponse, { result: boolean; message: string }>((response) => {
        let retValue: { result: boolean; message: string } = { result: false, message: '' };

        if (Array.isArray(response.body) && response.body.length > 0) {
          const responseValue: { Result: string; Message: string } = response.body[0];
          retValue.result = responseValue.Result === 'OK';
          retValue.message = responseValue.Message;
        }

        return retValue;
      })
    );
  }

  public createCashReceipt(params: { PaymentDate?: DateTime, CashReceipt: IReservationApiCreateCashReceipt[], IndividualID?: number }): Observable<any> {
    return this.postDynamicAPI('cashReceipt/create', params);
  }

  public checkComboAvailability(params: IReservationCheckComboAvailabilityParams) {
    return this.postDynamicAPI('serviceOption/checkComboAvailability', params).pipe(
      map<DynamicApiResponse, IReservationSuiteType[]>((response) => response.body.map((p: IReservationSuiteType) => p))
    );
  }

  public updateMessage(params: IReservationUpdateMessageParams) {
    return this.postDynamicAPI('message/update', params);
  }

  public createInvoiceForBkg(params: IReservationApiCreateBkgDocumentParams): Observable<any> {
    return this.postDynamicAPI('invoiceForBkg/create', params);
  }

  public getInvoiceTypes(BkgID?: number) {
    return this.callDynamicAPI('invoiceType/select', { BkgID }, 'GET').pipe(
      map<DynamicApiResponse, { value: string; label: string }[]>((response) => {
        return response.body.map((t: { Key: string; Label: string }) => ({ value: t.Key, label: t.Label }));
      })
    );
  }

  public getInvoiceSendingTypes() {
    return this.callDynamicAPI('invoiceSendingType/select', undefined, 'GET').pipe(
      map<DynamicApiResponse, { value: string; label: string }[]>((response) => {
        return response.body.map((t: { Key: string; Label: string }) => ({ value: t.Key, label: t.Label }));
      })
    );
  }

  public deleteVoyage(params: IReservationApiDeleteCruiseParams): Observable<any> {
    return this.postDynamicAPI('cruise/delete', params);
  }

  public linkedPackageUpdate(params: IReservationApiLikedPackageUpdateParams): Observable<any> {
    return this.postDynamicAPI('package/linkedPackage', params);
  }

  public deletePackage(params: IReservationApiDeletePackageParams): Observable<any> {
    return this.postDynamicAPI('package/delete', params);
  }

  public cloneServices(params?: IReservationApiCloneServices): Observable<ReservationServiceData> {
    return this.postDynamicAPI('service/clone', params).pipe(
      map<DynamicApiResponse, ReservationServiceData>((response) => {
        return new ReservationServiceData(response.body);
      })
    );
  }

  private fillPaginatedParams(params?: any, loadOptions?: LoadOptions) {
  if (!params) params = {};
  if (!loadOptions) return params;

  // Paginazione
  const skip = loadOptions.skip ?? 0;
  const take = loadOptions.take ?? 20;
  params.PageNum = Math.floor(skip / take) + 1;
  params.PageSize = take;

  // Filtri
  if (loadOptions.filter) {
    const filters = Array.isArray(loadOptions.filter[0])
      ? loadOptions.filter
      : [loadOptions.filter];

    filters.forEach((filter: any) => {
      if (Array.isArray(filter)) {
        const [field, _operator, value] = filter;
        params[field] = value;  // Nome più chiaro
      }
    });
  }

  // Ordinamento
  if (loadOptions.sort) {
    const sort = loadOptions.sort as { selector: string; desc: boolean }[];
    params.OrderField = sort[0]?.selector;
    params.OrderDirect = sort[0]?.desc ? 1 : 0;
  }

  return params;
}

  private titleCaseWord(word: string) {
    if (!word) return word;
    return word[0].toUpperCase() + word.substring(1);
  }

  private localSort(items: [], loadOptions: any): [] {
    if (!loadOptions?.sort?.length) return items;
    return items.sort((i1, i2) => {
      let field = loadOptions.sort[0].selector;
      let desc = loadOptions.sort[0].desc;
      let item1 = i1[field];
      if (!item1) {
        item1 = i1[this.titleCaseWord(field)];
      }
      let item2 = i2[field];
      if (!item2) {
        item2 = i2[this.titleCaseWord(field)];
      }
      if (item1 > item2) {
        return desc ? -1 : 1;
      }
      if (item1 < item2) {
        return desc ? 1 : -1;
      }
      return 0;
    });
  }


  public getServicesOptionsIncludedDetail(serviceID: number, optionID: number): Observable<ReservationSetupServiceOptionInclusiveDetail[]> {
    return this.callDynamicAPI('option/selectDetail', { serviceID: serviceID, optionID: optionID }).pipe(
      map<DynamicApiResponse, Array<ReservationSetupServiceOptionInclusiveDetail>>((response) => {
        response.body.forEach((o: IReservationApiSetupServiceOptionInclusiveDetail) => {
          if (!o.Status) o.Status = 'A';
        });
        return response.body.map((o: IReservationApiSetupServiceOptionInclusiveDetail) => new ReservationSetupServiceOptionInclusiveDetail(o));
      })
    );
  }

  public getServicesOptions(params?: IReservationApiGetOptions): Observable<ReservationServiceOption[]> {
    return this.callDynamicAPI('option/select', params).pipe(
      map<DynamicApiResponse, Array<ReservationServiceOption>>((response) => {
        return response.body.map((o: IReservationApiServiceOption) => {
          let item = new ReservationServiceOption(o);
          return item;
        });
      })
    );
  }

  public getSetupOptionsPaginated(params?: any, loadOptions?: any): Promise<ResolvedData<ReservationSetupOption>> {
    params = this.fillPaginatedParams(params, loadOptions);

    return lastValueFrom(
      this.callDynamicAPI('option/get', params).pipe(
        map<DynamicApiResponse, ResolvedData<ReservationSetupOption>>((response: DynamicApiResponse) => {
          return {
            data: this.localSort(response.body, loadOptions).map((o: IReservationApiSetupOption) => new ReservationSetupOption(o)),
            totalCount: response.recordsCount,
          }
        })
      )
    ).catch(() => {
      throw 'Data loading error';
    });
  }

  public getSetupSequenceDiscountsPaginated(params?: any, loadOptions?: any): Promise<ResolvedData<ReservationSetupOption>> {
    params = this.fillPaginatedParams(params, loadOptions);

    return lastValueFrom(
      this.callDynamicAPI('option/get', params).pipe(
        map<DynamicApiResponse, ResolvedData<ReservationSetupOption>>((response: DynamicApiResponse) => {
          return {
            data: response.body.map((o: IReservationApiSetupSequenceDiscount) => new ReservationSetupSequenceDiscount(o)),
            totalCount: response.recordsCount,
          };
        })
      )
    ).catch(() => {
      throw 'Data loading error';
    });
  }


  public getSequentialDiscountCods(companyId: number): Observable<string[]> {
    const params = {CompanyID: companyId}
    return this.callDynamicAPI('sequentialDiscount/getOptionCod', params).pipe(
      map<DynamicApiResponse, Array<string>>((response) => {
        return response.body;
      })
    );
  }



  public getSetupOptions(params?: IReservationApiGetOptions): Observable<ReservationSetupOption[]> {
    return this.callDynamicAPI('option/get', params).pipe(
      map<DynamicApiResponse, Array<ReservationSetupOption>>((response) => {
        return response.body.map((o: IReservationApiSetupOption) => new ReservationSetupOption(o));
      })
    );
  }

  public getOptions(params?: IReservationApiGetOptions): Observable<ReservationServiceOption[]> {
    return this.callDynamicAPI('option/select', params).pipe(
      map<DynamicApiResponse, Array<ReservationServiceOption>>((response) => {
        return response.body.map((o: IReservationApiServiceOption) => new ReservationServiceOption(o));
      })
    );
  }


  public getOptionCost(params?: IReservationApiGetOptionCost): Observable<ReservationOptionCost[]> {
    return this.callDynamicAPI('optionCost/select', params).pipe(
      map<DynamicApiResponse, ReservationOptionCost[]>((response) => {
        return response.body.map((c: any) => new ReservationOptionCost(c));
      })
    );
  }

  public getOptionPrice(params?: IReservationApiGetOptionPrice): Observable<any[]> {
    return this.callDynamicAPI('optionPrice/select', params).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body.map((p: IReservationApiOptionPrice) => new ReservationOptionPrice(p));
      })
    );
  }

  public getOptionAvailability(params?: IReservationApiGetOptionAvailability): Observable<any[]> {
    return this.callDynamicAPI('optionAvailability/select', params).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body.map((p: IReservationApiOptionPrice) => new ReservationOptionPrice(p));
      })
    );
  }

  public getAllocations(params?: IReservationApiGetOptionAllocation): Observable<ReservationGetAllocations[]> {
    return this.callDynamicAPI('optionAllocation/select', params).pipe(
      map<DynamicApiResponse, Array<ReservationGetAllocations>>((response) => {
        return response.body.map((p: IReservationGetAllocations) => new ReservationGetAllocations(p));
      })
    );
  }

  public getAllocationByOption(params?: IReservationApiGetAllocationByOption): Observable<ReservationOptionPriceAllocation[]> {
    return this.callDynamicAPI('optionAllocation/getAllocationByOption', params).pipe(
      map<DynamicApiResponse, Array<ReservationOptionPriceAllocation>>((response) => {
        return response.body.map((p: IReservationOptionPriceAllocation) => new ReservationOptionPriceAllocation(p));
      })
    );
  }

  // public getPriceDayByDay(params?: IReservationApiGetPriceDayByDay): Observable<ReservationOptionPriceDayByDay[]> {
  //   return this.callDynamicAPI('optionPrice/getPricePerDay', params).pipe(
  //     map<DynamicApiResponse, Array<ReservationOptionPriceDayByDay>>((response) => {
  //       return response.body.map((p: any) => new ReservationOptionPriceDayByDay(p));
  //     })
  //   );
  // }

  getPriceDayByDayPaginated(params?: IReservationApiGetPriceDayByDay, loadOptions?: any): Promise<ResolvedData<ReservationOptionPriceDayByDay>> {
    params = this.fillPaginatedParams(params, loadOptions);

    return lastValueFrom(
      this.callDynamicAPI('optionPrice/getPricePerDay', params).pipe(
        map<DynamicApiResponse, ResolvedData<ReservationOptionPriceDayByDay>>((response: DynamicApiResponse) => {
          return {
            data: response.body.map((o: any) => new ReservationOptionPriceDayByDay(o)),
            totalCount: response.recordsCount,
          };
        })
      )
    ).catch(() => {
      throw 'Data loading errors';
    });
  }

  public getCostPerPax(params?: IReservationApiGetCostPerPax): Observable<ReservationOptionCostPerPax[]> {
    return this.callDynamicAPI('optionCost/getCostPerPax', params).pipe(
      map<DynamicApiResponse, Array<ReservationOptionCostPerPax>>((response) => {
        return response.body.map((p: any) => new ReservationOptionCostPerPax(p));
      })
    );
  }

  getCostPerPaxPaginated(params?: IReservationApiGetCostPerPax, loadOptions?: any): Promise<ResolvedData<ReservationOptionCostPerPax>> {
    params = this.fillPaginatedParams(params, loadOptions);

    return lastValueFrom(
      this.callDynamicAPI('optionCost/getCostPerPax', params).pipe(
        map<DynamicApiResponse, ResolvedData<ReservationOptionCostPerPax>>((response: DynamicApiResponse) => {
          return {
            data: response.body.map((o: any) => new ReservationOptionCostPerPax(o)),
            totalCount: response.recordsCount,
          };
        })
      )
    ).catch(() => {
      throw 'Data loading errors';
    });
  }

  public getDetailsAllocationByOption(params?: IReservationApiGetOptionAllocationDetails): Observable<ReservationOptionPriceAllocationDetail[]> {
    return this.callDynamicAPI('optionAllocation/getAllocationByOptionDetail', params).pipe(
      map<DynamicApiResponse, Array<ReservationOptionPriceAllocationDetail>>((response) => {
        return response.body.map((p: any) => new ReservationOptionPriceAllocationDetail(p));
      })
    );
  }

  public manageOptionCost(params: IReservationApiManageV2OptionCost): Observable<any> {
    return this.postDynamicAPI('optionCost/manageV2', params);
  }

  public manageBandCost(params: IReservationApiManageBandCost): Observable<any> {
    return this.postDynamicAPI('optionCost/manageBandCost', params);
  }

  // public manageOptionPrice(params: IReservationApiManageV2OptionPrice): Observable<any> {
  //   return this.postDynamicAPI('optionPrice/manageV2', params);
  // }

  public manageOptionPriceAllPt(params: IReservationApiManageV2OptionPrice): Observable<any> {
    return this.postDynamicAPI('optionPrice/manageAllPt', params);
  }

  public manageOptionAvailability(params: IReservationApiManageOptionAvailability): Observable<any> {
    return this.postDynamicAPI('optionAvailability/manage', params);
  }

  public manageOptionAllocation(params: IReservationApiManageOptionAllocation): Observable<any> {
    return this.postDynamicAPI('optionAllocation/manage', params);
  }

  public manageOptionAvailabilityAllPt(params: IReservationApiManageOptionAvailability): Observable<any> {
    return this.postDynamicAPI('optionAvailability/manageAllPt', params);
  }

  public manageOption(params?: any): Observable<any> {
    params.refundable = !!params.refundable ? 'Y' : 'N';
    params.commissionable = !!params.commissionable ? 'Y' : 'N';
    params.isSBC = !!params.isSBC ? 'Y' : 'N';
    params.isDayRoom = !!params.isDayRoom ? 'Y' : 'N';
    return this.postDynamicAPI('setupServiceOption/manage', params);
  }

  public manageSequentialDiscounts(params?: any) {
    params.refundable = !!params.refundable ? 'Y' : 'N';
    params.commissionable = !!params.commissionable ? 'Y' : 'N';
    params.isSBC = !!params.isSBC ? 'Y' : 'N';
    return this.postDynamicAPI('setupServiceOption/manage', params);
  }

  public manageServiceOption(params?: any): Observable<any> {
    return this.postDynamicAPI('setupServiceOptionLink/manage', params);
  }

  public manageServiceOptionInclusive(params?: any): Observable<any> {
    return this.postDynamicAPI('setupServiceOptionInclusive/manage', params);
  }

  /**
   * Checks is params.Value is a valid and existing entity
   * The entity depends on params.Type:
   * BkgID -> booking and GroupCod -> Group
   *
   * @param {IResValueExistCheckParams} params
   * @return {Observable<boolean>}
   * @memberof ReservationApiService
   */
  public existsValue(params: IResValueExistCheckParams): Observable<boolean> {
    return this.getDynamicAPI('utility/exists', params).pipe(
      map<DynamicApiResponse, boolean>((response) => {
        let check = false;

        if (Array.isArray(response.body) && response.body.length > 0) {
          const retValue: { Valid: string } = response.body[0];
          check = retValue.Valid === 'Y';
        }

        return check;
      })
    );
  }

  public getDocument(url: string) {
    let headers = this.getHeaders(null);
    let token = localStorage.getItem(LocalStorageKeys.TOKEN);
    if (token) {
      headers = headers.set(LocalStorageKeys.TOKEN, token);
    }

    let options = {
      withCredentials: true,
      headers,
      observe: 'response' as 'body',
      responseType: 'blob' as 'blob',
    };

    let baseUrl = this.urlAPI.substr(0, this.urlAPI.length - 2);
    let apiUrl = baseUrl + '/documents/document?fullPath=' + url;
    return this.http.get(apiUrl, options as any);
  }

  public getInsurances(params: { bkgID: number }): Observable<any[]> {
    return this.callDynamicAPI('insurance/select', { BkgID: params.bkgID, Action: 'Get' }).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body.map((i: IReservationApiInsurance) => new ResInsurance(i));
      })
    );
  }

  public declineInsurance(params?: IReservationApiDeclineInsurance): Observable<any> {
    return this.postDynamicAPI('insurance/decline', params);
  }
  /**
   * Gets ECM data details for a voyage
   *
   * @param {number} voyageID
   * @return {*}  {Observable<Array<IReservationApiPackageEcmData>>}
   * @memberof ReservationApiService
   */
  getVoyageEcmData(voyageID: number): Observable<Array<IReservationApiPackageEcmData>> {
    return this.getDynamicAPI('voyageecmdata/get', { VoyageID: voyageID }).pipe(
      map<DynamicApiResponse, Array<IReservationApiPackageEcmData>>((response) => {
        return response.body as Array<IReservationApiPackageEcmData>;
      }),
      catchError<any, Array<IReservationApiPackageEcmData>>((_: any) => new Array<IReservationApiPackageEcmData>())
    );
  }
  /**
   * Gets ECM data details for a package
   *
   * @param {number} packageID
   * @return {*}  {Observable<Array<IReservationApiPackageEcmData>>}
   * @memberof ReservationApiService
   */
  getPackageEcmData(packageID: number): Observable<Array<IReservationApiPackageEcmData>> {
    return this.getDynamicAPI('packageecmdata/get', { PackageID: packageID }).pipe(
      map<DynamicApiResponse, Array<IReservationApiPackageEcmData>>((response) => {
        return response.body as Array<IReservationApiPackageEcmData>;
      }),
      catchError<any, Array<IReservationApiPackageEcmData>>((_: any) => new Array<IReservationApiPackageEcmData>())
    );
  }
  /**
   * Gets data details for a package: ECM, Itineraries and traveldate
   *
   * @param {number} packageID
   * @return {*}  {Observable<IReservationApiPackageInfo>}
   * @memberof ReservationApiService
   */
  getPackageInfo(parameters: IReservationApiPackageInfoParameters): Observable<IReservationApiPackageInfo> {
    return this.getDynamicAPI('packageinfo/get', parameters).pipe(
      map<DynamicApiResponse, IReservationApiPackageInfo>((response) => {
        return response.body as IReservationApiPackageInfo;
      }),
      catchError<any, Observable<IReservationApiPackageInfo>>((_: any) => new Observable<IReservationApiPackageInfo>())
    );
  }
  /**
   * Get itinerary of a voyage
   *
   * @param {number} voyageID
   * @return {*}  {Observable<ReservationCruiseItinerary[]>}
   * @memberof ReservationApiService
   */
  getVoyageItinerary(voyageID: number): Observable<ReservationCruiseItinerary[]> {
    return this.getDynamicAPI('cruise/itinerary', { VoyageID: voyageID }).pipe(
      map<DynamicApiResponse, ReservationCruiseItinerary[]>((response) => response.body.map((i: IReservationApiCruiseItinerary) => new ReservationCruiseItinerary(i))),
      catchError<any, ReservationCruiseItinerary[]>((_: any) => [])
    );
  }

  //#region Package

  public getPackages(params: IReservationApiGetPackageParams): Observable<ResPackage[]> {
    return this.callDynamicAPI('package/select', params).pipe(
      map<DynamicApiResponse, Array<ResPackage>>((response) => {
        return response.body.map((p: IReservationApiPackageSetup) => new ResPackage(p));
      })
    );
  }

  loadDayByDayItinerary(packageID:number) {
    return this.callDynamicAPI('package/getDayByDay', {PackageID:packageID}).pipe(
      map<DynamicApiResponse, ResPackageDayByDay>((response) => new ResPackageDayByDay(response.body))
    );
  }

  manageDayByDayItinerary(params: IResApiManageDayByDayItinerary) {
    return this.postDynamicAPI('package/manageDayByDay', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }
  dayByDayImportFromWebsite(params: IResApiDayByDayImportFromWebsite) {
    return this.postDynamicAPI('package/importDayByDayWebsite', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }
  dayByDayOperationalImportFromOperational(pkgID: number) {
    return this.postDynamicAPI('package/getPkgOperationalUpd', {PackageID: pkgID}).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }
  dayByDayUpdateBkg(params: IResApiDayByDayImportFromWebsite) {
    return this.postDynamicAPI('package/updateDayByDayBkg', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }
  queryDayByDayUpdateBkg(params: IResApiDayByDayImportFromWebsite) {
    return this.getDynamicAPI('package/queryDayByDayBkg', params).pipe(
      map<DynamicApiResponse, ResBkgList[]>((response) => {
        return response.body.map((data:any) => new ResBkgList(data));
      })
    )
  }
  getPackageOptionCostsAndPrices(params: {
    Currency: string,
    PriceTypeID: number,
    CompanyID: number,
    AgencyID: number | undefined,
    PackageTravelDateID: number,
    StartDate: string,
    EndDate: string,
    PackageID: number,
    ForceReload: 'Y' | 'N'
  }): Observable<[Array<ResPackageOptionCost>, Array<ResPackagePrice>, Array<ResPackageUpgradePrice>]> {
    return this.callDynamicAPI('package/selectOptionCostsAndPrices', params).pipe(
      map<DynamicApiResponse, [Array<ResPackageOptionCost>, Array<ResPackagePrice>, Array<ResPackageUpgradePrice>]>((response) => {
        return [
          response.body.Costs.map((c: IReservationApiPackageOptionCost) => new ResPackageOptionCost(c)),
          response.body.Prices.map((c: IReservationApiPackagePrice) => new ResPackagePrice(c)),
          response.body.UpgradesPrices.map((c: IReservationApiPackageUpgradePrice) => new ResPackageUpgradePrice(c))
        ];
      })
    );
  }

  public getPackageSpecialOptions(): Observable<Array<IReservationPackageOptionSpecialItem>> {
    return this.callDynamicAPI('package/selectSpecialOptions').pipe(
      map<DynamicApiResponse, Array<IReservationPackageOptionSpecialItem>>((response) => {
        return response.body as IReservationPackageOptionSpecialItem[];
      })
    );
  }

  public managePackage(params: IReservationApiManagePackageParams): Observable<number> {
    return this.postDynamicAPI('package/manage', params).pipe(
      map<DynamicApiResponse, number>((response) => {
        if (Array.isArray(response.body)) {
          return response.body.length > 0 && response.body[0].PackageID ? response.body[0].PackageID : undefined;
        } else {
          return response.body.DT0 && response.body.DT0.length > 0 && response.body.DT0[0].PackageID ? response.body.DT0[0].PackageID : undefined;
        }
      })
    );
  }

  public managePackageTravelDates(params: IReservationApiManagePackageTravelDatesParams): Observable<any> {
    return this.postDynamicAPI('packageTravelDate/manage', params);
  }

  getPackageDeparturesFootnotes(): Observable<ResPackageDepartureFootnotes[]> {
    return this.getDynamicAPI('packageDeparture/getFootnote').pipe(
      map<DynamicApiResponse, ResPackageDepartureFootnotes[]>((response) => {
        return response.body.map((data: any) => new ResPackageDepartureFootnotes(data))
      })
    );
  }

  getPackageDeparturesFreeShorex(): Observable<ResPackageDepartureFreeShorex[]> {
    return this.getDynamicAPI('packageDeparture/getFreeShorex').pipe(
      map<DynamicApiResponse, ResPackageDepartureFreeShorex[]>((response) => {
        return response.body.map((data: any) => new ResPackageDepartureFreeShorex(data))
      })
    );
  }


  public managePackageElements(params: { PackageID: number, PackageOption: IReservationApiManagePackageOptionsParams[] }): Observable<any> {
    return this.postDynamicAPI('package/manageOptions', params);
  }

  managePackagePrices(params: {
    PackageTravelDateID: number,
    currency: string,
    PriceTypes?:string,
    PackageOptionCost: IReservationApiPackageOptionCost[],
    PackagePrice: IReservationApiPackageOptionPriceParams[],
    PackageOptionUpgrade: IReservationApiPackageUpgradePriceParams[]
  },mode:'old'|'new'): Observable<any> {

    params.PriceTypes = mode==='old' ? undefined : `${params.PackagePrice[0].PriceTypeID}`
    const endpoint = mode==='old' ? 'package/managePrices' : 'package/manageCosts'
    return this.postDynamicAPI(endpoint, params);
  }
  packageManageSingleSupp(params: IResPackageSetupSingleSuppManage): Observable<any> {
    return this.postDynamicAPI('package/manageSingleSupplement', params);
  }
  public checkPackageOptionPrices(params: { PackageID: number, PackageTravelDateID?: number }): Observable<IReservationApiPackageConfiguratedPrice[]> {
    return this.getDynamicAPI('package/checkPrices', params).pipe(
      map<DynamicApiResponse, IReservationApiPackageConfiguratedPrice[]>((response) => response.body)
    );
  }

  public packagePricesCloneDates(params: IReservationApiPricesCloneDatesParams): Observable<any> {
    return this.postDynamicAPI('package/CostPriceClone', params);
  }
  public managePackageItineraryDays(params: IReservationApiManagePackageItineraryDaysParams): Observable<any> {
    return this.postDynamicAPI('packageItineraryDay/manage', params);
  }

  public managePackageLinkedPorts(params: IReservationApiManagePackageLinkedPortsParams): Observable<any> {
    return this.postDynamicAPI('package/managePorts', params);
  }

  public managePackageLinkedVoyages(params: IReservationApiManagePackageLinkedVoyagesParams): Observable<any> {
    return this.postDynamicAPI('package/manageLinkedVoyages', params);
  }
  public managePackageLinkedTours(params: IReservationApiManagePackageLinkedToursParams): Observable<any> {
    return this.postDynamicAPI('package/manageLinkedTours', params);
  }
  public getPackageDepScheduling(packageID: number, history = false): Observable<ResPackageDepScheduling[]> {
    return this.callDynamicAPI('package/selectDepScheduling', { packageID, history: history ? 'Y' : 'N' }).pipe(
      map<DynamicApiResponse, Array<ResPackageDepScheduling>>((response) => {
        return response.body.map((p: IReservationApiPackageDepScheduling) => new ResPackageDepScheduling(p));
      })
    );
  }

  public managePackageDepScheduling(params: IReservationApiManagePackageDepSchedulingParams): Observable<any> {
    return this.postDynamicAPI('package/manageDepScheduling', params);
  }

  public getPackageCancPolicy(packageID: number, history = false): Observable<ResPackageCancPolicy[]> {
    return this.callDynamicAPI('package/selectCancPolicy', { packageID, history: history ? 'Y' : 'N'}).pipe(
      map<DynamicApiResponse, ResPackageCancPolicy[]>((response) => {
        return response.body.map((p: any) => new ResPackageCancPolicy(p))
      })
    );
  }

  public managePackageCancPolicy(params: IReservationApiManangePackageCanPolicyParams): Observable<any> {
    return this.postDynamicAPI('package/manageCancPolicy', params);
  }
  public getPackageBookingList(params: { PackageID?: number, PackageTravelDateID?: number | null, BkgStatus?: string }): Observable<ResPackageBookingList[]> {
    return this.callDynamicAPI('package/selectBookingList', params).pipe(
      map<DynamicApiResponse, Array<ResPackageBookingList>>((response) => {
        return response.body.map((p: IReservationApiPackageBookingList) => new ResPackageBookingList(p));
      })
    );
  }

  getPackageAvailabilityStatus(): Observable<ResPackageAvailabilityStatus[]> {
    return this.getDynamicAPI('package/PackageAvailabilityStatus').pipe(
      map<DynamicApiResponse, ResPackageAvailabilityStatus[]>((response) => {
        return response.body.map((data: any) => new ResPackageAvailabilityStatus(data))
      })
    );
  }

  public getGuestPreferences(bkgID: number, Id: number): Observable<any> {
    let params: any = { Id: Id, BkgID: bkgID, Take: "1000", EntityType: "customer" }
    return this.postDynamicAPI('customer/getpreferences', params).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body;
      })
    );
  }

  public managePackageAvailability(params: IReservationApiManagePackageAvailabilityParams): Observable<any> {
    return this.postDynamicAPI('package/manageAvailability', params);
  }
  /**
   * Calls API to clone the package with specified package ID
   *
   * @param {{ PackageID: number, ClonePrice: ReservationApiBoolean }} params
   * @return {*}  {Observable<number>}
   * @memberof ReservationApiService
   */
  clonePackage(params: IResApiPackageClone): Observable<number> {
    return this.postDynamicAPI('package/clone', params).pipe(
      map<DynamicApiResponse, number>((response) => Array.isArray(response.body) && response.body.length > 0 && response.body[0].PackageID ? response.body[0].PackageID : ResPackage.INVALID_PACKAGE_ID)
    );
  }

  public getPackageOptionsAvailabilityElements(params: { AvByOption: IReservationApiPackageOptionPriceTypeAvailibilityParams[], AllPriceType: 'Y' | 'N' }): Observable<IReservationApiPackageOptionPriceTypeAvailibility[]> {
    return this.postDynamicAPI('package/availabilityOptions', params).pipe(
      map<DynamicApiResponse, IReservationApiPackageOptionPriceTypeAvailibility[]>((response) => response.body));
  }

  public bookPackage(params: IReservationApiBookPackageParams): Observable<any> {
    return this.postDynamicAPI('package/book', params);
  }

  getPackageUpgrades(params: { BkgID: number }): Observable<ResPackageUpgrade[]> {
    return this.callDynamicAPI('packageUpgrade/select', params).pipe(
      map<DynamicApiResponse, Array<ResPackageUpgrade>>((response) => {
        return response.body.map((p: IReservationApiPackageUpgrade) => new ResPackageUpgrade(p));
      })
    );
  }

  public managePackageUpgrades(params: IReservationApiManagePackageUpgradeParams): Observable<any> {
    return this.postDynamicAPI('packageUpgrade/manage', params);
  }

  //#endregion Package


  public getPriceTypes(params: { Type?: 'OD' | 'ChangePriceType', BkgID?: number }): Observable<Array<IReservationPriceType>> {
    return this.callDynamicAPI('products/pricetypes', params).pipe(
      map<DynamicApiResponse, Array<IReservationPriceType>>((response) => {
        return response.body?.PriceTypes;
      })
    );
  }

  public changeODFare(params: { BkgID: number, NEWPriceTypeID: number }): Observable<any> {
    return this.postDynamicAPI('openDeposit/changeFare', params);
  }

  public getSuiteStatus(params: { VoyageID?: number, PriceTypeID?: number }): Observable<Array<any>> {
    return this.callDynamicAPI('inventory/selectStatus', params).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body;
      })
    )
  }
  public getBookingToRequests(bkgId: number): Observable<BookingToRequest[]> {
    return this.callDynamicAPI('booking/toRequest', { BkgID: bkgId }).pipe(
      map<DynamicApiResponse, BookingToRequest[]>((response) => {
        return response.body.map((data: any) => new BookingToRequest(data));
      })
    )
  }
  public saveSuiteStatus(params: { VoyageID: number, PriceTypeID: number, SuiteCategoryCod: string, Status: string }) {
    return this.postDynamicAPI('inventory/manageStatus', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  public getAssignedTo(params: { EntityType?: string, Entity?: number, TouchId?: number }): Observable<Array<{ Cod: string, Desc: string }>> {
    return this.callDynamicAPI('touch/assignedto', params).pipe(
      map<DynamicApiResponse, Array<{ Cod: string, Desc: string }>>((response) => {
        return response.body;
      })
    );
  }

  public getAirData(): Observable<ResAirData> {
    return this.getDynamicAPI('air/get', {}).pipe(
      map<DynamicApiResponse, ResAirData>((response) => {
        return new ResAirData(response.body);
      })
    );
  }
  public getAirDataAll(): Observable<ResAirData> {
    return this.getDynamicAPI('air/get', {Type:'large_airport,medium_airport,small_airport'}).pipe(
      map<DynamicApiResponse, ResAirData>((response) => {
        return new ResAirData(response.body);
      })
    );
  }
  public getBkgNotes(params: IReservationApiGetBkgNotes): Observable<Array<ResBkgNote>> {
    return this.callDynamicAPI('bkgNotes/select', params).pipe(
      map<DynamicApiResponse, Array<ResBkgNote>>((response) => {
        return response.body.map((b: IReservationApiBookingNote) => new ResBkgNote(b));
      })
    );
  }

  public manageBkgNotes(params: IReservationApiManageBkgNotes): Observable<any> {
    return this.postDynamicAPI('bkgNotes/manage', params);
  }

  public getBkgTouch(bkgID?: number): Observable<Array<ResBkgNoteTouch>> {
    return this.callDynamicAPI('bkgNotes/getNoteTouch', {KeyCod: bkgID}).pipe(
      map<DynamicApiResponse, Array<ResBkgNoteTouch>>((response) => {
        return response.body.map((b: any) => new ResBkgNoteTouch(b));
      })
    );
  }

  public getGuestAirData(params: { BkgID: number }): Observable<Array<BookingGuestAirData>> {
    return this.callDynamicAPI('guestGTW/select', params).pipe(
      map<DynamicApiResponse, Array<BookingGuestAirData>>((response) => {
        return response.body.map((d: IReservationApiBookingGuestAirData) => new BookingGuestAirData(d));
      })
    );
  }

  public updateGuestAirData(params: IReservationApiUpdateBookingGuestAirData): Observable<any> {
    return this.postDynamicAPI('guestGTW/update', params);
  }

  public getSuitePriceCategories(params: { BkgID: number, SectionID: number }): Observable<Array<ResSuitePriceCategory>> {
    return this.callDynamicAPI('suitePriceCategory/select', params).pipe(
      map<DynamicApiResponse, Array<ResSuitePriceCategory>>((response) => {
        return response.body.map((d: IReservationSuitePriceCategory) => new ResSuitePriceCategory(d));
      })
    );
  }

  public updateSuitePriceCategory(params: IReservationApiUpdateSuiteData): Observable<any> {
    return this.postDynamicAPI('suitePriceCategory/update', params);
  }

  getPowerBIConfig(workspaceId: string, reportId: string): Observable<any> {
    return this.sendCommand(new CoreBaseApiCommandParams({
      url: 'pbi/params',
      data: { workspaceId, reportId },
      type: 'GET',
      customBaseUrl: this.pluginUrlAPI
    }));
  }

  getDocumentList(containerName: string, prefix: string) {
    return this.sendCommand(new CoreBaseApiCommandParams({
      url: 'documents/document-list',
      data: { containerName, prefix },
      type: 'GET',
      customBaseUrl: this.pluginUrlAPI
    }));
  }
  /**
   * Get light details of all voyages
   * Used in:
   * - Suite setup page
   *
   * @param {{ VoyageID?: number, MonthList?: string[], ShipID?: number[] }} filters
   * @param {DynamicApiRequestPaginatedParams} [params=new DynamicApiRequestPaginatedParams(1, 150)]
   * @return {*}  {Observable<DynamicApiResponsePaginatedModel<ReservationCruiseLight>>}
   * @memberof ReservationApiService
   */
  getPagedLightVoyages(
    filters: { VoyageID?: number, MonthList?: string[], ShipID?: number[] },
    params: DynamicApiRequestPaginatedParams = new DynamicApiRequestPaginatedParams(1, 150)
  ): Observable<DynamicApiResponsePaginatedModel<ReservationCruiseLight>> {
    return super.getDynamicAPI('cruise/selectDetail', { ...filters, ...params.serialize() }).pipe(
      map<DynamicApiResponse, DynamicApiResponsePaginatedModel<ReservationCruiseLight>>((response) => {
        const paginatedModel = new DynamicApiResponsePaginatedModel<ReservationCruiseLight>(response);
        paginatedModel.content = response.body.map((cruise: IReservationApiCruiseLight) => new ReservationCruiseLight(cruise));
        return paginatedModel;
      })
    );
  }

  /**
   * Get booked suite of a voyages and their bkgID
   *
   * @param {number} voyageID
   * @return {*}  {Observable<ReservationApiBookedSuite[]>}
   * @memberof ReservationApiService
   */
  getVoyageSuiteAndBookingList(voyageID: number, amenities: IReservationSuiteAmenity[] = [], includeWL: boolean = true): Observable<ReservationCruiseSuite[]> {
    return this.callDynamicAPI('inventory/suiteBookedNew', { VoyageID: voyageID, IncludeWL: includeWL ? 'Y' : 'N' }, 'GET').pipe(
      map<DynamicApiResponse, ReservationCruiseSuite[]>(response => {

        const suites: any[] = response.body.Suites ? response.body.Suites : []
        const bookingInfo: any[] = response.body.BookingInfo ? response.body.BookingInfo : []
        const guestBooking: any[] = response.body.GuestBooking ? response.body.GuestBooking : []
        const connectedSuite: any[] = response.body.ConnectedSuite ? response.body.ConnectedSuite : []

        return suites.map(s => {
          s.BookingInfo = bookingInfo.filter(bi => bi.SuiteID == s.SuiteID)
            .map(p => {
              p.GuestBooking = guestBooking.filter(item => item.BkgID == p.BkgID)
              return p
            })

          s.ConnectedSuite = connectedSuite.find(item => item.SuiteNo == s.SuiteNo)

          return new ReservationCruiseSuite(s, amenities);
        })

        //response.body.Suites.map((sl: IReservationApiCruiseSuite) => new ReservationCruiseSuite(sl, amenities))
      })
    )
  }

  getVoyageGroupList(voyageID?: number): Observable<any[]> {
    return this.callDynamicAPI('inventory/getSetupGroups', { VoyageID: voyageID }).pipe(
      map<DynamicApiResponse, any[]>((response) =>
        response.body.map((g: IReservationGroupVoyageList) => new ReservationGroupVoyageList(g))
      )
    );
  }


  getAirLines(): Observable<IReservationApiAirLine[]> {
    return this.callDynamicAPI('airlines/select', 'GET').pipe(
      map<DynamicApiResponse, IReservationApiAirLine[]>((response) =>
        response.body.map((al: IReservationApiAirLine) => new ReservationAirLine(al))
      )
    );
  }
  /**
   * Get voyage suite profitability
   *
   * @param {number} voyageID
   * @return {*}  {Observable<ReservationCruiseSuiteProfitability[]>}
   * @memberof ReservationApiService
   */
  getVoyageSuiteProfitability(voyageID: number, priceTypeID: number): Observable<ReservationCruiseSuiteProfitability[]> {
    return this.callDynamicAPI('voyage/suiteProfitability', { VoyageID: voyageID, PriceTypeID: priceTypeID }, 'GET').pipe(
      map<DynamicApiResponse, ReservationCruiseSuiteProfitability[]>((response) =>
        response.body.map((sl: IReservationApiCruiseSuiteProfitability) => new ReservationCruiseSuiteProfitability(sl))
      )
    );
  }

  getRightRezUrl(params: ReservationApiRightRezParams): Observable<ReservationApiRightRezResponse> {
    const commandParams = new CoreBaseApiCommandParams({
      url: 'oem/rightrez/book',
      data: params,
      type: 'POST',
      customBaseUrl: this.pluginUrlAPI
    });

    return this.sendCommand(commandParams).pipe(
      tap(val => console.log(`BEFORE MAP: ${val}`)),
      map<DynamicApiResponse, ReservationApiRightRezResponse>((response) => new ReservationApiRightRezResponse(response)),
      catchError(err => {
        return of(new ReservationApiRightRezResponse(err))
      })
    );
  }
  getAirResults(params: IReservationApiGetAirResultsParams): Observable<{ itineraries: ReservationAirResult[], Error?: string }> {

    const commandParams = new CoreBaseApiCommandParams({
      url: 'gmt/searchavailability',
      data: params,
      type: 'POST',
      customBaseUrl: this.pluginUrlAPI
    });

    return this.sendCommand(commandParams).pipe(
      map<{ itineraries?: IReservationApiAirResult[], Error?: string }, { itineraries: ReservationAirResult[], Error?: string }>((response) => {
        return {
          itineraries: response.itineraries?.map((itinerary: IReservationApiAirResult) => new ReservationAirResult(itinerary)) || [],
          Error: response.Error
        };
      }),
      catchError(({ error: { Error } }) => {
        return of({ itineraries: [], Error })
      })
    );
  }

  bookAirResult(params: IReservationApiBookAirResultParams): Observable<{ pnr: IReservationApiAirPnr, Error?: string }> {
    return this.sendCommand(new CoreBaseApiCommandParams({
      url: 'gmt/bookpnr',
      data: params,
      type: 'POST',
      customBaseUrl: this.pluginUrlAPI
    })).pipe(
      catchError(({ error: { Error } }) => {
        return of({ pnr: {} as IReservationApiAirPnr, Error })
      })
    )
  }

  getAirPnrs(bookingId: number): Observable<ReservationAirPnr[]> {
    return this.sendCommand(new CoreBaseApiCommandParams({
      url: 'gmt/pnr',
      data: { bookingId },
      type: 'GET',
      customBaseUrl: this.pluginUrlAPI
    })).pipe(
      map<IReservationApiAirPnr[], Array<ReservationAirPnr>>((response) => {
        return response.map((pnr: IReservationApiAirPnr) => new ReservationAirPnr(pnr));
      })
    );
  }
  deletePnrDocument(flightHeaderId: number) {
    return this.sendCommand(new CoreBaseApiCommandParams({
      url: 'air/registerPnrDoc',
      data: {
        FlightHeaderId: flightHeaderId
      },
      type: 'POST',
      useVersion: true
    })).pipe(
      map<IDynamicApiResponse, DynamicApiResponse>((response) => new DynamicApiResponse(response)),
      map<DynamicApiResponse, boolean>((response) => response.errors.length === 0)
    );
  }
  uploadPnrDocument(params: ReservationUploadBlobResponse, flightHeaderId: number) {
    return this.sendCommand(new CoreBaseApiCommandParams({
      url: 'air/registerPnrDoc',
      data: {
        FlightHeaderId: flightHeaderId,
        FileID: params.fileId,
        FileGUID: params.fileGuid,
        FileName: params.fileName
      },
      type: 'POST',
      useVersion: true
    })).pipe(
      map<IDynamicApiResponse, DynamicApiResponse>((response) => new DynamicApiResponse(response)),
      map<DynamicApiResponse, boolean>((response) => response.errors.length === 0)
    );
  }
  getAirPnrHistory(params: IReservationApiGetAirPnrHistoryParams): Observable<ReservationAirPnrHistory[]> {
    return this.sendCommand(new CoreBaseApiCommandParams({
      url: 'gmt/pnrhistory',
      data: params,
      type: 'GET',
      customBaseUrl: this.pluginUrlAPI
    })).pipe(
      map<IReservationApiAirPnrHistory[], Array<ReservationAirPnrHistory>>((response) => {
        return response.map((pnr: IReservationApiAirPnrHistory) => new ReservationAirPnrHistory(pnr));
      })
    );
  }
  /**
   * Import external A&K TravelStudio airs in OneIS
   *
   * @param {number} bkgID
   * @param {{ BkgID: string, SiteID: number }[]} params
   * @return {*}  {Observable<any>}
   * @memberof ReservationApiService
   */
  importExternalAirs(bkgID: number, params: { BkgID: string, SiteID: number }[]): Observable<any> {
    return this.postDynamicAPI('booking/importExternalAirs', { BkgID: bkgID, BookingList: params });
  }
  releaseAirRRPnr(params: IReservationApiDeleteAirPnrParams): Observable<string> {
    params.Button = 'Release'
    return this.postDynamicAPI('airPnr/delete', params).pipe(map<{ Error?: string }, string>((response) => response?.Error || ''))
  }
  deleteAirRRPnr(params: IReservationApiDeleteAirPnrParams, isTicketed: boolean): Observable<string> {
    params.Button = 'Delete'
    return (isTicketed ? this.postDynamicAPI('airPnr/delete', params) : this.sendCommand(new CoreBaseApiCommandParams({
      url: 'oem/rightrez/Cancel',
      data: params,
      type: 'POST',
      customBaseUrl: this.pluginUrlAPI
    }))).pipe(map<{ Error?: string }, string>((response) => response?.Error || ''))
  }

  deleteAirPnr(params: IReservationApiDeleteAirPnrParams, isTicketed: boolean): Observable<string> {

    return (isTicketed ? this.postDynamicAPI('airPnr/delete', params) : this.sendCommand(new CoreBaseApiCommandParams({
      url: 'gmt/pnr',
      data: params,
      type: 'DELETE',
      customBaseUrl: this.pluginUrlAPI
    }))).pipe(map<{ Error?: string }, string>((response) => response?.Error || ''))
  }

  refreshAirPnrs(params: IReservationApiRefreshAirResultPnrsParams): Observable<{ pnrs: ReservationAirPnr[], Error?: string }> {

    const commandParams = new CoreBaseApiCommandParams({
      url: 'gmt/summarypnr',
      data: params,
      type: 'POST',
      customBaseUrl: this.pluginUrlAPI
    });

    return this.sendCommand(commandParams).pipe(
      map<{ pnrs: IReservationApiAirPnr[], Error?: string }, { pnrs: ReservationAirPnr[], Error?: string }>((response) => {
        return {
          pnrs: response.pnrs?.map((pnr: IReservationApiAirPnr) => new ReservationAirPnr(pnr)),
          Error: response.Error
        }
      }),
      catchError(({ error: { Error } }) => {
        return of({ pnrs: [], Error })
      })
    );
  }
  refreshAirRRPnrs(params: IReservationApiRefreshAirResultPnrsParams): Observable<{ pnrs: ReservationAirPnr[], Error?: string }> {

    const commandParams = new CoreBaseApiCommandParams({
      url: 'oem/rightrez/update',
      data: params,
      type: 'POST',
      customBaseUrl: this.pluginUrlAPI
    });

    return this.sendCommand(commandParams).pipe(
      map<any, { pnrs: any, Error?: string }>((response): any => {
        const row = response[0]?.AirLineRecordLocators?.some((data: any) => !!data.ErrorCod);
        const hasError = !!row;

        if (hasError) {
          return {
            pnrs: null,
            Error: (response[0]?.AirLineRecordLocators?.filter((data: any) => !!data.ErrorCod).map((data: any) => data.ErrorDesc) ?? '').join('\n')
          }
        }
        return {
          pnrs: response,
          Error: null
        }
      }),
      catchError(({ error: { Error } }) => {
        return of({ pnrs: [], Error })
      })
    );
  }

  importFromPNR(params: IReservationApiImportPNRParams): Observable<{ Error?: string }> {

    const commandParams = new CoreBaseApiCommandParams({
      url: 'oem/rightrez/import',
      data: params,
      type: 'POST',
      customBaseUrl: this.pluginUrlAPI
    });

    return this.sendCommand(commandParams).pipe(
      map<any[], { Error?: string }>((response) => {
        if (response.length > 0 && (response[0]?.ErrorCod?.trim() ?? '') !== '') {
          const errorMessages = response
            .filter((item) => item.ErrorDesc && item.ErrorDesc.trim() !== '')
            .map((item) => item.ErrorDesc)
            .join('\n');

          if (errorMessages) {
            return { Error: errorMessages };
          }
        }

        return {};
      }),
      catchError((error: any) => {
       return this.handleGenericError(error);
      })
    );
  }


  changePnrAirClass(params: IReservationApiChangePnrAirClass): Observable<any> {
    return this.postDynamicAPI('pnrAirClass/update', params);
  }

  getLoyaltyRedemptionTiers(): Observable<IReservationLoyaltyRedemptionTier[]> {
    return this.callDynamicAPI('redemptionTiers/select', undefined, 'GET').pipe(
      map<DynamicApiResponse, IReservationLoyaltyRedemptionTier[]>((response) =>
        response.body
      )
    );
  }

  searchCertificates(params: IResApiSearchCertificate): Observable<ResBookingCertificate[]> {
    return this.callDynamicAPI('certificate/search', params, 'GET').pipe(
      map<DynamicApiResponse, ResBookingCertificate[]>((response) =>
        response.body.map((c: IResApiBookingCertificate) => new ResBookingCertificate(c))
      )
    );
  }

  importCertificates(params: IResApiImportCertificate): Observable<any> {
    return this.postDynamicAPI('certificate/import', params);
  }

  getBkgCertificates(bkgID: number): Observable<ResBookingCertificate[]> {
    return this.callDynamicAPI('bkgCertificate/select', { bkgID }, 'GET').pipe(
      map<DynamicApiResponse, ResBookingCertificate[]>((response) => {
        const certificates: ResBookingCertificate[] = response.body.Certificates.map((c: IResApiBookingCertificate) => new ResBookingCertificate(c));
        const availableAmounts: ResBookingCertificateAvailableAmount[] = response.body.AvailableAmounts?.map((c: IResApiBookingCertificateAvailableAmount) => new ResBookingCertificateAvailableAmount(c));
        availableAmounts
          .sort((a, b) => b.defaultValue - a.defaultValue)
          .forEach(a => {
            const certificate = certificates.find(c => a.certificateTypeCod === c.certificateTypeCod);
            if (certificate) {
              (certificate as any)['amountToUseByGuestCod-' + a.guestCod] = Math.min(a.defaultValue, certificate.bookingAvailable);
            }
        })
        return certificates
      }
      )
    );
  }

  getBkgCertificateDetail(params: IResApiGetBookingCertificateDetail): Observable<ResBookingCertificateDetail[]> {
    return this.callDynamicAPI('bkgCertificateDetail/select', params, 'GET').pipe(
      map<DynamicApiResponse, ResBookingCertificateDetail[]>((response) =>
        response.body.map((c: IResApiBookingCertificateDetail) => new ResBookingCertificateDetail(c))
      )
    );
  }

  getBkgCertificateTypes(): Observable<ResCertificateType[]> {
    return this.callDynamicAPI('bkgCertificateTypes/select', undefined, 'GET').pipe(
      map<DynamicApiResponse, ResCertificateType[]>((response) =>
        response.body.map((c: IReservationCertificateType) => new ResCertificateType(c))
      )
    );
  }

  createBkgCertificate(params: IResApiCreateBookingCertificate): Observable<any> {
    return this.postDynamicAPI('bkgCertificate/create', params);
  }

  updateBkgCertificate(params: IResApiUpdateBookingCertificate): Observable<any> {
    return this.postDynamicAPI('bkgCertificate/update', params);
  }

  getManifestVoyages({ VoyageNumber }: { VoyageNumber: string }): Observable<IResApiManifestVoyage[]> {
    return this.callDynamicAPI('manifestVoyage/select', { VoyageNumber }, 'GET').pipe(
      map<DynamicApiResponse, IResApiManifestVoyage[]>((response) =>
        response.body
      )
    );
  }

  getManifest(params: IResApiGetManifestParams, loadOptions: any): Observable<{ data: ResManifest[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);
    const url = params.PackageID && params.PackageID > 0 ? 'manifest/getManifestPkg' : 'manifest/select';
    return this.callDynamicAPI(url, params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResManifest[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: IResApiManifest) => new ResManifest(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  getManifestGroupBy(params: IResApiGetManifestParams, loadOptions: any): Observable<{ data: ResManifest[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);
    const url = params.PackageID && params.PackageID > 0 ? 'manifest/getManifestGroupPkg' : 'manifestGroupBy/select';
    return this.callDynamicAPI(url, params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResManifest[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: IResApiManifest) => new ResManifest(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  getManifestGroupByPaginated(params: IResApiGetManifestParams, loadOptions?: any): Promise<ResolvedData<ResManifest[]>> {
    params = this.fillPaginatedParams(params, loadOptions);
    const url = params.PackageID && params.PackageID > 0 ? 'manifest/getManifestGroupPkg' : 'manifestGroupBy/select';
    return lastValueFrom(this.callDynamicAPI(url, params)).then((response: any) => {
      return {
        data: response.body.map((manifestGroup: any) => new ResManifest(manifestGroup)),
        totalCount: response.recordsCount
      };
    }).catch(() => {
      throw 'Data loading error'
    });
  }

  getManifestGroupByPaginatedPR(params: IResApiGetManifestParams, loadOptions?: any): Promise<ResolvedData<ResManifest[]>> {
    params = this.fillPaginatedParams(params, loadOptions);
    const isPackageSearch = (params.PackageID && params.PackageID > 0) || (params.BkgID && params.BkgID > 0) || (params.ServiceName && params.ServiceName.trim() !== '');
    const url = isPackageSearch ? 'manifest/getManifestGroupPkgPR' : 'manifestGroupBy/selectPR';
    return lastValueFrom(this.callDynamicAPI(url, params)).then((response: any) => {
      return {
        data: response.body.map((manifestGroup: any) => new ResManifest(manifestGroup)),
        totalCount: response.recordsCount
      };
    }).catch(() => {
      throw 'Data loading error'
    });
  }

  getSavedManifest(params: IResApiGetSavedManifestsParams | IResApiGetSavedFullManifestsParams, loadOptions: any, isFullManifest: boolean = false): Observable<{ data: ResSavedManifestHeader[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    let url = ''
    if (params.PackageID && params.PackageID > 0) {
        url = 'manifest/getManifestSavedGetPkg'
    } else {
      url = isFullManifest ? 'fullManifestSaved/select' : 'manifestSaved/select'
    }

    return this.callDynamicAPI(url, params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResSavedManifestHeader[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: IResApiSavedManifestHeader) => new ResSavedManifestHeader(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  openSavedManifest(params: IResApiOpenSavedManifestParams | IResApiOpenSavedFullManifestParams, loadOptions: any, isFullManifest: boolean = false): Observable<{ data: ResManifest[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    let url = ''
    if (params.PackageID && params.PackageID > 0) {
        url = 'manifest/getManifestSavedOpenPkg'
    } else {
      url = isFullManifest ? 'fullManifestSaved/open' : 'manifestSaved/open'
    }

    return this.callDynamicAPI(url, params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResManifest[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: IResApiManifest) => new ResManifest(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  saveManifest(params: IResApiSaveManifestParams | IResApiSaveFullManifestParams, isFullManifest: boolean = false): Observable<any> {
    if(params.PackageID && params.PackageID > 0) {
      const url = 'manifest/getManifestSaveAsPkg';
      return this.postDynamicAPI(url, params);
    } else {
      const url = isFullManifest ? 'fullManifest/save' : 'manifest/save';
      return this.postDynamicAPI(url, params);
    }
  }

  updateManifest(params: IResApiUpdateManifestParams | IResApiUpdateFullManifestParams, isFullManifest: boolean = false): Observable<any> {
    const url = isFullManifest ? 'fullManifest/update' : 'manifest/update';
    return this.postDynamicAPI(url, params);
  }

  updateManifestHeader(params: IResApiUpdateManifestHeaderParams | IResApiUpdateFullManifestHeaderParams, isFullManifest: boolean = false): Observable<any> {
    const url = isFullManifest ? 'fullManifestHdr/update' : 'manifestHdr/update';
    return this.postDynamicAPI(url, params);
  }

  deleteManifest(params: IResApiDeleteManifestParams | IResApiDeleteFullManifestParams, isFullManifest: boolean = false): Observable<any> {
    const url = isFullManifest ? 'fullManifest/delete' : 'manifest/delete';
    return this.postDynamicAPI(url, params);
  }

  compareManifest(params: IResApiCompareManifestParams, loadOptions: any): Observable<{ data: ResManifest[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    return this.callDynamicAPI('manifest/compare', params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResManifest[], totalCount: number }>((response) => {
        return {
          data: response.body?.map?.((c: IResApiManifest) => new ResManifest(c)) ?? [],
          totalCount: response.recordsCount
        }
      })
    );
  }

  compareTwoManifest(params: IResApiCompareTwoManifestsParams, loadOptions: any): Observable<{ data: ResManifest[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    return this.callDynamicAPI('manifest/compareTwo', params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResManifest[], totalCount: number }>((response) => {
        return {
          data: response.body?.map?.((c: IResApiManifest) => new ResManifest(c)) ?? [],
          totalCount: response.recordsCount
        }
      })
    );
  }



  //

  getBkgAirDetails(params: { BkgID: number }): Observable<ResBkgAirDetail[]> {
    return this.callDynamicAPI('bkgAirDetails/select', params, 'GET').pipe(
      map<DynamicApiResponse, ResBkgAirDetail[]>((response) =>
        response.body.map((c: IReservationApiBkgAirDetail) => new ResBkgAirDetail(c))
      )
    );
  }

  getBkgWishlist(params: { BkgID: number }): Observable<ResBkgWishlist[]> {
    return this.callDynamicAPI('bkgWishlist/select', params, 'GET').pipe(
      map<DynamicApiResponse, ResBkgWishlist[]>((response) =>
        response.body.map((c: IReservationApiBkgWishlist) => new ResBkgWishlist(c))
      )
    );
  }

  manageBkgWishlist(params: IReservationApiManageBkgWishlistParams): Observable<any> {
    return this.postDynamicAPI('bkgWishlist/manage', params);
  }

  getDecks(params: { Wishlist: ReservationApiBoolean }): Observable<ResDeck[]> {
    return this.callDynamicAPI('deck/select', params, 'GET').pipe(
      map<DynamicApiResponse, ResDeck[]>((response) =>
        response.body.map((c: IReservationApiDeck) => new ResDeck(c))
      )
    );
  }

  getCommissionPayout(params: IResApiGetCommissionPayoutParams, loadOptions: any): Observable<{ data: ResCommissionPayout[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    return this.callDynamicAPI('commissionPayout/select', params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResCommissionPayout[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: IResApiCommissionPayout) => new ResCommissionPayout(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  getCommissionPayoutAK(params: IResApiGetCommissionPayoutParams, loadOptions: any): Observable<{ data: ResCommissionPayoutAK[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    return this.callDynamicAPI('commissionPayout/selectAK', params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResCommissionPayoutAK[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: IResApiCommissionPayout) => new ResCommissionPayoutAK(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  createCommissionPayoutBatch(params: IResApiCreateCommissionPayoutBatchParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/createBatch', params);
  }

  createCommissionPayoutBatchAK(params: IResApiCreateCommissionPayoutBatchParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/createBatchAK', params);
  }

  getCommissionPayoutBatches(params: IResApiGetCommissionPayoutBatchesParams, loadOptions: any): Observable<{ data: ResCommissionPayoutBatch[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    return this.callDynamicAPI('commissionPayout/selectBatches', params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResCommissionPayoutBatch[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: IResApiCommissionPayoutBatch) => new ResCommissionPayoutBatch(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  getCommissionPayoutBatchesAK(params: IResApiGetCommissionPayoutBatchesParams, loadOptions: any): Observable<{ data: ResCommissionPayoutBatch[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    return this.callDynamicAPI('commissionPayout/selectBatchesAK', params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResCommissionPayoutBatch[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: IResApiCommissionPayoutBatch) => new ResCommissionPayoutBatch(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  openCommissionPayoutBatch(params: IResApiOpenCommissionPayoutBatchParams, loadOptions: any): Observable<{ data: ResCommissionPayout[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    return this.postDynamicAPI('commissionPayout/selectBatch', params).pipe(
      map<DynamicApiResponse, { data: ResCommissionPayout[], totalCount: number }>((response) => {
        return {
          data: response.body.Bookings.map((c: IResApiCommissionPayout) => new ResCommissionPayout(c, response.body.DisplayFlags)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  openCommissionPayoutBatchAK(params: IResApiOpenCommissionPayoutBatchParams, loadOptions: any): Observable<{ data: ResCommissionPayoutAK[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    return this.postDynamicAPI('commissionPayout/selectBatchAK', params).pipe(
      map<DynamicApiResponse, { data: ResCommissionPayoutAK[], totalCount: number }>((response) => {
        return {
          data: response.body.DT0.map((c: IResApiCommissionPayout) => new ResCommissionPayoutAK(c, response.body.DT1[0])),
          totalCount: response.recordsCount
        }
      })
    );
  }

  manageCommissionPayoutBatch(params: IResApiManageCommissionPayoutBatchParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/manageBatch', params);
  }

  manageCommissionPayoutBatchAK(params: IResApiManageCommissionPayoutBatchParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/manageBatchAK', params);
  }


  deleteCommissionPayout(params: IResApiDeleteCommissionPayoutParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/delete', params);
  }

  deleteCommissionPayoutAK(params: IResApiDeleteCommissionPayoutParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/deleteAK', params);
  }

  checkCommissionPayoutBatch(params: IResApiCheckCommissionPayoutBatchParams): Observable<{ BkgID: number, CommissionPayoutDetID: number }[]> {
    return this.postDynamicAPI('commissionPayout/checkBatch', params).pipe(
      map<DynamicApiResponse, { BkgID: number, CommissionPayoutDetID: number }[]>((response) => response.body)
    )
  }

  checkCommissionPayoutBatchAK(params: IResApiCheckCommissionPayoutBatchParams): Observable<{ BkgID: number, CommissionPayoutDetID: number }[]> {
    return this.postDynamicAPI('commissionPayout/checkBatchAK', params).pipe(
      map<DynamicApiResponse, { BkgID: number, CommissionPayoutDetID: number }[]>((response) => response.body)
    )
  }

  sendCommissionPayoutBatchToFinance(params: IResApiSendCommissionPayoutBatchToFinanceParams): Observable<IResApiSendCommissionPayoutEmailSpecs | undefined> {
    return this.postDynamicAPI('commissionPayout/sendBatchToFinance', params).pipe(
      map<DynamicApiResponse, IResApiSendCommissionPayoutEmailSpecs | undefined>((response) => response.body?.[0])
    )
  }

  sendCommissionPayoutBatchToFinanceAK(params: IResApiSendCommissionPayoutBatchToFinanceParams): Observable<IResApiSendCommissionPayoutEmailSpecs | undefined> {
    return this.postDynamicAPI('commissionPayout/sendBatchToFinanceAK', params).pipe(
      map<DynamicApiResponse, IResApiSendCommissionPayoutEmailSpecs | undefined>((response) => response.body?.[0])
    )
  }

  uploadCommissionPayoutBatchPayment(params: IResApiUploadCommissionPayoutBatchPaymentParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/uploadBatchPayment', params)
  }

  uploadCommissionPayoutBatchPaymentAK(params: IResApiUploadCommissionPayoutBatchPaymentParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/uploadBatchPaymentAK', params)
  }

  generateCommissionPayoutBatchSelfInvoice(params: IResApiGenerateCommissionPayoutBatchSelfInvoiceParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/generateBatchSelfInvoice', params)
  }

  generateCommissionPayoutBatchSelfInvoiceAK(params: IResApiGenerateCommissionPayoutBatchSelfInvoiceParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/generateBatchSelfInvoiceAK', params)
  }

  sendCommissionPayoutBatchToBc(params: IResApiSendCommissionPayoutBatchToBcParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/sendBatchToBc', params)
  }

  sendCommissionPayoutBatchToBcAK(params: IResApiSendCommissionPayoutBatchToBcParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/sendRemittance', params).pipe(
      map<DynamicApiResponse, IResApiSendCommissionPayoutEmailSpecs | undefined>((response) => response.body?.[0])
    )
  }

  unlockComissionPayoutBkgs(params: IResApiUnlockCommissionPayoutBkgsParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/unlockBkgs', params)
  }

  unlockComissionPayoutBkgsAK(params: IResApiUnlockCommissionPayoutBkgsParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/unlockBkgsAK', params)
  }

  completeCommissionPayoutBatch(params: { batchID: number }): Observable<any> {
    return this.postDynamicAPI('commissionPayout/completeBatch', params)
  }

  completeCommissionPayoutBatchAK(params: { batchID: number }): Observable<any> {
    return this.postDynamicAPI('commissionPayout/completeBatchAK', params)
  }

  getCommissionPayoutBatchAgencies(params: IResApiGetCommissionPayoutAgenciesParams): Observable<ReservationAgency[]> {
    return this.callDynamicAPI('commissionPayout/selectBatchAgencies', params, 'GET').pipe(
      map<DynamicApiResponse, ReservationAgency[]>((response) =>
        response.body.map((c: IReservationApiAgency) => new ReservationAgency(c))
      )
    );
  }

  getCommissionPayoutBatchAgenciesAK(params: IResApiGetCommissionPayoutAgenciesParams): Observable<ReservationAgency[]> {
    return this.callDynamicAPI('commissionPayout/selectBatchAgenciesAK', params, 'GET').pipe(
      map<DynamicApiResponse, ReservationAgency[]>((response) =>
        response.body.map((c: IReservationApiAgency) => new ReservationAgency(c))
      )
    );
  }

  getCommissionTypesAK(): Observable<IReservationCommissionPayoutType[]> {
    return this.callDynamicAPI('commissionPayout/getCommissionTypesAK', {}, 'GET').pipe(
      map<DynamicApiResponse, IReservationCommissionPayoutType[]>((response) =>
        response.body
      )
    );
  }

  getCommissionPayoutBkgList(params: IResApiGetCommissionPayoutBkgListParams, loadOptions?: any): Observable<{ data: ResCommissionPayout[], totalCount: number } | ResCommissionPayout[]> {
    if (!loadOptions) {
      return this.postDynamicAPI('commissionPayout/selectBkgList', params).pipe(
        map<DynamicApiResponse, ResCommissionPayout[]>((response) =>
          response.body.map((c: IResApiCommissionPayout) => new ResCommissionPayout(c))
        )
      );
    }

    params = this.fillPaginatedParams(params, loadOptions);

    return this.postDynamicAPI('commissionPayout/selectBkgList', params).pipe(
      map<DynamicApiResponse, { data: ResCommissionPayout[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: IResApiCommissionPayout) => new ResCommissionPayout(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  getCommissionPayoutBkgListAK(params: IResApiGetCommissionPayoutBkgListParams, loadOptions?: any): Observable<{ data: ResCommissionPayoutAK[], totalCount: number } | ResCommissionPayoutAK[]> {
    if (!loadOptions) {
      return this.postDynamicAPI('commissionPayout/selectBkgListAK', params).pipe(
        map<DynamicApiResponse, ResCommissionPayoutAK[]>((response) =>
          response.body.map((c: IResApiCommissionPayout) => new ResCommissionPayoutAK(c))
        )
      );
    }

    params = this.fillPaginatedParams(params, loadOptions);

    return this.postDynamicAPI('commissionPayout/selectBkgListAK', params).pipe(
      map<DynamicApiResponse, { data: ResCommissionPayoutAK[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: IResApiCommissionPayout) => new ResCommissionPayoutAK(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  getCommissionPayoutCompanies(): Observable<ResCommissionCompanies[]> {
    return this.callDynamicAPI('commissionPayout/getCompany', 'GET').pipe(
      map<DynamicApiResponse, ResCommissionCompanies[]>((response) =>
        response.body.map((c: any) => new ResCommissionCompanies(c))
      )
    );
  }



  // #region Promo Setup

  public deletePromo(params?: { PromoID: number }) {
    return this.callDynamicAPI('/promo/delete', params).pipe(
      map<DynamicApiResponse, void>(() => {
      })
    );
  }

  public getCombinability(): Observable<{ Value: number, DisplayValue: string }[]> {
    return this.callDynamicAPI('promo/combinability').pipe(
      map<DynamicApiResponse, Array<{ Value: number, DisplayValue: string }>>((response) => {
        return response.body.map(
          (combinability: IPromoCombinability) => {
            return { Value: combinability.PromoCombinabilityID, DisplayValue: combinability.PromoCombinability }
          }
        );
      })
    );
  }

  public getPromos(
    params: { PromoID: number }
  ): Observable<ResPromo[]> {
    return this.callDynamicAPI('promo/get', params).pipe(
      map<DynamicApiResponse, Array<ResPromo>>((response) => {
        return response.body.map(
          (p: IReservationApiPromoDetail) => new ResPromo(p)
        );
      })
    );
  }


  public getApplyPromo(
    params: IReservationPromoApplyPramaters
  ): Observable<ResPromoApply[]> {

    // if(!params.BkgID && !params.PackageID && !params.VoyageID && !params.PromoID && !params.IndividualID) return of([])
    if (!params.VoyageID && !params.PackageID && !params.PackageTravelDateID && !params.PromoID && !params.BkgID) return of([])

    return this.callDynamicAPI('promo-apply/get', params).pipe(
      map<DynamicApiResponse, Array<ResPromoApply>>((response) => {
        if (!response.body.Promos) return []; //@TODO display error configuration
        return response.body.Promos.map(
          (p: IReservationApiPromoApply) => new ResPromoApply(p)
        );
      })
    );
  }

  public bkgPromoItemsManage(
    params: IReservationBkgPromoItemManagePramaters
  ): Observable<boolean> {
    return this.postDynamicAPI('promo-apply/manage', params).pipe(
      map<DynamicApiResponse, boolean>((response) => {
        return (response?.errors?.length ?? 0) === 0
      })
    );
  }
  public managePromo(params: IReservationApiManagePromoParams): Observable<number> {
    return this.postDynamicAPI('promo/upsert', params).pipe(
      map<DynamicApiResponse, number>((response) => {
        return Array.isArray(response.body) && response.body.length > 0 && response.body[0].PromoID ? response.body[0].PromoID : undefined;
      })
    );
  }

  public getPromoItems(params: { PromoID?: number }
  ): Observable<ResPromoActionItems> {
    return this.callDynamicAPI('promo/getItems', params).pipe(
      map<DynamicApiResponse, ResPromoActionItems>((response) => {
        return new ResPromoActionItems(response.body)
      })
    );
  }

  public getBonusCommissionType(params: { PromoID?: number }): Observable<ResBonusCommissionType[]> {
    return this.callDynamicAPI('promo/getBonusCommissionType', params).pipe(
      map<DynamicApiResponse, ResBonusCommissionType[]>((response) => {
        return response.body.map((r: any) => new ResBonusCommissionType(r))
      })
    );
  }

  public savePromoAction(params: IReservationApiManagePromoActionParams): Observable<void> {
    return this.postDynamicAPI('promo/itemsUpsert', params).pipe(
      map<DynamicApiResponse, void>(() => {
      })
    );
  }

  public getPromoVoyages(params: { PromoID?: number }
  ): Observable<Array<ReservationCruise>> {
    return this.callDynamicAPI('promo/getVoyages', params).pipe(
      map<DynamicApiResponse, Array<ReservationCruise>>((response) => {
        return response.body.map((r: IReservationApiCruise) => new ReservationCruise(r))
      })
    );
  }

  getPromoCompatibility(params: { PromoID?: number }): Observable<Array<ResPromoCompatibility>> {
    return this.callDynamicAPI('promocompatibility/get', params).pipe(
      map<DynamicApiResponse, Array<ResPromoCompatibility>>((response) => {
        return response.body.map((data: IResPromoCompatibility) => new ResPromoCompatibility(data));
      })
    );
  }

  public getPromoCompanies(params: { Promo?: string }
  ): Observable<ResPromoCompany[]> {
    return this.callDynamicAPI('promo/companies', params).pipe(
      map<DynamicApiResponse, Array<ResPromoCompany>>((response) => {
        const promoCompanies = response.body.map((r: IResPromoCompany) => new ResPromoCompany(r));
        // const allCompanies = { companyID: 0, companyCode: '*', reportingCompanyName: 'All Companies' }
        // promoCompanies.splice(0, 0, allCompanies)
        return promoCompanies;
      })
    );
  }

  public getPromoSuiteCategories(params: { promoId?: number }
  ): Observable<ResPromoSuiteCategory[]> {
    return this.callDynamicAPI('promo/suitecategories', params).pipe(
      map<DynamicApiResponse, Array<ResPromoSuiteCategory>>((response) => {
        const promoSuiteCategories = response.body.map((r: IResPromoSuiteCategory) => new ResPromoSuiteCategory(r));
        const allSuiteCategories = new ResPromoSuiteCategory({ SuiteCategoryID: 0, SuiteCategory: 'ALL' })
        promoSuiteCategories.splice(0, 0, allSuiteCategories)
        return promoSuiteCategories;
      })
    );
  }

  public getPromotionHeader(params: { promoID?: number }): Observable<ResPromoHeader[]> {
    return this.callDynamicAPI('promo/getPromotionHeader', params).pipe(
      map<DynamicApiResponse, Array<ResPromoHeader>>((response) => {
        const promoHeader = response.body.DT2.map((r: IResPromoHeader) => new ResPromoHeader(r));
        return promoHeader;
      })
    );
  }

  public savePromoVoyages(params: IReservationApiManagePromoVoyagesParams): Observable<void> {
    return this.postDynamicAPI('/promo/voyagesUpsert', params).pipe(
      map<DynamicApiResponse, void>(() => { })
    )
  }

  public saveCompatiblePromo(params: IReservationApiManagePromoCompatibilityParams): Observable<void> {
    return this.postDynamicAPI('promocompatibility/manage', params).pipe(
      map<DynamicApiResponse, void>(() => { })
    )
  }

  public getPromoAgencies(params: { PromoID?: number }
  ): Observable<Array<ReservationAgency | ReservationIndividual>> {
    return this.callDynamicAPI('promo/getAgencies', params).pipe(
      map<DynamicApiResponse, Array<ReservationAgency | ReservationIndividual>>((response) => {
        return response.body.Agencies.map(
          (agency: IReservationApiAgency) => {
            const a = new ReservationAgency(agency);
            a.agencyTypology = "AGN";
            return a;
          }
        ).concat(response.body.Consortiums.map(
          (consortium: IReservationApiAgency) => {
            const c = new ReservationAgency(consortium);
            c.isConsortium = true;
            c.agencyTypology = "CNS";
            return c;
          }
        )).concat(response.body.HeadQuarters.map(
          (headQuarter: IReservationApiAgency) => {
            const hq = new ReservationAgency(headQuarter);
            hq.isHeadQuarter = true;
            hq.agencyTypology = "AHQ";
            return hq;
          }
        ));
      })
    );
  }

  public savePromoAgencies(params: IReservationApiManagePromoAgenciesParams): Observable<void> {
    return this.postDynamicAPI('promo/agenciesUpsert', params).pipe(
      map<DynamicApiResponse, void>(() => {
      })
    )
  }

  getPromoBooking(promoID: number): Observable<ResGetPromoBooking[]> {
    return this.getDynamicAPI('promo/getPromoBooking', {PromoID: promoID}).pipe(
      map<DynamicApiResponse, ResGetPromoBooking []>((response) => {
        return response.body.map((data: any) => new ResGetPromoBooking(data))
      })
    );
  }
  // #endregion Promo Setup


  // Create or Modify a city inside Destination Geo Tree Tab
  public manageGeoTreeDestination(params: IReservationApiGeotree): Observable<any> {
    return this.callDynamicAPI('geotree/treeManage', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    )
  }

  public loadDestinationPopup(orderType?: number, geoTreeID?: number): Observable<GeoTreeSearch[]> {
    return this.callDynamicAPI('geotree/FilterGet', { orderType: orderType, GeoTreeId: geoTreeID }).pipe(
      map<DynamicApiResponse, GeoTreeSearch[]>((response) => {
        return response.body.map((r: any) => new GeoTreeSearch(r))
      })
    )
  }
  public getBookingMarginality(bkgID?: number): Observable<ReservationBookingMarginality> {
    return this.callDynamicAPI('booking/marginality', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ReservationBookingMarginality>((response) => {
        return response.body.map((data: IReservationBookingMarginality) => new ReservationBookingMarginality(data));
      })
    )
  }

  public getMarkets(): Observable<Array<Market>> {
    return this.callDynamicAPI('market/list').pipe(
      map<DynamicApiResponse, Array<Market>>((response) => {
        return response.body.map((data: IMarket) => new Market(data));
      })
    )
  }

  createNewServiceOptionPackage(params: IReservationApiCreateNewServiceOrOption) {
    return this.postDynamicAPI('service/manageServiceOption', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  public getSearchProducts(): Observable<ResProductCod[]> {
    return this.callDynamicAPI('productCod/get').pipe(
      map<DynamicApiResponse, ResProductCod[]>((response) => {
        return response.body.map((data: IResProductCod) => new ResProductCod(data));
      })
    )
  }

  public getPromoPaginated(params?: IPromoParams, loadOptions?: any): Promise<ResolvedData<ResPromo>> {
    params = this.fillPaginatedParams(params, loadOptions);

    return lastValueFrom(this.postDynamicAPI('promo/search', params)).then((response: any) => {
      return {
        data: response.body.map((promo: any) => new ResPromo(promo)),
        totalCount: response.recordsCount
      };
    }).catch(() => {
      throw 'Data loading error'
    });
  }

  public getCashReceiptsPaginated(params?: any, loadOptions?: any): Promise<ResolvedData<ResCashReceipt>> {
    params = this.fillPaginatedParams(params, loadOptions);

    return lastValueFrom(this.getDynamicAPI('cashReceipt/select', params)).then((response: any) => {
      return {
        data: response.body.map((cashReceipt: any) => new ResCashReceipt(cashReceipt)),
        totalCount: response.recordsCount
      };
    }).catch(() => {
      throw 'Data loading error'
    });
  }
  getSearchFilterPreferences(companyID?: number): Observable<ResSearchFilterPreferences> {
    return this.callDynamicAPI('search/getFilterPreferences', {CompanyID: companyID}).pipe(
      map<DynamicApiResponse, ResSearchFilterPreferences>((response) => {
        return new ResSearchFilterPreferences(response.body);
      })
    )
  }
  getSearchFilterToggleFavourite(section:string, item:ResSearchFilterSmartItem, favourite:boolean): Observable<boolean> {
    const params = {
      ProductSearchFiltersID: item.productSearchFiltersID === 0 ? undefined : item.productSearchFiltersID,
      Section: section,
      ID: item.id === 0 ? undefined : item.id,
      Cod: item.cod === '' ? undefined : item.cod,
      ParentKey: item.relatedKey === '' ? undefined : item.relatedKey,
      Value: item.value,
      Status: favourite ? 'A' : 'D'
    }
    return this.callDynamicAPI('search/filterToogleFavourite', params).pipe(
      map<DynamicApiResponse, boolean>((_response) => {
        return true;
      })
    )
  }
  getSearchSmartFilterPage(params:IResSearchSmartFilter) {
    const p = {
      Section: params.section,
      PageNum: params.pageNum,
      SearchText: (params.searchText ?? '') !== '' ? params.searchText : undefined,
      SelectedCodes: (params.selectedCodes ?? '') !== '' ? params.selectedCodes : undefined,
    }
    return this.getDynamicAPI('search/addsmartitems', p).pipe(
      map<DynamicApiResponse, ResSearchFilterSmartItem[]>((response) => {
        return response.body.map((item: any) => new ResSearchFilterSmartItem(item));
      })
    );
  }
  public getPromo(params?: IPromoParams) {
    return this.postDynamicAPI('promo/search', params).pipe(
      map<DynamicApiResponse, Array<ResPromo>>((response) => {
        return response.body.map((promo: any) => new ResPromo(promo));
      })
    );
  }

  //Topic Tab
  getTopicItinerary(params?: IResApiGetGeoTreeTopicItinerary ): Observable<Array<ResTopicItinerary>> {
    return this.callDynamicAPI('topic/getTopicItinerary', params).pipe(
      map<DynamicApiResponse, Array<ResTopicItinerary>>((response) => {
        return response.body.map((data: any) => new ResTopicItinerary(data));
      })
    )
  }
  //Visa & Health Tab
  getVisaHealth(packageID?: number ): Observable<Array<ResBkgVisaHealth>> {
    return this.callDynamicAPI('bkg/getVisaHealth', {ProductID: packageID}).pipe(
      map<DynamicApiResponse, Array<ResBkgVisaHealth>>((response) => {
        return response.body.map((data: any) => new ResBkgVisaHealth(data));
      })
    )
  }
  getVisaHealthBkg(bkgId:number ): Observable<Array<ResBkgVisaHealth>> {
    return this.callDynamicAPI('booking/visa-and-health', {BkgID: bkgId}).pipe(
      map<DynamicApiResponse, Array<ResBkgVisaHealth>>((response) => {
        if(!response.body.DT0) return []
        return response.body.DT0.map((data: any) => new ResBkgVisaHealth(data));
      })
    )
  }
  //Voyage Description Tab
  getVoyageDescription(voyageID?: number ): Observable<ResVoyageDescription> {
    return this.callDynamicAPI('bkg/getVoyageEcmDesc', {voyageID: voyageID}).pipe(
      map<DynamicApiResponse, ResVoyageDescription>((response) => {
        return new ResVoyageDescription(response.body[0])
      })
    )
  }

  //Booking Journey Summary Itinerary
  getBkgJourneyItinerary(params: IResApiGetBkgJourneyItineraryParams): Observable<Array<ResBkgJourneyItinerary>> {
    return this.callDynamicAPI('booking/getBkgJourneyItinerary', params).pipe(
      map<DynamicApiResponse, Array<ResBkgJourneyItinerary>>((response) => {
        return response.body.map((data: any) => new ResBkgJourneyItinerary(data));
      })
    )
  }

  getNoteTypes(bkgID?: number): Observable<IReservationNoteType[]> {
    return this.callDynamicAPI('noteTypes/select', {BkgID: bkgID}).pipe(
      map<DynamicApiResponse, IReservationNoteType[]>((response) => {
        return response.body as IReservationNoteType[];
      })
    )
  }
  getCabinOccupancy(params: IResGetCabinOccupancyParams): Observable<ReservationCabinOccupancy[]> {
    return this.getDynamicAPI('voyage/cabinOccupancy', params).pipe(
      map<DynamicApiResponse, ReservationCabinOccupancy[]>((response) => {
        return response.body.map((data: any) => new ReservationCabinOccupancy(data))
      })
    );
  }

  manageBkgJourneyItinerary(params: any) {
    return this.postDynamicAPI('booking/manageBkgJourneyItinerary', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  public getPackagesPaginated(params?: IReservationApiGetPackageParams, loadOptions?: any): Promise<ResolvedData<ResPackage>> {
    params = this.fillPaginatedParams(params, loadOptions);

    return lastValueFrom(this.callDynamicAPI('packageHeader/select', params)).then((response: any) => {
      return {
        data: response.body.map((pkg: any) => new ResPackage(pkg)),
        totalCount: response.recordsCount
      };
    }).catch(() => {
      throw 'Data loading error'
    });
  }

  getBkgDmc(bkgID: number): Observable<Array<ResBkgDmc>> {
    return this.callDynamicAPI('booking/dmcList', {BkgID: bkgID}).pipe(
      map<DynamicApiResponse, Array<ResBkgDmc>>((response) => {
        return response.body.map((data: any) => new ResBkgDmc(data));
      })
    )
  }

  getDmcManage(bkgID: number): Observable<any> {
    return this.getDynamicAPI('booking/dmcManage', {BkgID: bkgID}).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    );
  }

  getFinancialSplit(bkgID: number, section: string): Observable<Array<ResFinancialSplit>> {
    return this.callDynamicAPI('financial/split', {BkgID: bkgID, Section: section, IsFe: 'Y'}).pipe(
      map<DynamicApiResponse, Array<ResFinancialSplit>>((response) => {
        return response.body.map((data: any) => new ResFinancialSplit(data));
      })
    )
  }

  getMarketingStatus(): Observable<MarketingStatus[]> {
    return this.getDynamicAPI('package/marketingStatus').pipe(
      map<DynamicApiResponse, MarketingStatus[]>((response) => {
        return response.body.map((data: any) => new MarketingStatus(data))
      })
    );
  }

  getHlTypes(): Observable<HlTypes[]> {
    return this.getDynamicAPI('package/hlTypes').pipe(
      map<DynamicApiResponse, HlTypes[]>((response) => {
        return response.body.map((data: any) => new HlTypes(data))
      })
    );
  }

  getPackageHighlightsType(): Observable<PackageHighlightType[]> {
    return this.getDynamicAPI('package/highlightsType').pipe(
      map<DynamicApiResponse, PackageHighlightType[]>((response) => {
        return response.body.map((data: any) => new PackageHighlightType(data))
      })
    );
  }

  getPackageHighlightsList(packageID: number): Observable<PackageHighlight[]> {
    return this.getDynamicAPI('package/highlightsList', {PackageID: packageID}).pipe(
      map<DynamicApiResponse, PackageHighlight[]>((response) => {
        return response.body.map((data: any) => new PackageHighlight(data))
      })
    );
  }

  getBookingCreatedBy(bookingID: number): Observable<BookingCreatedBy[]> {
    return this.getDynamicAPI('booking/createdByModifyUsers', {BkgID: bookingID}).pipe(
      map<DynamicApiResponse, BookingCreatedBy[]>((response) => {
        return response.body.map((data: any) => new BookingCreatedBy(data))
      })
    );
  }

  managePackageHighlights(packageID: number, packageHighlights: IPackageHighlight[]) {
    return this.postDynamicAPI('package/highlightsManage', {PackageID: packageID, PackageHighlights: packageHighlights}).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    )
  }

  getPackageItineraryDays2AKP(packageID: number, packageItineraryDayID: number): Observable<PackageItineraryDay2AKP[]> {
    return this.getDynamicAPI('package/itineraryDay2AKP', {PackageID: packageID, PackageItineraryDayID: packageItineraryDayID}).pipe(
      map<DynamicApiResponse, PackageItineraryDay2AKP[]>((response) => {
        return response.body.map((data: any) => new PackageItineraryDay2AKP(data))
      })
    );
  }

  managePackageItineraryDays2AKP(packageID: number, packageItineraryDay2AKP: IPackageItineraryDay2AKP[]) {
    return this.postDynamicAPI('package/packageItineraryDay2AKP', {PackageID: packageID, PackageItineraryDay2AKP: packageItineraryDay2AKP}).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    )
  }

  getPackageDayLocationsByProjectID(params?: IPackageDayLocationsByProject): Observable<PackageDayLocation[]> {
    return this.getDynamicAPI('package/dayLocationByProject', params).pipe(
      map<DynamicApiResponse, PackageDayLocation[]>((response) => {
        return response.body.map((data: any) => new PackageDayLocation(data))
      })
    );
  }

  getPackageDayProjects(params?: IPackageDayProjects): Observable<PackageDayProject[]> {
    return this.getDynamicAPI('package/dayProject', params).pipe(
      map<DynamicApiResponse, PackageDayProject[]>((response) => {
        return response.body.map((data: any) => new PackageDayProject(data))
      })
    );
  }

  //Edit Bkg Page

  getSalesAssistant(bkgID?: number): Observable<ResBkgSalesAssistant[]> {
    return this.getDynamicAPI('editBkg/getSalesAssistant', {BkgID: bkgID}).pipe(
      map<DynamicApiResponse, ResBkgSalesAssistant[]>((response) => {
        return response.body.map((data: any) => new ResBkgSalesAssistant(data))
      })
    );
  }

  getSecondaryTravelAgency(bkgID?: number): Observable<ResBkgSecondaryTravelAgency[]> {
    return this.getDynamicAPI('editBkg/getSecondaryTravelAgent', {BkgID: bkgID}).pipe(
      map<DynamicApiResponse, ResBkgSecondaryTravelAgency[]>((response) => {
        return response.body.map((data: any) => new ResBkgSecondaryTravelAgency(data))
      })
    );
  }

  getSecondarySalesAgent(agencyCod?: string): Observable<ResBkgSecondarySalesAgent[]> {
    return this.getDynamicAPI('editBkg/getSecondarySalesAgent', {AgencyCod: agencyCod}).pipe(
      map<DynamicApiResponse, ResBkgSecondarySalesAgent[]>((response) => {
        return response.body.map((data: any) => new ResBkgSecondarySalesAgent(data))
      })
    );
  }

  getSegment(): Observable<ResBkgSegment[]> {
    return this.getDynamicAPI('bkg/getSegment').pipe(
      map<DynamicApiResponse, ResBkgSegment[]>((response) => {
        return response.body.map((data: any) => new ResBkgSegment(data))
      })
    );
  }

  getSubSegment(segmentID?: number): Observable<ResBkgSubSegment[]> {
    return this.getDynamicAPI('bkg/getSubSegment', {SegmentID: segmentID}).pipe(
      map<DynamicApiResponse, ResBkgSubSegment[]>((response) => {
        return response.body.map((data: any) => new ResBkgSubSegment(data))
      })
    );
  }

  getMainDestination(): Observable<ResBkgMainDestination[]> {
    return this.getDynamicAPI('bkg/getMainDestination').pipe(
      map<DynamicApiResponse, ResBkgMainDestination[]>((response) => {
        return response.body.map((data: any) => new ResBkgMainDestination(data))
      })
    );
  }

  //Download Excel from Package Setup
  getExportedPackageTable(params: {PackageID?: number, PackageTravelDateID?: number, BkgStatus?: string}): Observable<ResPackageExportListByBooking[]> {
    return this.getDynamicAPI('bkg/getExportPackageListBooking', params).pipe(
      map<DynamicApiResponse, ResPackageExportListByBooking[]>((response) => {
        return response.body.map((data: any) => new ResPackageExportListByBooking(data))
      })
    );
  }

  getTourBook(tourBookParams: IResApiGetTourBookParams): Observable<TourBook[]> {
    return this.getDynamicAPI('product/getTourBook', tourBookParams).pipe(
      map<DynamicApiResponse, TourBook[]>((response) => {
        return response.body.map((data: any) => new TourBook(data))
      })
    );
  }

  getServiceECM(serviceID?: number): Observable<ResServiceEMCDetails[]> {
    return this.getDynamicAPI('bkg/getServiceECM', {ServiceID: serviceID}).pipe(
      map<DynamicApiResponse, ResServiceEMCDetails[]>((response) => {
        return response.body.map((data: any) => new ResServiceEMCDetails(data))
      })
    );
  }

  getConnectedPackage(params: IResApiPackageConnectedPackages): Observable<ResPackageConnectedPackages[]> {
    return this.getDynamicAPI('package/getConnectedPackages', params).pipe(
      map<DynamicApiResponse, ResPackageConnectedPackages[]>((response) => {
        return response.body.map((data: any) => new ResPackageConnectedPackages(data))
      })
    );
  }

  //Package Travel date guide
  getPackageTravelDateGuide(params?: IResApiGetPackageTravelDateGuide): Observable<ResPackageTravelDateGuide[]> {
    return this.getDynamicAPI('package/getPackageTravelDateGuide', params).pipe(
      map<DynamicApiResponse, ResPackageTravelDateGuide[]>((response) => {
        return response.body.map((data: any) => new ResPackageTravelDateGuide(data))
      })
    );
  }

  managePackageTravelDateGuide(params: IResManagePackageTravelDateGuide) {
    return this.postDynamicAPI('package/managePackageTravelDateGuide', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    )
  }

  //Package Suppliers Contacts
  getCommunicationEmail(params: IResApiPackageCommunicationEmail): Observable<ResPackageCommunicationEmail[]> {
    return this.getDynamicAPI('package/getCommunicationEmail', params).pipe(
      map<DynamicApiResponse, ResPackageCommunicationEmail[]>((response) => {
        return response.body.map((data: any) => new ResPackageCommunicationEmail(data))
      })
    );
  }

  //Package Suppliers Contacts
  getSupplierRequestData(params: IResApiPackageSupplierRequest): Observable<ResPackageSupplierRequestData> {
    return this.getDynamicAPI('supplier/requestGet', params).pipe(
      map<DynamicApiResponse, ResPackageSupplierRequestData>((response) => {
        return new ResPackageSupplierRequestData(response.body)
      })
    );
  }

  getSupplierEmailRequestURL(params: IResApiPackageSupplierRequest): Observable<ResPackageSupplierEmailRequestURL[]> {
    return this.getDynamicAPI('supplier/getSupplierRequestEmailURL', params).pipe(
      map<DynamicApiResponse, ResPackageSupplierEmailRequestURL[]>((response) => {
        return response.body.map((data: any) => new ResPackageSupplierEmailRequestURL(data))
      })
    );
  }

  manageCommunicationEmail(params: IResApiPackageCommunicationEmail) {
    return this.postDynamicAPI('package/manageCommunicationEmail', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  saveSupplierRequest(params: IResApiSupplierRequest) {
    return this.postDynamicAPI('supplierRequest/supplierRequestSave', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  //Get Package Footnote
  getPackageFootnote(packageID: number): Observable<ResPackageFootnotes[]> {
    return this.getDynamicAPI('package/getPackageFootnote', { PackageID: packageID }).pipe(
      map<DynamicApiResponse, ResPackageFootnotes[]>((response) => {
        return response.body.map((data: any) => new ResPackageFootnotes(data))
      })
    );
  }

  guestsManage(params:IResApiGuestsManage): Observable<any[]> {
    return this.postDynamicAPI('guests/manage', params).pipe(
      map<DynamicApiResponse, any[]>((response) => {
        return response.body
      })
    );
  }
  checkGuestActivity(bkgID?: number, guestCod?: string): Observable<any[]> {
    return this.getDynamicAPI('guest/checkGuestActivity', { BkgID: bkgID, GuestCod: guestCod }).pipe(
      map<DynamicApiResponse, any[]>((response) => {
        return response.body
      })
    );
  }

  getPackageType(): Observable<ResPackageType[]> {
    return this.getDynamicAPI('package/getPackageType').pipe(
      map<DynamicApiResponse, ResPackageType[]>((response) => {
        return response.body.map((data: any) => new ResPackageType(data))
      }),
    );
  }

  getPackageTypeForSetup(): Observable<ResPackageType[]> {
    return this.getDynamicAPI('package/getPackageType').pipe(
      map<DynamicApiResponse, ResPackageType[]>((response) => {
        return response
                  .body
                  .map((data: any) => new ResPackageType(data))
                  .filter((r: ResPackageType)=>r.setup)
                  .sort((pt: ResPackageType)=>pt.order)
      }),
    )
  }

  getPackageVoyageSuites(packageID: number, packageTravelDateID: number): Observable<ResPackageSuiteConfig[]> {
    return this.getDynamicAPI('package/getPackageVoyageSuiteConfig', {PackageID: packageID, PackageTravelDateID: packageTravelDateID}).pipe(
      map<DynamicApiResponse, ResPackageSuiteConfig[]>((response) => {
        return response.body.map((data: any) => new ResPackageSuiteConfig(data))
      })
    );
  }

  getVillaAvailability(params: IResVillaAvailabilityParams): Observable<ResVillaAvailability[]> {
    return this.getDynamicAPI('villa/details', params).pipe(
      map<DynamicApiResponse, ResVillaAvailability[]>((response) => {
        return response.body.map((data: any) => new ResVillaAvailability(data))
      })
    );
  }

  experienceAvailabilityGet(params: IResExpAvailabilityGetParams): Observable<ResExpAvailability> {
    return this.getDynamicAPI('experience/details', params).pipe(
      map<DynamicApiResponse, ResExpAvailability>((response) => {
          const exp:any = Object.assign({},response.body.DT0[0] ?? {})
          exp.templates = (response.body.DT1 ?? []).filter((d:any)=>d.ExperienceID===exp.ExperienceID)
            .map((t:any)=>{
              t.items = (response.body.DT2 ?? []).filter((d:any)=>d.ExperienceID===t.ExperienceID && d.ExperienceTemplateHdrID === t.ExperienceTemplateHdrID)
              return t
            })
          return new ResExpAvailability(exp)
      })
    );
  }
  getExperienceDetail(experienceID:number): Observable<ResExperience>  {
    return this.callDynamicAPI('experience/view', { ExperienceID: experienceID }).pipe(
      map<DynamicApiResponse, ResExperience>((response) => {
        const exp:any = Object.assign({},response.body.DT0[0] ?? {})
        exp.templates = (response.body.DT3 ?? []).filter((d:any)=>d.ExperienceID===exp.ExperienceID)
          .map((t:any)=>{
            t.items = (response.body.DT5 ?? []).filter((d:any)=>d.ExperienceID===t.ExperienceID && d.ExperienceTemplateHdrID === t.ExperienceTemplateHdrID)
            return t
          })
        return new ResExperience(exp)
      })
    );
  }
  getExperienceOptional(bkgID:number, experienceID:number): Observable<ResExperienceOptional[]>  {
    return this.callDynamicAPI('experience/optional',
            {
              BkgID: bkgID,
              ExperienceID: experienceID,
            })
      .pipe(
        map<DynamicApiResponse, ResExperienceOptional[]>((response) => {
          return response.body.map((d:any)=>new ResExperienceOptional(d))
        })
    );
  }
  experienceBook(params:ResBkgExperiencesApiParams) {
    return this.postDynamicAPI('experience/bookExpereiences', params).pipe(
      map<DynamicApiResponse, any>((response) => {
         return response.body
      })
    );
  }
  experienceManageChoices(params:ResBkgExperienceManageChoicesApiParams) {
    return this.postDynamicAPI('experience/manageChoices', params).pipe(
      map<DynamicApiResponse, any>((response) => {
         return response.body
      })
    );
  }

  getJets(packageTypeID?: number): Observable<ResPackageJets[]> {
    return this.getDynamicAPI('package/getPackageJet', {PackageTypeID: packageTypeID}).pipe(
      map<DynamicApiResponse, ResPackageJets[]>((response) => {
        return response.body.map((data: any) => new ResPackageJets(data))
      })
    );
  }

  getBkgProductTypes(JSONCompanyID?: number): Observable<ResBkgProductTypes[]> {
    return this.getDynamicAPI('booking/getBkgProductTypes', {JSONCompanyID: JSONCompanyID}).pipe(
      map<DynamicApiResponse, ResBkgProductTypes[]>((response) => {
        return response.body.map((data: any) => new ResBkgProductTypes(data)).filter((r: ResBkgProductTypes)=>r.search)
      })
    );
  }

  getPackagePricesSetup(params: { PackageID: number, CompanyIDTM: number, StartDate?: DateTime }): Observable<{ packageHeader: ResPackagePriceHeader; prices: ResPackagePriceSetup[]; travelDates: any[], currencies: string[], priceTypes: IReservationPriceType[], validityPeriods: ResPackagePriceValidityPeriod[], priceNoteTypes: IResPriceNote[], packageDisplayTypes: IResPackageDisplay[], companies: IReservationCompany[], voyages: { voyageID: number, voyageNumber: string }[] }> {
		return this.getDynamicAPI('pkgPriceSetup/getPackagePrices', params).pipe(
      map<DynamicApiResponse, { packageHeader: ResPackagePriceHeader, prices: ResPackagePriceSetup[], travelDates: any[], currencies: string[], priceTypes: IReservationPriceType[], validityPeriods: ResPackagePriceValidityPeriod[], priceNoteTypes: IResPriceNote[], packageDisplayTypes: IResPackageDisplay[], companies: IReservationCompany[], voyages: {voyageID: number, voyageNumber: string }[] }>(response => {
				return {
					packageHeader: new ResPackagePriceHeader(response.body.DT0[0]),
          validityPeriods: response.body.DT2.map((data: any) => new ResPackagePriceValidityPeriod(data)),
          travelDates: response.body.DT1.map((data: any) => new ResPackageSetupTravelDate(data)),
					prices: response.body.DT3.map((data: any) => new ResPackagePriceSetup(data)),
					currencies: response.body.DT4.map((data: any) => data.CurrencyCod),
          priceTypes: response.body.DT5,
          priceNoteTypes: response.body.DT6,
          packageDisplayTypes: response.body.DT7,
          companies: response.body.DT8,
          voyages: response.body.DT0.map((data: any) => ({ voyageID: data.VoyageID, voyageNumber: data.VoyageNumber }))
				}
			})
		)
	}

  managePackagePricesSetup(params: IResApiManagePackagePricesSetup) {
    return this.postDynamicAPI('pkgPriceSetup/managePackagePrices', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  checkBookingDayByDay(BkgID:number) {
    return this.getDynamicAPI('booking/getDayByDayCheck', {BkgID}).pipe(
      map<DynamicApiResponse, boolean>((response) => {
        return response.body?.[0]?.HasDayByDay == 'Y';
      })
    );
  }

  getBkgDeleteMessage(bkgID?: number): Observable<string> {
    return this.getDynamicAPI('bkg/getDeleteMessage', {BkgID: bkgID}).pipe(
      map<DynamicApiResponse, string>((response) => {
        return response.body[0].Msg
      })
    );
  }

  deleteBookingPreCheck(bkgID?: number): Observable<{MessageCod: string, MessageDesc: string}[]> {
    return this.getDynamicAPI('booking/getCheckBkgDelete', {BkgID: bkgID}).pipe(
      map<DynamicApiResponse, {MessageCod: string, MessageDesc: string}[]>((response) => {
        return response.body
      })
    );
  }

  getPaymentDisclaimerMessage(): Observable<string> {
    const params = {
      BkgID: 0,
      Amount: 0,
      Currency: '',
      EntityId: 0
    }
    return this.getDynamicAPI('bkg/PaymentDisclaimer', params).pipe(
      map<DynamicApiResponse, string>((response) => {
        return response.body[0].HTML
      })
    );
  }

  getBonusCommissionDetail(bkgID?: number): Observable<ResBonusCommissionDetail[]> {
    return this.getDynamicAPI('commission/getBonusCommission25Details', {BkgID: bkgID}).pipe(
      map<DynamicApiResponse, ResBonusCommissionDetail[]>((response) => {
        return response.body.map((data: any) => new ResBonusCommissionDetail(data))
      })
    );
  }

  getPackageProperty(): Observable<ResPackageProperty[]> {
    return this.getDynamicAPI('package/getPackageProperty').pipe(
      map<DynamicApiResponse, ResPackageProperty[]>((response) => {
        return response.body.map((data: any) => new ResPackageProperty(data))
      })
    );
  }

  managePromoItems(params: any) {
    return this.postDynamicAPI('promo/manageItems', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  managePromoAgenciesAll(selectAll: boolean, promoID: number) {
    return this.postDynamicAPI('promo/manageAllPromoAgencies', {All: selectAll, PromoID: promoID}).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    );
  }

  getPromoProperties(promoID: number): Observable<ResPromoProperty[]> {
    return this.getDynamicAPI('promo/getPromoProperties', {PromoID: promoID}).pipe(
      map<DynamicApiResponse, ResPromoProperty[]>((response) => {
        return response.body.map((data: any) => new ResPromoProperty(data))
      })
    );
  }

  getSearchablePromoProperties(params: IResApiSearchablePromoProperties, loadOptions?: any): Promise<ResolvedData<ResPromoProperty>> {
    params = this.fillPaginatedParams(params, loadOptions);
    return lastValueFrom(this.callDynamicAPI('promo/getSearchableProperties', params)).then((response: any) => {
      return {
        data: response.body.map((promoProperty: any) => new ResPromoProperty(promoProperty)),
        totalCount: response.recordsCount
      };
    }).catch(() => {
      throw 'Data loading error'
    });
  }


  managePromoProperties(params?: IResApiManagePromoProperties) {
    return this.postDynamicAPI('promo/managePromoProperties', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  getSupplierContacts(params: IResApiSupplierContacts): Observable<ResSupplierContact[]> {
    return this.getDynamicAPI('supplier/getSupplierContacts', params).pipe(
      map<DynamicApiResponse, ResSupplierContact[]>((response) => {
        return response.body.map((data: any) => new ResSupplierContact(data))
      })
    );
  }

  getBkgTailorMadeParams(bkgID?: number): Observable<{params:ResBkgTailorMadeParams, serviceTypes: ResBkgTailorMadeServiceTypes[] }> {
    return this.callDynamicAPI('bkg/getTMParams', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, { params: ResBkgTailorMadeParams, serviceTypes: ResBkgTailorMadeServiceTypes[] }>((response) => {
        return {
          params: response.body.DT0.map((data: any) => new ResBkgTailorMadeParams(data))[0],
          serviceTypes: response.body.DT1.map((data: any) => new ResBkgTailorMadeServiceTypes(data))
        };
      })
    )
  }
  saveBkgTailorMade(bkgID: number,items:IResTailorMadeItems[]): Observable<any> {
    const params = { BkgID: bkgID, TMSrc: items }
    return this.postDynamicAPI('bkg/tmManage', params).pipe(
      map<DynamicApiResponse, { params: ResBkgTailorMadeParams, serviceTypes: ResBkgTailorMadeServiceTypes[] }>((response) => {
        return Array.isArray(response.body) ? response.body[0] : (response.body?.DT0?.[0] ?? 'OK')
      })
    )
  }

  getPackageCategory(companyID?: number): Observable<ResPackageCategory[]> {
    return this.getDynamicAPI('package/getPackageCategory', {CompanyID: companyID}).pipe(
      map<DynamicApiResponse, ResPackageCategory[]>((response) => {
        return response.body.map((data: any) => new ResPackageCategory(data))
      })
    );
  }

  getSupplierEditRequestDetails(params: IResApiSupplierEditRequest): Observable<ResSupplierEditRequestDetails[]> {
    return this.getDynamicAPI('supplier/getSupplierServiceItem', params).pipe(
      map<DynamicApiResponse, ResSupplierEditRequestDetails[]>((response) => {
        return response.body.map((data: any) => new ResSupplierEditRequestDetails(data))
      }),
    )
  }

  manageSupplierItemRequest(params?: IResApiManageSupplierItemRequestListItem) {
    return this.postDynamicAPI('supplier/manageSupplierItemRequest', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  getGoodwillDepositParams(): Observable<{RMTypes:ResGoodwillRMTypes[], pricesAndCurrencies: ResGoodwillDefaultPrices[], priceTypes: ResGoodwillDefaultPriceType[], companies: ResGoodwillCompany[]}> {
    return this.callDynamicAPI('bkg/getGoodWillParams').pipe(
      map<DynamicApiResponse, { RMTypes: ResGoodwillRMTypes[], pricesAndCurrencies: ResGoodwillDefaultPrices[], priceTypes: ResGoodwillDefaultPriceType[], companies: ResGoodwillCompany[]}>((response) => {
        return {
          RMTypes: response.body.DT0.map((data: any) => new ResGoodwillRMTypes(data)),
          pricesAndCurrencies: response.body.DT1.map((data: any) => new ResGoodwillDefaultPrices(data)),
          priceTypes: response.body.DT2.map((data: any) => new ResGoodwillDefaultPriceType(data)),
          companies: response.body.DT3.map((data: any) => new ResGoodwillCompany(data))
        };
      })
    )
  }

  getHeritageDepositParams(params?: IResApiGetHeritageDepositParams): Observable<{priceTypes: ResHeritageTier[], currencies: ResHeritageCurrency[], prices: ResHeritagePrice[]}> {
    return this.callDynamicAPI('bkg/getHeritageParams', params).pipe(
      map<DynamicApiResponse, { priceTypes: ResHeritageTier[], currencies: ResHeritageCurrency[], prices: ResHeritagePrice[]}>((response) => {
        return {
          priceTypes: response.body.DT0.map((data: any) => new ResHeritageTier(data)),
          currencies: response.body.DT1.map((data: any) => new ResHeritageCurrency(data)),
          prices: response.body.DT2.map((data: any) => new ResHeritagePrice(data)),
        };
      })
    )
  }

  // getContinent(): Observable<ResContinent[]> {
  //   return this.getDynamicAPI('pkg/getContinent').pipe(
  //     map<DynamicApiResponse, ResContinent[]>((response) => {
  //       return response.body.map((data: any) => new ResContinent(data))
  //     }),
  //   )
  // }

  manageJetSeats(params: IResApiManageJetSeats) {
    return this.postDynamicAPI('bkg/manageJetSeats', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    )
  }

  getExchangeRate(baseCurrency: string): Observable<ResPriceSetupExchangeRate[]> {
    return this.getDynamicAPI('priceSetup/getExchangeRate', {CurrencyCod: baseCurrency}).pipe(
      map<DynamicApiResponse, ResPriceSetupExchangeRate[]>((response) => {
        return response.body.map((data: any) => new ResPriceSetupExchangeRate(data))
      }),
    )
  }

  getPackageItinerary(voyageID: number): Observable<ResPackageItinerary> {
    return this.getDynamicAPI('package/getItinerary', {VoyageID: voyageID}).pipe(
      map<DynamicApiResponse, ResPackageItinerary>((response) => {
        const pkgItinerary = new ResPackageItinerary()

        if(Array.isArray(response.body)) {
          pkgItinerary.itineraries = response.body.map((data: any) => new ReservationProductItineraryBaseData(data))
        } else {
          if(response.body.DT0) pkgItinerary.itineraries = response.body.DT0.map((data: any) => new ReservationProductItineraryBaseData(data))
          if(response.body.DT1) pkgItinerary.calendarDate = response.body.DT1.map((data: any) => new ReservationPackageTravelDateDetail(data))
        }

        return pkgItinerary
      }),
    )
  }

  getPackageTravelDateRelease(packageID: number, packageTravelDateID: number): Observable<{status: string, releaseData: ResPackageTravelDateRelease[]}> {
    return this.getDynamicAPI('package/getTravelDateRelease', {PackageID: packageID, PackageTravelDateID: packageTravelDateID}).pipe(
      map<DynamicApiResponse, {status: string, releaseData: ResPackageTravelDateRelease[]}>((response) => {
        return {
					status: response.body.DT0[0]?.AvailabilityStatusCod,
					releaseData: response.body.DT1.map((data: any) => new ResPackageTravelDateRelease(data)),
				}
      })
    );
  }

  managePackageTravelDateRelease(params: IResApiManagePackageTravelDateRelease) {
    return this.postDynamicAPI('package/manageTravelDateRelease', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  packageSwap(params: IResApiPackageSwapParams) {
    return this.postDynamicAPI('package/swap', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  getInventoryPackage(voyageID: number): Observable<ResInventoryPackage[]> {
    return this.callDynamicAPI('inventory/getPackages', {VoyageID: voyageID}).pipe(
      map<DynamicApiResponse, ResInventoryPackage[]>((response) => {
        return response.body.map((data: any) => new ResInventoryPackage(data))
      })
    )
  }

  changeBookingOwner(params: IResApiChangeBookingOwnerParams) {
    return this.postDynamicAPI('booking/changeOwner', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  getOandaCurrencyRoe() {
    return this.callDynamicAPI('oanda/roe', {}).pipe(
      map<DynamicApiResponse, ResOandaCurrency[]>((response) => {
        return response.body.map((data: any) => new ResOandaCurrency(data))
      })
    )
  }

  createIntraBooking(bkgID: number) {
    return this.postDynamicAPI('booking/manageDMCBooking', {BkgIDTOC: bkgID}).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  getBkgIntercompanyList(bkgID: number): Observable<ResBkgIntercompanyList[]> {
    return this.callDynamicAPI('booking/getBkgIntercompanyList', {BkgID: bkgID}).pipe(
      map<DynamicApiResponse, ResBkgIntercompanyList[]>((response) => {
        return response.body.map((data: any) => new ResBkgIntercompanyList(data))
      })
    )
  }

  syncIntercompanyBooking(bkgID: number) {
    return this.callDynamicAPI('booking/syncIntercompanyBooking', {BkgID: bkgID}).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    )
  }

  public getVoyageInventoryInfo(voyageID: number, endpoint: string = 'booking/getVoyageInventoryInfo') {
    return this.callDynamicAPI(endpoint, {VoyageID: voyageID}, 'POST').pipe(
      map<DynamicApiResponse, DynamicApiResponsePaginatedModel<ReservationCruise>>((response) => {
        const paginatedModel = new DynamicApiResponsePaginatedModel<ReservationCruise>(response);
        paginatedModel.content = response.body.map(
          (cruise: IReservationApiCruise) => new ReservationCruise(cruise)
        );
        return paginatedModel;
      })
    );
  }

  getInventoryPriceTypes(voyageID: number): Observable<ResInventoryPriceType[]> {
    return this.callDynamicAPI('inventory/getPriceTypes', { VoyageID: voyageID }).pipe(
      map<DynamicApiResponse, ResInventoryPriceType[]>((response) => {
        return response.body.map((priceType: any) => new ResInventoryPriceType(priceType));
      })
    );
  }

  packageOptionCostBandGet(params:IResPackageCostBandGetParams) {
    return this.callDynamicAPI('package/costBandGet', params).pipe(
      map<DynamicApiResponse, PackageOptionCostBand[]>((response) => {
        return response.body.map((data: any)=>new PackageOptionCostBand(data))
      })
    )
  }

  packageOptionCostBandManage(params:IResPackageCostBandManageParams) {
    return this.callDynamicAPI('package/costBandManage', params, 'POST').pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    )

  }

  villaSellingAvailabilityGet(params:IResVillaAvailabilityGetParams) {
    return this.callDynamicAPI('product/villaAvailability', params).pipe(
      map<DynamicApiResponse, ResVillaSellingAvailability>((response) => {
        const villa:any = response.body.DT0 && response.body.DT0.length > 0 ? response.body.DT0[0] : {};
        villa.Availability = response.body.DT1 ? response.body.DT1 : []
        villa.Prices = response.body.DT2 ? response.body.DT2 : []
        villa.EcmData = response.body.DT3 ? response.body.DT3 : []
        villa.Amenities = response.body.DT4 ? response.body.DT4 : []
        villa.Options = response.body.DT5 ? response.body.DT5 : []
        villa.Bailment = response.body.DT6 && response.body.DT6.length > 0 ? response.body.DT6[0] : {};
        return new ResVillaSellingAvailability(villa)
      })
    )
  }

  getBkgVillaService(bkgID: number): Observable<ResBkgVillaService[]> {
    return this.callDynamicAPI('property/getServices', {BkgID: bkgID}).pipe(
      map<DynamicApiResponse, ResBkgVillaService[]>((response) => {
        return response.body.map((data: any) => new ResBkgVillaService(data))
      })
    )
  }

  getBkgVillaOption(bkgID: number, serviceID: number): Observable<ResBkgVillaOption[]> {
    return this.callDynamicAPI('property/getOptions', {BkgID: bkgID, ServiceID: serviceID}).pipe(
      map<DynamicApiResponse, ResBkgVillaOption[]>((response) => {
        return response.body.map((data: any) => new ResBkgVillaOption(data))
      })
    )
  }

  addBkgVillaOption(params: IResApiAddVillaOptionParams) {
    return this.postDynamicAPI('property/addOptions', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  getCharterBookingParams(voyageID: number): Observable<ResBargeVoyageParams> {
    return this.callDynamicAPI('bkg/getBkgCharterParams', {VoyageID: voyageID}).pipe(
      map<DynamicApiResponse, ResBargeVoyageParams>((response) => {
        return new ResBargeVoyageParams(response.body[0])
      })
    )
  }
  changeVilla(params:IResExtendBookingApiParams): Observable<IResBookingCreateApiResponse> {
    return this.postDynamicAPI('bkg/extend', params).pipe(
      map<DynamicApiResponse, IResBookingCreateApiResponse>((apiResponse) => {
        let response: IResBookingCreateApiResponse = {
          BkgID: ResBooking.INVALID_BOOKING
        };

        //TODO: check function Artiom
        //Exception only used in Heritage deposit page
        // if(apiResponse.body.DT0[0].BkgID) {
        //   response.BkgID = apiResponse.body.DT0[0].BkgID ?? ResBooking.INVALID_BOOKING;
        //   return response;
        // }

        if (!!apiResponse && Array.isArray(apiResponse.body) && apiResponse.body.length > 0) {
          response.BkgID = apiResponse.body[0].BkgID ?? ResBooking.INVALID_BOOKING;
          response.ErrorMessage = apiResponse.body[0].ErrorMessage;
        }

        return response;
      })
    );
  }

  getBkgJourneyItineraryHistoryDates(params: IResApiGetBkgJourneyItineraryHistoryParams): Observable<ResBkgJourneyItineraryHistoryDates[]> {
    return this.callDynamicAPI('booking/getBkgItineraryHistory', params).pipe(
      map<DynamicApiResponse, ResBkgJourneyItineraryHistoryDates[]>((response) => {
        return response.body.map((data: any) => new ResBkgJourneyItineraryHistoryDates(data))
      })
    )
  }

  getBkgJourneyItineraryHistoryDetails(params: IResApiGetBkgJourneyItineraryHistoryDetailsParams): Observable<ResBkgJourneyItineraryHistory[]> {
    return this.callDynamicAPI('booking/getBkgItineraryHistoryDetail', params).pipe(
      map<DynamicApiResponse, ResBkgJourneyItineraryHistory[]>((response) => {
        return response.body.map((data: any) => new ResBkgJourneyItineraryHistory(data))
      })
    )
  }

  importBkgJourneyFromProposal(params: IResApiGetBkgJourneyItineraryHistoryParams) {
    return this.postDynamicAPI('booking/importBkgItinerary', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  confirmVilla(bkgID: number) {
    return this.postDynamicAPI('booking/confirmVilla', {BkgID: bkgID}).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  getPackagePhysicalLevels() {
    return this.callDynamicAPI('package/getPhysicalLevels').pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  getOwnArrangement(OwnArrangementID: number) {
    return this.callDynamicAPI('guest/getOwnArrangement', { OwnArrangementID }).pipe(
      map<DynamicApiResponse, any>((response) => {
        return new ResFlightOwnArrangement(response.body[0]);
      })
    )
  }

  manageOwnArrangement(params: IResApiManageOwnArrangementParams[]) {
    return this.postDynamicAPI('guest/manageOwnArrangement', {OwnArrangement: params}).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  getAirlines({AirLine, AirLineCod}: {AirLine?: string, AirLineCod?: string}) {
    return this.callDynamicAPI('lookup/getAirlines', {AirLine, AirLineCod}).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  getAirports({IataCod, AirName}: {IataCod?: string, AirName?: string}) {
    return this.callDynamicAPI('lookup/getAirports', {IataCod, AirName}).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  getExtraSegments() {
    return this.callDynamicAPI('lookup/getExtraSegments').pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  getServiceBookingReport(params: { ServiceID: number, ServiceOptionID?: number }): Observable<ReservationServiceBookingReport[]> {
    return this.callDynamicAPI('service/getServiceBookingReport', params).pipe(
      map<DynamicApiResponse, any>((res) => res.body.map((el: any) => new ReservationServiceBookingReport(el)))
    )
  }
  getServicePackageList(params: { ServiceID: number, OptionID?: number }): Observable<ReservationServicePackageListElement[]> {
    return this.callDynamicAPI('service/getServicePackageList', params).pipe(
      map<DynamicApiResponse, any>((res) => res.body.map((el: any) => new ReservationServicePackageListElement(el)))
    )
  }

  getPackageServiceAllocations(params: { PackageID: number }): Observable<{ travelDates: { packageTravelDateID: number, travelStartDate: string, travelEndDate: string }[], options: ReservationPackageServiceAllocation[] }> {
    return this.callDynamicAPI('package/getPackageServiceAllocations', params).pipe(
      map<DynamicApiResponse, any>((res) => {
        const options = (res.body.DT0 ?? []).map(
          (el: any) => new ReservationPackageServiceAllocation({
            ...el, packageMaxPax: res.body.DT1?.[0]?.MaxPax
          })
        );
        const travelDates = (res.body.DT2 ?? []).map((el: { PackageTravelDateID: number, TravelStartDate: string, TravelEndDate: string }) => {
          return {
            packageTravelDateID: el.PackageTravelDateID, travelStartDate: el.TravelStartDate, travelEndDate: el.TravelEndDate
          }
        });
        return { options, travelDates }
      })
    )
  }

  getServiceAllocationForInspectModal(params: any): Observable<ReservationServiceAllocation[]> {
    return this.callDynamicAPI('package/getServiceAllocation', params).pipe(
      map<DynamicApiResponse, any>((res) => res.body.map((el: any) => new ReservationServiceAllocation(el)))
    );
  }

  managePackageServiceAllocations(params: any) {
    return this.postDynamicAPI('package/managePackageAllocation', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  getSupplierCancPolicy(params: { supplierID?: number, serviceID?: number }): Observable<ResSupplierCancPolicy[]> {
    return this.callDynamicAPI('supplier/getCancPolicy', params).pipe(
      map<DynamicApiResponse, ResSupplierCancPolicy[]>((response) => {
        return response.body.DT0.map((data: any) => {
          data.Details = response.body.DT1.filter((d: any) => d.SupplierCancellationPolicyID === data.SupplierCancellationPolicyID)
          return new ResSupplierCancPolicy(data)
        })
      })
    )
  }

  manageSupplierCancPolicy(params: any): Observable<any> {
    return this.postDynamicAPI('supplier/manageCancPolicy', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  getSupplierDepScheduling(params: { supplierID?: number, serviceID?: number }): Observable<ResSupplierDepScheduling[]> {
    return this.callDynamicAPI('supplier/getDepScheduling', params).pipe(
      map<DynamicApiResponse, ResSupplierDepScheduling[]>((response) => {
        return response.body.map((data: any) => new ResSupplierDepScheduling(data))
      })
    )
  }

   manageSupplierDepScheduling(params: any): Observable<any> {
    return this.postDynamicAPI('supplier/manageDepScheduling', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  getPackageHighlightsByPackageType(): Observable<ReservationPackageHighlightByType[]> {
    return this.callDynamicAPI('package/getPackageHighlightsByPackageType', { OrderField: 'PackageTypeID' }).pipe(
      map<DynamicApiResponse, any>((res) => res.body.map((el: any) => new ReservationPackageHighlightByType(el)))
    );
  }
  managePackageHighlightsByPackageType(params: { PackageHighlightsByPackageType: ReservationPackageHighlightByType[] }): Observable<any> {
    return this.postDynamicAPI('package/managePackageHighlightsByPackageType', params).pipe(
      map<DynamicApiResponse, any>((res) => res.body)
    );
  }
  getPackageHighlightsTypeList(params?: any, loadOptions?: any): Observable<{ data: ReservationPackageHighlightsType[]; totalCount: number }> {
    const filters = this.fillPaginatedParams(params, loadOptions);
    return this.callDynamicAPI('package/getPackageHighlightsTypeList', filters).pipe(
      map<DynamicApiResponse, { data: ReservationPackageHighlightsType[]; totalCount: number }>((response) => {
        return {
          data: response.body.map((data: any) => new ReservationPackageHighlightsType(data)),
          totalCount: response.recordsCount,
        };
      })
    );
  }
  getScopeHighlightsList(): Observable<{ Scope: string }[]> {
    return this.callDynamicAPI('package/getScopeHighlightsList').pipe(
      map<DynamicApiResponse, { Scope: string }[]>((response) => response.body)
    );
  }
  managePackageHighlightsType(params: { PackageHighlightsType: ReservationPackageHighlightsType & { status: 'A' | 'D' } }): Observable<any> {
    return this.postDynamicAPI('package/managePackageHighlightsType', params).pipe(
      map<DynamicApiResponse, { data: ReservationPackageHighlightsType[]; totalCount: number }>((response) => response.body)
    );
  }
  manageConfirmSuiteStatus(params: IResBookingManageConfirmSuiteStatusParams): Observable<any> {
    return this.postDynamicAPI('booking/manageConfirmSuiteStatus', params);
  }

  manageSuiteRemark(params: IResBookingManageSuiteRemarkParams): Observable<any> {
    return this.postDynamicAPI('booking/manageSuiteRemark', params);
  }

  getPriceSetupBookingList(params?: IResApiGetPriceSetupBookingListParams): Observable<ResPriceSetupBookingList[]> {
    return this.getDynamicAPI('priceSetup/selectBookingList', params).pipe(
      map<DynamicApiResponse, ResPriceSetupBookingList[]>((response) => {
        return response.body.map((data: any) => new ResPriceSetupBookingList(data))
      })
    );
  }
  getPackageClearing(params: { hasCruise: boolean, gty?: boolean }): Observable<ResPackageClearingCruise[] | ResPackageClearingNoCruise[]> {
    return this.getDynamicAPI('bkg/getPackageClearing', { HasCruise: params.hasCruise ? 'Y' : 'N', GTY: params.gty ? 'Y' : 'N' }).pipe(
      map<DynamicApiResponse, ResPackageClearingCruise[] | ResPackageClearingNoCruise[]>((response) => {
        return response.body.map((data: any) => params.hasCruise ? new ResPackageClearingCruise(data) : new ResPackageClearingNoCruise(data))
      })
    );
  }
  getPackageClearingByCompany(params: { companyID: number, gty?: boolean }): Observable<ResPackageClearingVoyage[]> {
    return this.getDynamicAPI('bkg/getPackageClearingByCompany', { Company: params.companyID, GTY: params.gty ? 'Y' : 'N' }).pipe(
      map<DynamicApiResponse, ResPackageClearingVoyage[]>((response) => {
        return response.body.map((data: any) => new ResPackageClearingVoyage(data))
      })
    );
  }
  getPackageClearingCompanyList(): Observable<{ companyID: number, endpoint?: string }[]> {
    return this.getDynamicAPI('bkg/getPackageClearingCompanyList').pipe(
      map<DynamicApiResponse, { companyID: number, endpoint?: string }[]>((response) => {
        return response.body.map((data: any) => { return { companyID: data.CompanyID, endpoint: data.Sp ?? undefined } })
      })
    );
  }
  editVillaPrice(params: IResVillaEditPrice) {
    return this.callDynamicAPI(
      'bkgitem/editVillaPrice',
      params,
      'POST'
    ).pipe(
      map<DynamicApiResponse, any>(response => response.body)
    );
  }

  getExportInvoiceData(params?: IResApiGetExportInvoiceDataParams): Observable<ResAccountingExportData[]> {
    return this.getDynamicAPI('payable/getAccountingExport', params).pipe(
      map<DynamicApiResponse, ResAccountingExportData[]>((response) => {
        return response.body.map((data: any) => new ResAccountingExportData(data))
      })
    );
  }

  getPurchaseOrderForPackage(params: IResApiGetPurcahseOrderParams): Observable<ResPackagePurchaseOrder> {
    return this.callDynamicAPI('apc/getPkgPurchaseOrderID', params).pipe(
        map<DynamicApiResponse, ResPackagePurchaseOrder>((response) => new ResPackagePurchaseOrder(response.body[0]))
    );
}

  getSanctuaryCampAllocations(filters?: IReservationApiGetSanctuaryCampAllocations, loadOptions?: any): Observable<{ data: ResSanctuaryCampAllocation[]; totalCount: number }> {
    filters = this.fillPaginatedParams(filters, loadOptions);

    return this.callDynamicAPI('camp/getSanctuaryCampAllocations', filters, 'GET').pipe(
      map<DynamicApiResponse, { data: ResSanctuaryCampAllocation[]; totalCount: number }>((response) => {
        return {
          data: response.body.map((data: any) => new ResSanctuaryCampAllocation(data)),
          totalCount: response.recordsCount,
        };
      }),
    );
  }
  getSanctuaryCampSuiteCategoryFilters(params?: {PropertyID: number}): Observable<ResSanctuaryCampSuiteCategory[]> {
    return this.getDynamicAPI('camp/getSuiteCategoryFilters', params).pipe(
      map<DynamicApiResponse, ResSanctuaryCampSuiteCategory[]>((response) => response.body.map((data: any) => new ResSanctuaryCampSuiteCategory(data))),
    );
  }
  getSanctuaryCampAgenciesFilters(params?: {Agency?: string}): Observable<ResSanctuaryCampAgency[]> {
    return this.getDynamicAPI('camp/getAgenciesFilters', params).pipe(
      map<DynamicApiResponse, ResSanctuaryCampAgency[]>((response) => response.body.map((data: any) => new ResSanctuaryCampAgency(data))),
    );
  }

  getPackageCostSupplierList(params: IResApiGetPackageCostSupplierListParams): Observable<ResPackageCostSupplierList[]> {
    return this.callDynamicAPI('package/getServiceListSupplierForAPC', params).pipe(
      map<DynamicApiResponse, ResPackageCostSupplierList[]>((response) => response.body.map((data: any) => new ResPackageCostSupplierList(data)))
    );
  }

  getBookingSupplierListForAdvisingReport(bookingID: number): Observable<ResBookingSupplierListForAdvisingReport[]> {
    return this.callDynamicAPI('booking/getSupplierForAdvisingReport', { BkgID: bookingID }).pipe(
      map<DynamicApiResponse, ResBookingSupplierListForAdvisingReport[]>((response) => response.body.map((data: any) => new ResBookingSupplierListForAdvisingReport(data)))
    );
  }

  manageRequestAdvisingReport(params: IResBookingSupplierListForAdvisingReportParams): Observable<any> {
    return this.postDynamicAPI('booking/manageRequestAdvisingReport', params);
  }

  getFutureFlights(bookingID: number): Observable<ResFutureFlights> {
    return this.callDynamicAPI('bookingAir/getFutureFlight', { BkgID: bookingID }).pipe(
      map<DynamicApiResponse, ResFutureFlights>((response) => {
        const futureFlights = new ResFutureFlights(response.body?.DT0[0])
        futureFlights.futureFlightsDetail = response.body?.DT1.map((detail: any) => new FutureFlightsDetail(detail))
        futureFlights.futureFlightsGuest = response.body?.DT2.map((detail: any) => new FutureFlightsGuest(detail))
        return futureFlights
      })
    );
  }

  manageFutureFlights(params?: IResApiFutureFlights){
    return this.postDynamicAPI('bookingAir/manageFutureFlight', params);
  }

  managePackageSuiteCategoryStatus(params: IResApiManagePackageSuiteCategoryStatus): Observable<any> {
    return this.postDynamicAPI('package/managePackageSuiteCategoryStatus', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  getDayByDayPackages(params: IResApiGetDayByDayPackagesParams): Observable<ResBookingPackageDayByDay[]> {
    return this.callDynamicAPI('booking/getDayByDayPackage', params).pipe(
      map<DynamicApiResponse, ResBookingPackageDayByDay[]>(
        (response) => response.body.map((data: any) => new ResBookingPackageDayByDay(data))
      ));
  }

  manageDayByDayPackages(params: IResApiManageDayByDayPackagesParams): Observable<any> {
    return this.postDynamicAPI('booking/manageDayByDayPackage', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  getDayByDayTopicItinerary(params: IResApiGetDayByDayTopicItineraryParams): Observable<ResBookingTopicDayByDay[]> {
    return this.callDynamicAPI('booking/getTopicDayByDay', params).pipe(
      map<DynamicApiResponse, ResBookingTopicDayByDay[]>(
        (response) => response.body.map((data: any) => new ResBookingTopicDayByDay(data))
      ));
  }

  manageDayByDayTopicItinerary(params: IResApiManageDayByDayTopicItinerary): Observable<any> {
      return this.postDynamicAPI('booking/manageTopicDayByDay', params).pipe(
        map<DynamicApiResponse, any>((response) => response.body)
      );
  }

  resetDayByDayTopicItinerary(bkgID: number): Observable<any> {
    return this.callDynamicAPI('booking/resetTopicDayByDay', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  restoreCommissionFromTam(bkgID: number): Observable<any> {
    return this.callDynamicAPI('booking/restoreCommissionFromTam', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  getTopicPackage(params: IResApiGetDayByDayTopicItineraryParams): Observable<ResPackageTopic[]> {
    return this.callDynamicAPI('package/getTopicPackage', params).pipe(
      map<DynamicApiResponse, ResPackageTopic[]>(
        (response) => response.body.map((data: any) => new ResPackageTopic(data))
      ));
  }

  manageTopicPackage(params: IResApiManageTopicPackageParams): Observable<any> {
    return this.postDynamicAPI('package/manageTopicPackage', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  resetTopicPackage(packageID: number): Observable<any> {
    return this.callDynamicAPI('package/resetTopicPackage', { PackageID: packageID }).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  getDayByDayTopicHistory(bkgID: number): Observable<ResBkgTopicHistoryDates[]> {
    return this.callDynamicAPI('booking/getTopicHistory', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResBkgTopicHistoryDates[]>(
        (response) => response.body.map((data: any) => new ResBkgTopicHistoryDates(data))
      ));
  }

  getDayByDayTopicHistoryDetail(params: IResApiGetBkgTopicHistoryDetailsParams): Observable<ResBkgTopicHistoryDetail[]> {
    return this.callDynamicAPI('booking/getTopicHistoryDetail', params).pipe(
      map<DynamicApiResponse, ResBkgTopicHistoryDetail[]>(
        (response) => response.body.map((data: any) => new ResBkgTopicHistoryDetail(data))
      ));
  }
  
  deleteFutureFlight(bkgID?: number): Observable<any> {
    return this.getDynamicAPI('bookingAir/deleteFutureFlight', {BkgID: bkgID}).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    );
  }
  
}
